import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Layout from '../layout/Layout';
import { choreographyApi, organisationApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { loadRehearsalSnapshot, saveRehearsalSnapshot } from './choreographyCache';
import { allocationFragmentsFor, analyseBlueprintNotes, describeDancerMusicStructure, effectiveChoreography, formationAtCount, formationPaths, projectChoreoCanvas, variantLineView } from './blueprintComposer';
import { CommunityReviewLayer, COMMUNITY_REVIEW_DISPLAYS, SelectionToolbar, normalizeCommunityReviewDisplay, readCommunityReviewDisplay, selectionToolbarPosition, writeCommunityReviewDisplay } from './CommunityReview';
import CollaborativeCanvas, { CollaborationPeople, divergenceDetail, statusLabel } from './CollaborativeCanvas';
import { collaboratorColor, collaboratorSelectionColor, groupColor, groupSelectionColor, orderedLines, projectRemoteCursors, sourceEditOperations } from './collaborationProtocol';
import { ResourceAccessManager, reasonFor, writeFailure, failureLine } from '../sharing/ResourceSharePanel';
import DemonstrationWorkspace, { completeDemonstrationBars } from './DemonstrationWorkspace';
import MediaLibraryGrid from './MediaLibraryGrid';
import CanvasMediaPanel from './CanvasMediaPanel';
import UploadQueueManager, { useUploadQueueManager } from './UploadQueueManager';
import DemonstrationTimingRail from './DemonstrationTimingRail';
import StageInformationPanel from './StageInformationPanel';
import MappedDemonstrationBackdrop from './MappedDemonstrationBackdrop';
import RehearsalScrubTimeline from './RehearsalScrubTimeline';
import {
  REHEARSAL_SPEED_PRESETS,
  markLoopEdge,
  nudgeLoopEdge,
  readMirrorPreference,
  sectionJumpTarget,
  videoRateReadout,
  writeMirrorPreference,
} from './rehearsalStudio';
import { focusOnClassification, focusPlaybackRateForCount, orderedDemonstrations } from './demonstrationPriority';
import { mediaTrackColours } from './mediaTrackColour';
import { mappedDemonstrationAuthorityKey, readApprovedMappedDemonstrations, readServedInvalidDemonstrations } from './mappedDemonstrationProjection';
import { createProportionalTimingMap } from './demonstrationTimingMap';
import { finalizeThenAwaitReady, uploadChunks } from '../../services/videoIngestApi';
import './MusicChoreography.css';

export { blueprintFromNotes } from './blueprintComposer';

const payload = (response) => response?.data?.data || response?.data || {};
const listFrom = (value, keys) => keys.reduce((found, key) => found || (Array.isArray(value?.[key]) ? value[key] : null), null) || (Array.isArray(value) ? value : []);
const message = (error) => error?.response?.data?.message || error?.response?.data?.error?.message || error?.message || 'Something went wrong.';
// MCRO001-F0068 / T-MCRO-068 case D (WebUI #51, D005 §4.1 (c)): a Rehearsal
// load failure is stated in product language. A transport failure (no HTTP
// response at all — the shared client's "timeout of 10000ms exceeded", a
// network drop) is never shown; a server answer keeps the server's own words.
export const REHEARSAL_UNAVAILABLE_NOTICE = 'Rehearsal is taking longer than usual to load. Your routine is safe — try again.';
export const rehearsalLoadFailureNotice = (error) => (error?.response ? message(error) : REHEARSAL_UNAVAILABLE_NOTICE);
const uid = (prefix) => `${prefix}-${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;

// MCRO001-D011 §5.4a item 7 ("Workspace-load robustness") / issue #51 (jobs
// 176219/176317, first red pipeline
// 7845): Core's authorization decision can be TRANSIENTLY unavailable —
// choreo's coreAuthorizationClient fails closed with 503
// CORE_AUTHORIZATION_UNAVAILABLE ("Core authorization is unavailable") when
// Core's limiter sheds the decision call — while the session itself is
// perfectly fine. The workspace died on attempt 1: getCapabilities set the
// error alert and loadRoutines (gated on capabilities.choreography_view)
// never ran, leaving a dead routines rail. getCapabilities and loadRoutines
// therefore retry that one transient shape with bounded exponential backoff,
// mirroring the TestDeploy specs' own withTransientRetry semantics: a hard
// attempt cap, never an infinite loop, and NO retry for any other failure —
// a 401/403 is a real authorization defect that must surface, a
// CORE_APP_CREDENTIAL_MISSING 503 is a configuration fault that no amount of
// retrying heals, so only an absent code (proxy-generated 503) or the exact
// transient code is retried.
const TRANSIENT_AUTHORIZATION_CODE = 'CORE_AUTHORIZATION_UNAVAILABLE';
const TRANSIENT_AUTHORIZATION_MAX_ATTEMPTS = 4;
const TRANSIENT_AUTHORIZATION_BACKOFF_BASE_MS = 500;

const authorizationUnavailable = (cause) => Number(cause?.response?.status) === 503 && (
  !cause?.response?.data?.code || cause?.response?.data?.code === TRANSIENT_AUTHORIZATION_CODE
);

const retryTransientAuthorization = async (action, request, attempt = 1) => {
  try {
    return await request();
  } catch (cause) {
    if (!authorizationUnavailable(cause) || attempt >= TRANSIENT_AUTHORIZATION_MAX_ATTEMPTS) throw cause;
    const delayMs = TRANSIENT_AUTHORIZATION_BACKOFF_BASE_MS * 2 ** (attempt - 1);
    // eslint-disable-next-line no-console
    console.warn(`${action}: transient Core authorization unavailability (attempt ${attempt}/${TRANSIENT_AUTHORIZATION_MAX_ATTEMPTS}); retrying in ${delayMs}ms`);
    await new Promise((resolve) => { setTimeout(resolve, delayMs); });
    return retryTransientAuthorization(action, request, attempt + 1);
  }
};

// MCRO001-F0058 hardening (owner/coordinator, pipeline 7008/7013 job 146349
// investigation): job 146349's captured error-context.md showed the REAL
// mechanism behind Case E's failure — the live backend's rate limiter
// genuinely rejected the folder-move PATCHes with a real 429 "Too many
// requests", because this Feature's own extra per-load requests (the
// reconcile canvas-media GET plus its eligibility machinery) consumed
// enough of the session's rate budget that the user's own subsequent PATCH
// burst tipped over the limit. The honest-failure notice was telling the
// truth; request VOLUME was the actual defect, and the fix is
// consumer-side backpressure — never a limit raise, which would only mask
// the same contention against a busier backend. On a 429, this honours a
// numeric `Retry-After` header (seconds; RFC 9110 also allows an HTTP-date,
// which this backend has never been observed to send, so only the numeric
// form is parsed) when present, else a bounded exponential backoff
// (1s/2s/4s for the default maxAttempts=3), and rethrows the final failure
// unchanged so every existing honest-failure path (T-MCRO-055 Case E's
// true succeeded/total count, F0026's conflict messages) is completely
// unaffected once retries are genuinely exhausted.
async function retryOnRateLimit(operation, { maxAttempts = 3, baseDelayMs = 1000 } = {}) {
  let attempt = 0;
  for (;;) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await operation();
    } catch (error) {
      attempt += 1;
      if (error?.response?.status !== 429 || attempt >= maxAttempts) throw error;
      const headerSeconds = Number(error.response.headers?.['retry-after']);
      const delayMs = Number.isFinite(headerSeconds) && headerSeconds >= 0
        ? headerSeconds * 1000
        : baseDelayMs * (2 ** (attempt - 1));
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
}

export const CHOREOGRAPHY_ACCESS_PRESETS = [
  { id: 'no_access', label: 'No access', description: 'Cannot see Choreography.' },
  { id: 'view_only', label: 'View only', description: 'View published routines and use rehearsal.' },
  { id: 'create_own', label: 'Create their own', description: 'View, create, edit and privately rehearse their own routines/videos.' },
  { id: 'contributor', label: 'Contributor', description: 'View published routines and add their own comments, reactions and approved media.' },
  { id: 'manage_everything', label: 'Manage everything', description: 'Edit and privately rehearse any routine; publish, manage music and manage all videos.' },
];

const accessMemberId = (member) => member?.userId || member?.user_id || member?.id || member?.memberId || member?.member_id || member?.user?.id || member?.user?.userId || member?.user?.user_id;
const accessMemberName = (member) => member?.displayName || member?.display_name || member?.name || member?.user?.displayName || member?.user?.display_name || member?.user?.name || member?.email || member?.user?.email || 'Unnamed member';
const accessPreset = (member) => {
  const preset = member?.preset || member?.access?.preset || member?.choreographyAccess?.preset || member?.choreography_access?.preset;
  return CHOREOGRAPHY_ACCESS_PRESETS.some((item) => item.id === preset) ? preset : null;
};

export const canManageChoreographyAccess = (user, organisation) => {
  if (String(user?.role || '').toLowerCase() === 'super_admin') return true;
  const membershipRole = organisation?.user_role || organisation?.organisation_role || organisation?.membership_role || organisation?.membership?.role;
  return String(membershipRole || '').toLowerCase() === 'admin';
};

export function ChoreographyAccessPanel({ orgId }) {
  const [members, setMembers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedPreset, setSelectedPreset] = useState('no_access');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await choreographyApi.getAccess(orgId);
      const next = listFrom(payload(response), ['access', 'members', 'items', 'rows', 'activeMembers', 'active_members']);
      setMembers(next);
      const firstMember = next[0];
      setSelectedUserId(String(accessMemberId(firstMember) || ''));
      setSelectedPreset(accessPreset(firstMember) || 'no_access');
    } catch (cause) {
      setMembers([]);
      setError(message(cause));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const selectedMember = members.find((member) => String(accessMemberId(member)) === String(selectedUserId));

  const chooseMember = (member) => {
    setSelectedUserId(String(accessMemberId(member)));
    setSelectedPreset(accessPreset(member) || 'no_access');
    setError('');
  };

  // GitLab issue #41: the hand-typed "Audit reason" textarea (10-char
  // minLength gate on Save access) is retired in favour of a derived reason,
  // in `reasonFor()`'s own style (ResourceSharePanel.js, issue #30) — a
  // description of the actual change (who, and to what preset), never a
  // constant placeholder, so the audit row this write still produces stays
  // meaningful without making an admin write one.
  const save = async (event) => {
    event.preventDefault();
    if (!selectedMember) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const presetLabel = CHOREOGRAPHY_ACCESS_PRESETS.find((preset) => preset.id === selectedPreset)?.label || selectedPreset;
      const reason = reasonFor(`Changed ${accessMemberName(selectedMember)}'s access to ${presetLabel}.`);
      await choreographyApi.updateAccess(orgId, accessMemberId(selectedMember), { preset: selectedPreset, reason });
      setMembers((current) => current.map((member) => String(accessMemberId(member)) === String(accessMemberId(selectedMember)) ? { ...member, preset: selectedPreset } : member));
      setSuccess(`Access updated for ${accessMemberName(selectedMember)}.`);
    } catch (cause) {
      setError(failureLine(writeFailure(cause, message(cause))));
    } finally {
      setSaving(false);
    }
  };

  return <section className="mc-access" aria-labelledby="mc-access-title">
    <header className="mc-panel-head"><div><span className="mc-kicker">Choreography</span><h2 id="mc-access-title">Access</h2><p className="mc-muted">Set what each active member can do in this studio. These choices apply only to Choreography.</p></div></header>
    {error && <div className="mc-alert" role="alert">{error}<button type="button" onClick={() => setError('')}>Dismiss</button></div>}
    {success && <div className="mc-notice" role="status">{success}</div>}
    <div className="mc-access-layout">
      <section className="mc-panel" aria-labelledby="mc-members-title"><h3 id="mc-members-title">Active members</h3>{loading ? <p>Loading members…</p> : members.length === 0 ? <p className="mc-empty">There are no active members to manage.</p> : <div className="mc-member-list">{members.map((member) => { const memberId = accessMemberId(member); const currentPreset = CHOREOGRAPHY_ACCESS_PRESETS.find((preset) => preset.id === accessPreset(member)); return <button type="button" key={memberId} className={String(memberId) === String(selectedUserId) ? 'active' : ''} onClick={() => chooseMember(member)} aria-pressed={String(memberId) === String(selectedUserId)}><strong>{accessMemberName(member)}</strong><span>{member.email || member.user?.email || ''}</span><small>{currentPreset?.label || 'Needs review'}</small></button>; })}</div>}</section>
      <form className="mc-panel mc-access-form" onSubmit={save} aria-label="Update choreography access">
        <h3>Member access</h3>{selectedMember ? <><p className="mc-muted">Updating <strong>{accessMemberName(selectedMember)}</strong></p><label className="mc-field"><span>Access preset</span><select aria-label="Access preset" value={selectedPreset} onChange={(event) => setSelectedPreset(event.target.value)}>{CHOREOGRAPHY_ACCESS_PRESETS.map((preset) => <option value={preset.id} key={preset.id}>{preset.label}</option>)}</select></label><fieldset className="mc-preset-cards"><legend>Choose an access preset</legend>{CHOREOGRAPHY_ACCESS_PRESETS.map((preset) => <label key={preset.id} className={selectedPreset === preset.id ? 'selected' : ''}><input type="radio" name="choreography-preset" value={preset.id} checked={selectedPreset === preset.id} onChange={() => setSelectedPreset(preset.id)} /><span><strong>{preset.label}</strong><small>{preset.description}</small></span></label>)}</fieldset><button className="mc-primary" disabled={saving}>{saving ? 'Saving…' : 'Save access'}</button></> : <p className="mc-empty">Choose a member to set their Choreography access.</p>}</form>
    </div>
  </section>;
}

// M001-D023 v0.11 §7.4 / MCRO001-D003 §3.3 amendment (2026-08-22, Mantis
// #1196): pure derivation of the ceiling→community-contribution-policy
// mapping. "Sharing ceiling is authoritative; policy is derived" — a broad
// (link/scope/public) row's ceiling landing on `contribute`/`edit` also
// raises this policy to a matching non-`view` mode
// (`contribute`→`comment`, `edit`→`edit`), always with
// `contributionsEnabled: true`; landing on `view`, or the ceiling staying
// unchanged, issues no call at all — MCRO001-D003 §3.3: "the stored policy
// is left as-is rather than force-disabled" (an inert-but-enabled stored
// policy is accepted, not corrected). This ticket's scope (per the owner's
// ruling) covers only the BROAD-audience row's ceiling, driven by Quick
// setup's Organisation/Link/Publish buttons — not a per-named-principal
// ceiling edit, which MCRO001-D003 §3.3 also describes in its fuller text
// but which is not part of M001-F0232/M001-F0297's quick-setup-row scope
// (flagged here rather than silently narrowed).
export function communityPolicyEffectForBroadCeiling(previousCeiling, nextAudience, nextCeiling) {
  if (!['link', 'scope', 'public'].includes(nextAudience)) return null;
  if (nextCeiling === previousCeiling) return null;
  const mode = nextCeiling === 'contribute' ? 'comment' : nextCeiling === 'edit' ? 'edit' : null;
  return mode ? { mode, contributionsEnabled: true } : null;
}

// Owner-reviewed correction (2026-08-22): a failed `updateCommunityPolicy`
// call must not be silently swallowed. Leaving the sharing change alone on
// failure (never rolling it back) is the right call for the TRANSACTION —
// there is nothing to undo, the share genuinely succeeded — but silence
// about the FAILURE itself would let an actor believe contribute/edit is
// now exercisable when the server-side policy gating it never updated. This
// exact string is asserted by MusicChoreography.test.js and mutation-proved
// there.
export const COMMUNITY_POLICY_UPDATE_FAILED_WARNING = "Sharing was updated, but the community contribution policy could not be updated to match — check that contribute/edit access behaves as expected.";

export function SheetPermissionsPanel({ routine, orgId, routineId, publishControl = null }) {
  const selectedRoutineId = routineId || routine?.routineId;
  const [policyWarning, setPolicyWarning] = useState('');
  // Tracks the Sheet's own last-known BROAD-audience ceiling (null when the
  // current audience carries no broad row at all, e.g. private/specific) so
  // `communityPolicyEffectForBroadCeiling` above can tell a genuine change
  // from a repeat. Reset from every successful getAccess/replaceAccess
  // response — never from client-side desired state, so a rejected mutation
  // never mis-tracks.
  const broadCeilingRef = useRef(null);
  const trackBroadCeiling = (value) => {
    broadCeilingRef.current = value && ['link', 'scope', 'public'].includes(value.audience) ? value.defaultCeiling : null;
  };
  // GitLab WebUI issue #30 / M001-D023 §7: ResourceAccessManager is generic
  // over resourceType — this binds it to one Sheet (choreo_sheet) by closing
  // over orgId/selectedRoutineId, same shape choreographyMediaSharingAdapter's
  // side uses in MediaLibraryGrid.js for choreo_media. `replaceAccess` below
  // additionally derives and issues MCRO001-F0014's community-policy update —
  // Choreo-owned behaviour that stays out of ResourceSharePanel.js, which
  // remains resourceType-generic (per that file's own header comment).
  const sharingApi = useMemo(() => ({
    getAccess: async () => {
      const response = await choreographyApi.getSharingAccess(orgId, selectedRoutineId);
      trackBroadCeiling(response?.data?.data || response?.data);
      return response;
    },
    replaceAccess: async (body, idem) => {
      const previousCeiling = broadCeilingRef.current;
      const response = await choreographyApi.replaceSharingAccess(orgId, selectedRoutineId, body, idem);
      const value = response?.data?.data || response?.data;
      trackBroadCeiling(value);
      const effect = communityPolicyEffectForBroadCeiling(previousCeiling, value?.audience, value?.defaultCeiling);
      if (effect) {
        try {
          await choreographyApi.updateCommunityPolicy(orgId, selectedRoutineId, effect);
          setPolicyWarning('');
        } catch {
          // Best-effort for the TRANSACTION (the sharing change already
          // succeeded and is never undone for a derived policy write
          // failing) — but NOT silent: an actor who just widened sharing
          // must be told the contribution policy behind it did not follow,
          // or they walk away believing contribute/edit works when the
          // server-side gate never moved.
          setPolicyWarning(COMMUNITY_POLICY_UPDATE_FAILED_WARNING);
        }
      }
      return response;
    },
    searchPrincipals: (query, types) => choreographyApi.searchSharingPrincipals(orgId, selectedRoutineId, query, types),
    createLink: (body, idem) => choreographyApi.createSharingLink(orgId, selectedRoutineId, body, idem),
    deleteLink: (body, idem) => choreographyApi.deleteSharingLink(orgId, selectedRoutineId, body, idem),
    retryInvitation: (invitationId, body) => choreographyApi.retrySharingInvitation(orgId, selectedRoutineId, invitationId, body),
    getAccessExplain: (subjectUuid) => choreographyApi.getSheetAccessExplain(orgId, selectedRoutineId, subjectUuid),
  }), [orgId, selectedRoutineId]);

  // M001-D023 v0.11 §7.1/§7.4.1: no standalone "Publish" section and no
  // standalone "Community contribution policy" section remain on this tab —
  // both fold into Quick setup's row (rendered inside ResourceAccessManager
  // itself, above "People with access") via `publishControl` below.
  return <section className="mc-sheet-permissions" aria-labelledby="mc-sheet-permissions-title">
    <header className="mc-panel-head"><div><span className="mc-kicker">Selected Choreo Sheet</span><h2 id="mc-sheet-permissions-title">Sheet access</h2><p className="mc-muted">Share <strong>{routine?.title || 'this Sheet'}</strong>. Organisation member administration remains under Manage access.</p></div></header>
    {policyWarning && <div className="mc-alert" role="alert">{policyWarning}<button type="button" onClick={() => setPolicyWarning('')}>Dismiss</button></div>}
    <ResourceAccessManager resourceLabel={routine?.title} resourceKey={`${orgId || ''}:${selectedRoutineId || ''}`} api={sharingApi} enabled={Boolean(orgId && selectedRoutineId)} publishControl={publishControl} />
  </section>;
}

// MCRO001-F0164 / T-MCRO-165 (WebUI issue #58) — the canvas "Who can access"
// panel. Owner ruling, verbatim: "I must be able to see all access by any
// path and I must be able to control what rights each access path gives."
// The share-scope path this panel lists is the one MusicChoreography's own
// create payloads used to force to 'organisation' (removed in this same
// change); it renders beside the named-grant Sheet access panel
// (SheetPermissionsPanel below) on the same Access tab, so every access path
// this app offers a routine is listed in one place.
//
// Wire contract, verified against studioaeye-choreo origin/main 84015d8:
// PATCH /api/v1/orgs/:orgId/choreography/routines/:routineId/sharing with
// body { shareScope } (backend/src/routes/choreography.routes.ts:689 →
// choreographyService.updateSharing, backend/src/services/choreography.service.ts:1159;
// client: choreographyApi.updateSharing, src/services/api.js:595 — pre-existing,
// verb and body already match). The service's shareScope() helper falls back
// to 'organisation' when the body omits the value, so Remove always sends
// the explicit 'private' rather than omitting the field.
//
// Rights are rendered exactly as the verified contract grants them, never
// invented: share_scope is READ authority only (MCRO001-D021 §5) and every
// write path is canEdit-gated (choreography.service.ts canEdit :669 —
// edit_all, or edit_own + creator, mirrored by the parent's canEdit
// derivation), so a scope row's only right is "Can view"; the creator row's
// is edit plus the sharing control itself. The Core resource-sharing engine
// state ResourceAccessManager renders is a DIFFERENT access path with a
// different wire shape — it is not fabricated here, which is why this panel
// reuses ResourceSharePanel's presentation conventions (one list, explicit
// honest empty state, no control that would 403) rather than the component
// itself.
export function CanvasAccessPanel({ routine, orgId, orgName, canEdit, currentUser, onScopeChanged }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const scope = routine?.shareScope;
  const knownScope = ['private', 'organisation', 'logged_in', 'everyone'].includes(scope);
  const isCreator = Boolean(routine?.createdBy)
    && String(routine.createdBy) === String(currentUser?.id ?? currentUser?.userId ?? '');
  const scopeRows = [
    { id: 'organisation', who: `Everyone in ${orgName || 'this organisation'}` },
    { id: 'logged_in', who: 'Anyone logged in' },
    { id: 'everyone', who: 'Anyone' },
  ];
  const activeRow = scopeRows.find((row) => row.id === scope);
  const changeScope = async (nextScope) => {
    if (!knownScope || !canEdit || saving || !routine?.routineId || !nextScope || nextScope === scope) return;
    setSaving(true);
    setError('');
    try {
      const response = await choreographyApi.updateSharing(orgId, routine.routineId, { shareScope: nextScope });
      // M004-F0663 (#130): only the scope Core returns is a saved scope. A 2xx
      // that does not echo the requested scope is not success.
      const confirmed = payload(response)?.shareScope;
      if (confirmed !== nextScope) throw Object.assign(new Error('Core did not confirm the change — the access was not saved.'), { unconfirmed: true });
      onScopeChanged?.(confirmed);
    } catch (cause) {
      setError(failureLine(writeFailure(cause, message(cause))));
    } finally {
      setSaving(false);
    }
  };
  return <section className="mc-sheet-permissions" aria-labelledby="mc-canvas-access-title">
    <header className="mc-panel-head"><div><span className="mc-kicker">Choreo Canvas</span><h2 id="mc-canvas-access-title">Who can access</h2><p className="mc-muted">Audience granted by this canvas sharing scope. Other access paths are shown under Sheet access.</p></div></header>
    {error && <div className="mc-alert" role="alert">{error}<button type="button" onClick={() => setError('')}>Dismiss</button></div>}
    <ul className="mc-sheet-grants">
      <li><span><strong>{isCreator ? 'You (creator)' : 'Routine creator'}</strong></span><em>Can edit and share</em></li>
      {/* A recognized private scope has no audience row; missing or unknown
          values cannot establish the saved scope and must not expose writes. */}
      <li>
        <span>
          <strong>{knownScope ? (activeRow ? activeRow.who : 'Only you') : 'Sharing scope unavailable'}</strong>
          {knownScope && scope === 'private' && <small>This sharing scope is private.</small>}
          {!knownScope && <small>Sharing settings are unavailable. No scope changes can be made.</small>}
        </span>
        {canEdit && knownScope && <span className="mc-canvas-access-actions">
          <select aria-label="Change who can access" value={scope} disabled={saving} onChange={(event) => changeScope(event.target.value)}>
            <option value="private">Private</option>
            {scopeRows.map((row) => <option key={row.id} value={row.id}>{row.who}</option>)}
          </select>
          {activeRow && <button type="button" onClick={() => changeScope('private')} disabled={saving}>Remove access</button>}
        </span>}
        {knownScope && activeRow && <em>Can view through this scope</em>}
      </li>
    </ul>
  </section>;
}

export const emptySnapshot = () => ({ schemaVersion: 1, sections: [], blocks: [], scenes: [], cues: [] });
export async function demonstrationSnapshotDigest(snapshot) {
  return sourceDigest(JSON.stringify(snapshot || emptySnapshot()));
}

export function latestEditableSnapshot(routine) {
  const revisions = routine?.revisions || [];
  const draft = revisions.find((revision) => revision.revisionId === routine?.draftRevisionId || revision.lifecycle === 'draft');
  return JSON.parse(JSON.stringify(draft?.stateSnapshot || revisions[0]?.stateSnapshot || emptySnapshot()));
}

// MCRO001-F0192 / T-MCRO-215. The bounded current read carries at most the exact
// draft and published pointer rows. Selection is by those pointers only: never the
// newest row, never a lifecycle guess. A draft pointer wins for an editor; a viewer
// (draftRevisionId null/omitted) gets the published pointer; otherwise nothing.
export function currentRoutineRevision(routine) {
  const revisions = Array.isArray(routine?.revisions) ? routine.revisions : [];
  const byId = (id) => (id ? revisions.find((revision) => revision.revisionId === id) || null : null);
  return byId(routine?.draftRevisionId) || byId(routine?.publishedRevisionId) || null;
}
// Saved snapshot for the current read. Source-absent stays absent: the snapshot is
// returned exactly as stored, nothing is reconstructed from sections/blocks.
export function currentRoutineSnapshot(routine) {
  return JSON.parse(JSON.stringify(currentRoutineRevision(routine)?.stateSnapshot || emptySnapshot()));
}
const SOURCE_UNAVAILABLE_NOTICE = 'The saved choreography source is unavailable right now, so this routine cannot be shown or edited. Try again later.';
const routineReadMessage = (cause) => {
  const body = cause?.response?.data;
  if (cause?.response?.status === 409 && [body?.code, body?.error, body?.error?.code].includes('CHOREOGRAPHY_SOURCE_UNAVAILABLE')) return SOURCE_UNAVAILABLE_NOTICE;
  return message(cause);
};

export function displayCount(absoluteCount) {
  const count = Math.max(1, Number(absoluteCount) || 1);
  return ((count - 1) % 8) + 1;
}

export function applyLivePlaybackRate(audio, playbackRate) {
  const parsed = Number(playbackRate);
  const rate = Number.isFinite(parsed) ? Math.min(1.5, Math.max(0.5, parsed)) : 1;
  if (audio) {
    audio.playbackRate = rate;
    if ('preservesPitch' in audio) audio.preservesPitch = true;
    if ('mozPreservesPitch' in audio) audio.mozPreservesPitch = true;
    if ('webkitPreservesPitch' in audio) audio.webkitPreservesPitch = true;
  }
  return rate;
}

export function applyExplicitFocusPlaybackRate(audio, playbackRate) {
  const parsed = Number(playbackRate);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  try {
    if (audio) {
      audio.playbackRate = parsed;
      if ('preservesPitch' in audio) audio.preservesPitch = true;
      if ('mozPreservesPitch' in audio) audio.mozPreservesPitch = true;
      if ('webkitPreservesPitch' in audio) audio.webkitPreservesPitch = true;
    }
    return parsed;
  } catch (_) {
    return null;
  }
}

const emptyDemonstrationPlaybackSession = () => ({
  count: 1,
  rehearsalRate: 1,
  explicitFocusId: '',
  focusPlaybackRateOverride: null,
  focusTimingMap: null,
  focusAuthorityKey: '',
  focusRevisionId: '',
  focusRateLocked: false,
  focusSegmentIndex: null,
  focusSequenceLabel: '',
  focusMessage: '',
  bpm: null,
});

function withoutFocus(session, focusMessage = '') {
  return {
    ...session,
    explicitFocusId: '',
    focusPlaybackRateOverride: null,
    focusTimingMap: null,
    focusAuthorityKey: '',
    focusRevisionId: '',
    focusRateLocked: false,
    focusSegmentIndex: null,
    focusSequenceLabel: '',
    focusMessage,
  };
}

// MCRO001-F0065 (T-MCRO-065): effective allocations answer for the rows of
// the base span they inherited (effectiveRows), not their own layout rows.
const sourceAllocationRows = (allocation) => allocation.effectiveRows || allocation;

export function projectRehearsalPosition(snapshot = emptySnapshot(), absoluteCount = 1, viewingAs = null) {
  const count = Math.max(1, Number(absoluteCount) || 1);
  // MCRO001-F0065 (T-MCRO-065): a cast number switches the source view to
  // that dancer's effective choreography (base minus replaced spans plus
  // their group's variant lines). Sources without accepted @group lines
  // project byte-identically — the parameter is inert there.
  const viewer = Number(viewingAs);
  const viewingDancer = viewingAs !== null && viewingAs !== undefined && viewingAs !== ''
    && Number.isSafeInteger(viewer) && viewer >= 1 ? viewer : null;
  const cues = [...(snapshot.cues || [])].sort((a, b) => Number(a.atCount) - Number(b.atCount));
  const sections = [...(snapshot.sections || [])].sort((a, b) => Number(a.startCount) - Number(b.startCount));
  const snapshotBlocks = [...(snapshot.blocks || [])].sort((a, b) => Number(a.startCount) - Number(b.startCount));
  const currentSection = sections.find((section) => count >= Number(section.startCount) && count <= Number(section.endCount)) || null;
  const currentSectionIndex = currentSection ? sections.findIndex((section) => section.id === currentSection.id) : -1;
  const snapshotBlock = snapshotBlocks.find((block) => count >= Number(block.startCount) && count < Number(block.startCount) + Number(block.countLength)) || null;
  const sectionBlocks = currentSection
    ? (snapshot.blocks || []).filter((block) => block.sectionId === currentSection.id || (Number(block.startCount) >= Number(currentSection.startCount) && Number(block.startCount) <= Number(currentSection.endCount))).sort((a, b) => Number(a.startCount) - Number(b.startCount))
    : [];
  let currentBlock = snapshotBlock;
  let blockIndex = snapshotBlock ? sectionBlocks.findIndex((block) => block.id === snapshotBlock.id) : -1;
  const practiceLines = String(currentSection?.practiceNote || '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  let sourceSection = null;
  let sourceAllocation = null;
  let sourceScore = null;
  let authoredBlockStarts = snapshotBlocks.map((block) => Number(block.startCount));
  if (typeof snapshot.sourceDocument?.body === 'string' && snapshot.sourceDocument.body.trim()) {
    sourceScore = projectChoreoCanvas(snapshot.sourceDocument.body);
    // MCRO001-F0065 (T-MCRO-065): "viewing as" projects the source score
    // through the dancer's effective choreography before the row lookups
    // below; effective allocations carry `effectiveRows` (the rows they
    // answer for in this view), which the span lookups prefer.
    if (viewingDancer !== null && (sourceScore.groups || []).length > 0) {
      sourceScore = { ...sourceScore, sections: effectiveChoreography(sourceScore, viewingDancer).sections };
    }
    const sourceBlockStarts = sourceScore.sections.flatMap((source, index) => {
      const section = sections[index];
      if (!section) return [];
      return (source.allocations || []).map((allocation) => Number(section.startCount) + (Number(sourceAllocationRows(allocation).startRow) - 1) * 8);
    }).filter(Number.isFinite);
    if (sourceBlockStarts.length) authoredBlockStarts = sourceBlockStarts;
    if (currentSection) {
      sourceSection = sourceScore.sections[currentSectionIndex] || null;
      const row = Math.floor((count - Number(currentSection.startCount)) / 8) + 1;
      sourceAllocation = sourceSection?.allocations?.find((allocation) => {
        const span = sourceAllocationRows(allocation);
        return row >= Number(span.startRow) && row <= Number(span.endRow);
      }) || null;
      if (sourceAllocation) {
        blockIndex = sourceSection.allocations.findIndex((allocation) => allocation.id === sourceAllocation.id);
        const sourceStartCount = Number(currentSection.startCount) + (Number(sourceAllocationRows(sourceAllocation).startRow) - 1) * 8;
        const sourceText = (sourceAllocation.movementLines || []).map(({ text }) => String(text || '').trim()).filter(Boolean).join('\n');
        currentBlock = {
          ...(snapshotBlock || {}),
          id: `source-${sourceAllocation.id}`,
          sectionId: currentSection.id,
          startCount: sourceStartCount,
          countLength: Number(sourceAllocation.countLength) || Number(sourceAllocation.rowSpan) * 8,
          spanRows: Number(sourceAllocation.rowSpan) || 1,
          ...(sourceAllocation.sequenceTitle ? { sequenceTitle: sourceAllocation.sequenceTitle } : {}),
          ...(sourceText ? { text: sourceText } : {}),
        };
      }
    }
  }
  const barText = String(currentBlock?.text || '').trim() || practiceLines[Math.max(0, blockIndex)] || '';
  const blockCountLength = Math.max(1, Number(currentBlock?.countLength) || 8);
  const countIndex = currentBlock ? Math.min(blockCountLength, Math.max(1, count - Number(currentBlock.startCount) + 1)) : 0;
  const barCount = currentBlock ? Math.max(1, Number(currentBlock.spanRows) || Math.ceil(blockCountLength / 8)) : 0;
  const barIndex = currentBlock ? Math.min(barCount, Math.floor((count - Number(currentBlock.startCount)) / 8) + 1) : 0;
  let nextBlock = null;
  if (sourceAllocation && sourceSection && sourceScore) {
    let nextAllocation = sourceSection.allocations[sourceSection.allocations.findIndex((allocation) => allocation.id === sourceAllocation.id) + 1] || null;
    let nextSourceSection = sourceSection;
    let nextSection = currentSection;
    if (!nextAllocation) {
      for (let index = currentSectionIndex + 1; index < sourceScore.sections.length; index += 1) {
        if (sourceScore.sections[index]?.allocations?.length) {
          nextSourceSection = sourceScore.sections[index];
          nextSection = sections[index];
          [nextAllocation] = nextSourceSection.allocations;
          break;
        }
      }
    }
    if (nextAllocation && nextSection) {
      const startCount = Number(nextSection.startCount) + (Number(sourceAllocationRows(nextAllocation).startRow) - 1) * 8;
      const text = (nextAllocation.movementLines || []).map(({ text: line }) => String(line || '').trim()).filter(Boolean).join('\n');
      nextBlock = {
        id: `source-${nextAllocation.id}`,
        sectionId: nextSection.id,
        startCount,
        countLength: Number(nextAllocation.countLength) || Number(nextAllocation.rowSpan) * 8,
        spanRows: Number(nextAllocation.rowSpan) || 1,
        ...(nextAllocation.sequenceTitle ? { sequenceTitle: nextAllocation.sequenceTitle } : {}),
        ...(text ? { text } : {}),
      };
    }
  } else {
    nextBlock = snapshotBlocks.find((block) => Number(block.startCount) > count) || null;
  }
  const sectionCountIndex = currentSection ? count - Number(currentSection.startCount) : null;
  const previousAuthoredCount = [...authoredBlockStarts, ...cues.map((cue) => Number(cue.atCount))]
    .filter((value) => Number.isFinite(value) && value < count)
    .reduce((nearest, value) => Math.max(nearest, value), 0) || null;
  return { absoluteCount: count, displayCount: displayCount(count),
    currentSection,
    sectionTitle: sourceSection?.label || currentSection?.label || null,
    sectionProgress: currentSection ? { sectionIndex: currentSectionIndex + 1, barIndex: Math.floor(sectionCountIndex / 8) + 1, beatIndex: (sectionCountIndex % 8) + 1 } : null,
    sequenceTitle: sourceAllocation?.sequenceTitle || currentBlock?.sequenceTitle || null,
    currentBlock: currentBlock ? { ...currentBlock, indexWithinSection: blockIndex + 1, text: barText } : null,
    nextBlock,
    previousAuthoredCount,
    blockProgress: currentBlock ? { countIndex, countLength: blockCountLength, barIndex, barCount } : null,
    currentCue: [...cues].reverse().find((cue) => Number(cue.atCount) <= count) || null,
    nextCue: cues.find((cue) => Number(cue.atCount) > count) || null,
    // MCRO001-F0064 (T-MCRO-064): the current section's numbered-dancer
    // formations, straight from the same live source projection — present only
    // when the section actually has them, so existing position shapes for
    // formation-less sources are byte-identical.
    ...(sourceSection?.formations?.length ? { formations: sourceSection.formations } : {}) };
}

export function canOpenRehearsal(routine, canEdit) {
  return Boolean(routine && (canEdit || routine.publishedRevisionId));
}

export function shouldUseDraftPreview(routine, canEdit, dirty) {
  return Boolean(routine && canEdit && (routine.draftRevisionId || dirty));
}

// T-MCRO-025 cases C/F/J: editors always name the projection source explicitly
// (auto|draft|published); a non-editor's omitted source stays published-only
// and can never infer a draft.
export function rehearsalRequestSource(canEdit, sourceChoice = 'auto') {
  if (!canEdit) return undefined;
  return sourceChoice === 'draft' || sourceChoice === 'published' ? sourceChoice : 'auto';
}

// T-MCRO-025 case C: an acknowledged draft response may only be applied while no
// newer in-memory draft state exists. A newer in-memory snapshot or unsaved
// local edits always win over a server response requested earlier.
export function shouldDiscardDraftProjection(inMemoryAtRequest, inMemoryNow) {
  if (!inMemoryNow?.snapshot) return false;
  if (inMemoryNow.dirty) return true;
  return inMemoryNow.snapshot !== inMemoryAtRequest?.snapshot;
}

// The routine's authored tempo, or null when it genuinely has none. A zero,
// negative, absent or non-numeric bpm is not a tempo and must never be coerced
// into one — `projectMappedDemonstration` already treats null as "no projection"
// and that honest empty state is the correct outcome.
function draftTrackBpm(routine = {}) {
  const bpm = Number(routine.bpm);
  return Number.isFinite(bpm) && bpm > 0 ? bpm : null;
}

export function draftRehearsalView(snapshot = emptySnapshot(), routine = {}, absoluteCount = 1, dirty = false, viewingAs = null) {
  return {
    routineId: routine.routineId,
    revisionId: routine.draftRevisionId || null,
    revisionNumber: (routine.revisions || []).find((revision) => revision.revisionId === routine.draftRevisionId)?.revisionNumber || null,
    title: routine.title,
    projectionSource: 'draft',
    preview: true,
    dirty,
    readOnly: true,
    offlineCacheable: false,
    track: {
      title: routine.trackTitle || routine.title,
      // MCRO001-F0027 / T-MCRO-027 (D006 §3): the authored tempo belongs to the
      // routine's TRACK, not to its audio asset. It is what turns a count into
      // rehearsal time (`segmentRehearsalDurationAt100%`), so every F0027
      // projection — mapped video frame, segmentBaseVideoRate, effective video
      // rate — is impossible without it. Reporting `null` here regardless of
      // the routine's real tempo meant a saved F0026 demonstration could never
      // project on an UNPUBLISHED draft, which is precisely where a map is
      // authored and rehearsed: the backdrop stayed at
      // data-projection-state="none" and mounted no <video> at all. Draft
      // preview withholds AUDIO (no authorised audio binding exists for a
      // draft) — it has never had a reason to withhold the tempo.
      //
      // `null` stays the honest answer, and the projection stays honestly
      // absent, only when the routine genuinely has no authored tempo: the
      // track's bpm column is nullable and a substitute would silently
      // mis-time every projected frame.
      bpm: draftTrackBpm(routine),
      firstCountOffsetMs: Number.isFinite(Number(routine.firstCountOffsetMs)) ? Number(routine.firstCountOffsetMs) : 0,
      audioAvailable: false,
      audioUnavailableReason: 'Draft preview uses counts and walkthrough until an authorised saved audio preview is available',
    },
    snapshot,
    position: projectRehearsalPosition(snapshot, absoluteCount, viewingAs),
    supportedModes: ['count_led', 'walkthrough'],
    playbackRate: { min: 0.5, max: 1.5, default: 1, pitchPreservationRequested: false },
    media: [],
  };
}

export function hasRehearsalContent(snapshot = emptySnapshot()) {
  return ['sections', 'blocks', 'cues'].some((key) => Array.isArray(snapshot?.[key]) && snapshot[key].length > 0);
}

export function rehearsalBarLabel(block) {
  if (!block) return '';
  const bars = Number(block.spanRows) || Math.ceil(Number(block.countLength) / 8);
  if (!Number.isFinite(bars) || bars < 1) return '';
  return bars === 1 ? '[8]' : `[${bars}\u00d78]`;
}

export function rehearsalCardContent(position, kind) {
  const isNow = kind === 'now';
  const currentBlock = position?.currentBlock || null;
  const nextBlock = position?.nextBlock || null;
  const currentCue = position?.currentCue || null;
  const nextCue = position?.nextCue || null;
  const currentCueInBlock = !currentBlock || Number(currentCue?.atCount) >= Number(currentBlock.startCount);
  const nextBlockComesFirst = nextBlock && (!nextCue || Number(nextBlock.startCount) <= Number(nextCue.atCount));
  const cue = isNow ? (currentCueInBlock ? currentCue : null) : (nextBlockComesFirst ? null : nextCue);
  const block = isNow ? currentBlock : (nextBlockComesFirst ? nextBlock : (nextCue && currentBlock && Number(nextCue.atCount) < Number(nextBlock?.startCount || Infinity) ? currentBlock : nextBlock));
  const blockText = String(block?.text || block?.sequenceTitle || '').trim();
  const cueText = String(cue?.text || '').trim();
  const barCount = Number(position?.blockProgress?.barCount);
  const barIndex = Number(position?.blockProgress?.barIndex);
  const containedBar = Number.isFinite(barCount) && barCount > 1 && Number.isFinite(barIndex)
    ? Math.min(barCount, Math.max(1, barIndex))
    : null;
  return {
    text: blockText || cueText || String(block?.label || '').trim() || (isNow ? 'Listen for the entrance' : 'End of routine'),
    detail: [blockText && cueText !== blockText ? cueText : '', cue?.teachingPoint || ''].filter(Boolean).join(' \u00b7 '),
    barLabel: rehearsalBarLabel(block),
    barProgress: isNow && containedBar ? `${containedBar} / ${barCount}` : '',
  };
}

export function rehearsalCountLabel(position) {
  const progress = position?.sectionProgress;
  if (!progress) return `Absolute count ${position?.absoluteCount || 1}`;
  return `Section ${progress.sectionIndex} / 8-beat bar ${progress.barIndex} / beat ${progress.beatIndex}`;
}

export function nextRehearsalCount(position, count) {
  const nextBlockCount = Number(position?.nextBlock?.startCount);
  const nextCueCount = Number(position?.nextCue?.atCount);
  const candidates = [nextBlockCount, nextCueCount].filter((value) => Number.isFinite(value) && value > Number(count));
  return candidates.length ? Math.min(...candidates) : Number(count) + 1;
}

export function previousRehearsalCount(position, count) {
  const previousAuthoredCount = Number(position?.previousAuthoredCount);
  if (Number.isFinite(previousAuthoredCount) && previousAuthoredCount >= 1 && previousAuthoredCount < Number(count)) return previousAuthoredCount;
  return Math.max(1, Number(count) - 1);
}

function shortenRehearsalText(value, maxLength = 52) {
  const compact = String(value || '').replace(/\s+/g, ' ').trim();
  if (!compact) return 'No movement text';
  if (compact.length <= maxLength) return compact;
  return `${compact.slice(0, maxLength - 1).trimEnd()}…`;
}

// T-MCRO-025 case G: the highest valid absolute count of a projection; used to
// clamp/reanchor the active count safely when source topology differs.
export function rehearsalCountLimit(snapshot = emptySnapshot()) {
  const ends = [
    ...(snapshot.sections || []).map((section) => Number(section.endCount)),
    ...(snapshot.blocks || []).map((block) => Number(block.startCount) + Number(block.countLength) - 1),
    ...(snapshot.cues || []).map((cue) => Number(cue.atCount)),
  ].filter(Number.isFinite);
  return Math.max(1, ...ends);
}

export function rehearsalBarOptions(snapshot = emptySnapshot(), revisionId = null) {
  const bars = [];
  [...(snapshot.sections || [])].sort((a, b) => Number(a.startCount) - Number(b.startCount)).forEach((section) => {
    const sectionStart = Math.max(1, Number(section.startCount) || 1);
    const sectionEnd = Math.max(sectionStart, Number(section.endCount) || sectionStart);
    const sectionId = section.id || `section-${sectionStart}-${sectionEnd}`;
    for (let startCount = sectionStart; startCount <= sectionEnd; startCount += 8) {
      const endCount = Math.min(sectionEnd, startCount + 7);
      const position = projectRehearsalPosition(snapshot, startCount);
      const fullText = String(position.currentBlock?.text || position.sequenceTitle || position.sectionTitle || 'No movement text').replace(/\s+/g, ' ').trim();
      const text = shortenRehearsalText(fullText);
      const barIndex = bars.length + 1;
      bars.push({
        barId: `${revisionId || 'unbound'}:${sectionId}:${startCount}-${endCount}`,
        revisionId,
        barIndex,
        label: `${barIndex} · ${text}`,
        startCount,
        endCount,
        sectionId,
        sectionTitle: position.sectionTitle || section.label || null,
        text,
        fullText,
        blockProgress: position.blockProgress,
      });
    }
  });
  return bars;
}

export function validateEditorSnapshot(snapshot) {
  const errors = [];
  const blocks = [...(snapshot.blocks || [])].sort((a, b) => Number(a.startCount) - Number(b.startCount));
  blocks.forEach((block, index) => {
    if (!Number.isInteger(Number(block.startCount)) || Number(block.startCount) < 1) errors.push(`${block.label || `Block ${index + 1}`} needs a positive start count.`);
    if (!Number.isInteger(Number(block.countLength)) || Number(block.countLength) < 1) errors.push(`${block.label || `Block ${index + 1}`} needs a positive length.`);
    if (index && Number(block.startCount) < Number(blocks[index - 1].startCount) + Number(blocks[index - 1].countLength)) errors.push('Count blocks cannot overlap.');
  });
  (snapshot.sections || []).forEach((section) => {
    if (!String(section.label || '').trim()) errors.push('Every section needs a label.');
    if (Number(section.endCount) < Number(section.startCount)) errors.push(`${section.label || 'Section'} ends before it starts.`);
  });
  const sections = [...(snapshot.sections || [])].sort((a, b) => Number(a.startCount) - Number(b.startCount));
  sections.slice(1).forEach((section, index) => { if (Number(section.startCount) <= Number(sections[index].endCount)) errors.push('Song sections cannot overlap.'); });
  return [...new Set(errors)];
}

export function findBoundaryReanchor(baseline, edited) {
  const changed = (edited.blocks || []).find((block) => { const previous = (baseline.blocks || []).find((item) => item.id === block.id); return previous && Number(previous.startCount) !== Number(block.startCount); });
  const previous = changed && (baseline.blocks || []).find((item) => item.id === changed.id);
  if (!previous) return null;
  const hasDependents = (baseline.scenes || []).some((scene) => scene.startCount >= previous.startCount)
    || (baseline.cues || []).some((cue) => cue.atCount >= previous.startCount);
  return hasDependents ? { fromCount: previous.startCount, deltaCounts: Number(changed.startCount) - Number(previous.startCount), blockId: changed.id } : null;
}

function RowField({ label, value, onChange, type = 'text', min, className = '' }) {
  return <label className={`mc-field ${className}`}><span>{label}</span><input type={type} min={min} value={value ?? ''} onChange={(event) => onChange(type === 'number' ? Number(event.target.value) : event.target.value)} /></label>;
}

const COMPOSER_DRAFT_VERSION = 1;
const composerDraftStorageKey = (draftKey) => `studioaeye.choreography.composer.v${COMPOSER_DRAFT_VERSION}:${draftKey || 'default'}`;
const reviewKey = (section) => `${section.sourceLine}:${section.label}`;
const emptyPasteFormat = () => ({ status: 'idle', message: '', beforeSource: '', formattedSource: '' });

function isLargePaste(value) {
  const text = String(value || '');
  return text.length >= 400 || text.split(/\r?\n/).filter((line) => line.trim()).length >= 6;
}

function isAlreadyStructuredPaste(value) {
  const { analysis } = analyseBlueprintNotes(value);
  return analysis.sectionsDetected > 0 && analysis.provisional === false && analysis.unresolved.length === 0;
}

function insertionOnlyCaretMap(source, formatted) {
  const offsets = [0];
  let sourceIndex = 0;
  for (let formattedIndex = 0; formattedIndex < formatted.length && sourceIndex < source.length; formattedIndex += 1) {
    if (formatted[formattedIndex] !== source[sourceIndex]) continue;
    sourceIndex += 1;
    offsets[sourceIndex] = formattedIndex + 1;
  }
  return sourceIndex === source.length ? offsets : null;
}

async function sourceDigest(value) {
  const bytes = typeof globalThis.TextEncoder === 'function'
    ? new globalThis.TextEncoder().encode(value)
    : Uint8Array.from(String(value), (character) => character.charCodeAt(0) & 255);
  if (globalThis.crypto?.subtle) {
    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  // The exact source/revision guards remain authoritative in older test browsers;
  // this fallback is only a response correlation value, never a security boundary.
  let hash = 2166136261;
  bytes.forEach((byte) => { hash ^= byte; hash = Math.imul(hash, 16777619); });
  return `fallback-${(hash >>> 0).toString(16)}-${bytes.length}`;
}

function readComposerDraft(draftKey) {
  try {
    const parsed = JSON.parse(globalThis.localStorage?.getItem(composerDraftStorageKey(draftKey)) || 'null');
    return parsed?.version === COMPOSER_DRAFT_VERSION && typeof parsed.title === 'string' && typeof parsed.notes === 'string' ? parsed : null;
  } catch { return null; }
}

function snapshotWithReviewedCounts(baseSnapshot, sections, reviewedCounts) {
  const snapshot = { ...baseSnapshot, sections: [], blocks: [] };
  let nextCount = 1;
  sections.forEach((analysisSection, index) => {
    const source = baseSnapshot.sections[index];
    if (!source) return;
    const reviewed = reviewedCounts[reviewKey(analysisSection)];
    const countLength = analysisSection.provisional && Number.isInteger(reviewed) ? reviewed * 8 : analysisSection.countLength;
    const sectionId = source.id;
    snapshot.sections.push({ ...source, startCount: nextCount, endCount: nextCount + countLength - 1 });
    const reviewedSection = analysisSection.provisional && Number.isInteger(reviewed);
    const sourceBlocks = (baseSnapshot.blocks || []).filter((block) => block.sectionId === sectionId);
    const barLengths = reviewedSection
      ? Array.from({ length: reviewed }, () => 8)
      : sourceBlocks.map((block) => Number(block.countLength));
    let blockStart = nextCount;
    barLengths.forEach((barLength) => {
      snapshot.blocks.push({ id: `block-${snapshot.blocks.length + 1}`, label: String(snapshot.blocks.length + 1), startCount: blockStart, countLength: barLength, sectionId });
      blockStart += barLength;
    });
    nextCount += countLength;
  });
  return snapshot;
}

function movementRows(allocation) {
  const rows = Array.from({ length: allocation.displayRowSpan || allocation.fragments?.length || allocation.rowSpan }, () => []);
  allocation.movementLines.forEach((line, index) => rows[Math.min(index, rows.length - 1)].push(line));
  return rows;
}

export function snapshotFromCanvasScore(score) {
  const sections = [];
  const blocks = [];
  let nextCount = 1;
  score.sections.forEach((scoreSection, sectionIndex) => {
    const sectionId = `section-${sectionIndex + 1}`;
    const countLength = (scoreSection.allocations || []).reduce((total, allocation) => total + Math.max(1, Number(allocation.countLength) || 8), 0);
    // Empty source headings are zero-duration boundaries. Their exact source
    // remains in sourceDocument; do not turn the optional four-row reference
    // frame into stored section duration or synthetic blocks.
    if (countLength === 0) return;
    const movement = [
      ...scoreSection.allocations.flatMap(({ movementLines }) => movementLines.map(({ text }) => text)),
      // D002 §5.9 `//` lines are section-attached notes: retained as practice
      // notes without consuming counts or creating allocations.
      ...(scoreSection.noteLines || []).map(({ text }) => text),
    ].filter(Boolean);
    let allocationCount = nextCount;
    scoreSection.allocations.forEach((allocation) => {
      const allocationCountLength = Math.max(1, Number(allocation.countLength) || 8);
      const startCount = Number.isInteger(allocation.countStart) ? allocation.countStart : allocationCount;
      const spanRows = Math.ceil(allocationCountLength / 8);
      const text = (allocation.movementLines || []).map((line) => String(line.text || '').trim()).filter(Boolean).join('\n');
      blocks.push({
        startCount,
        countLength: allocationCountLength,
        spanRows,
        sectionId,
        ...(text ? { text } : {}),
        ...(allocation.sequenceTitle ? { sequenceTitle: allocation.sequenceTitle } : {}),
      });
      allocationCount = startCount + allocationCountLength;
    });
    sections.push({
      id: sectionId,
      label: scoreSection.label,
      kind: 'custom',
      startCount: nextCount,
      endCount: nextCount + countLength - 1,
      ...(movement.length > 0 ? { practiceNote: movement.join('\n') } : {}),
    });
    nextCount += countLength;
  });
  blocks.forEach((block, index) => { block.id = `block-${index + 1}`; block.label = String(index + 1); });
  return { schemaVersion: 1, sections, blocks, scenes: [], cues: [] };
}

export function NamedSequenceCanvas({ score, onEditLine }) {
  return <section className="mc-canvas-score" aria-label="Formatted choreography score">
    {score.sections.map((section) => <section className="mc-canvas-score-section" key={section.id} aria-label={`${section.label}, optional 32-count reference frame`}>
      <header className="mc-canvas-section-rule">
        <button type="button" onClick={() => onEditLine(section.sourceLine)} aria-label={`Edit ${section.label} source line`}>{section.label}</button>
        <span aria-hidden="true"></span><strong>32-count frame</strong>
      </header>
      {section.allocations.length > 0 && <div className="mc-canvas-rhythm-paper" style={{ '--mc-canvas-rows': section.rows }}>
        {Array.from({ length: section.rows }, (_, index) => <React.Fragment key={`row-${index + 1}`}>
          <span className={`mc-canvas-row-wash ${index % 2 ? 'shade' : ''}`} style={{ gridRow: index + 1 }} aria-hidden="true"></span>
          <span className="mc-canvas-eight" style={{ gridRow: index + 1 }} aria-hidden="true">{section.rowCounts?.[index] || 8}</span>
        </React.Fragment>)}
        {section.allocations.map((allocation) => {
          const rows = movementRows(allocation);
          const displayRowSpan = allocation.displayRowSpan || allocation.fragments?.length || allocation.rowSpan;
          const isSpanning = displayRowSpan > 1;
          return <div
            className={`mc-canvas-allocation ${isSpanning ? 'spanning' : 'single'}`}
            style={{ gridRow: `${allocation.startRow} / span ${displayRowSpan}`, '--mc-allocation-span': displayRowSpan }}
            role="group"
            aria-label={`${allocation.sequenceTitle ? `${allocation.sequenceTitle}, ` : ''}counts ${allocation.countStart ?? 'unknown'}–${allocation.countEnd ?? 'unknown'}, display rows ${allocation.startRow}${displayRowSpan > 1 ? ` through ${allocation.endRow}` : ''}`}
            key={allocation.id}
          >
            {isSpanning ? <>
              <span className="mc-canvas-span-bracket" aria-hidden="true"><i className="top"></i><i className="stem"></i><i className="bottom"></i><b>{allocation.durationLabel}</b></span>
              {allocation.sequenceTitle && <button type="button" className="mc-canvas-sequence-title" onClick={() => onEditLine(allocation.sequenceSourceLine || allocation.sourceLine)}>{allocation.sequenceTitle}</button>}
              <div className="mc-canvas-allocation-copy">{rows.map((lines, rowIndex) => <div className="mc-canvas-prose-row" key={`copy-${rowIndex + 1}`} style={{ gridRow: rowIndex + 1 }}>{lines.map((line) => <button type="button" onClick={() => onEditLine(line.sourceLine)} key={`${line.sourceLine}-${line.text}`}>{line.text}</button>)}</div>)}</div>
            </> : <>
              <button type="button" className="mc-canvas-single-duration" onClick={() => onEditLine(allocation.sourceLine)}>{allocation.durationLabel}</button>
              <div className="mc-canvas-single-copy">{rows[0].map((line) => <button type="button" onClick={() => onEditLine(line.sourceLine)} key={`${line.sourceLine}-${line.text}`}>{line.text}</button>)}</div>
            </>}
          </div>;
        })}
      </div>}
      {section.allocations.length > 0 && section.state !== 'explicit' && <p className="mc-canvas-allocation-state" role="status">{section.state === 'provisional' ? `${section.provisionalCountLength || section.provisionalRows * 8} counts are provisional; add explicit allocation tokens to confirm.` : section.state === 'incomplete' ? `${Math.max(0, 32 - Number(section.allocatedCountLength || 0))} counts remain unused in the optional reference frame.` : `${Math.max(0, Number(section.allocatedCountLength || 0) - 32)} counts continue beyond the optional 32-count frame.`}</p>}
    </section>)}
  </section>;
}

function canvasDocumentLines(source, score, view = null) {
  let sourceStart = 0;
  const lines = String(source || '').split(/\r?\n/).map((text, index) => {
    const line = { text, sourceLine: index + 1, sourceStart, kind: 'plain' };
    sourceStart += text.length + 1;
    return line;
  });
  // MCRO001-F0065 (T-MCRO-065): `view` is the variantLineView map for the
  // current "viewing as" choice — which source lines are hidden from the
  // mirror and which carry a group colour. The full-team view is `null`.
  const hiddenLines = view?.hidden ? new Set(view.hidden) : null;
  const variantNames = view?.variants || {};
  const variantMark = (line) => ({
    ...(variantNames[line.sourceLine] !== undefined ? { variant: variantNames[line.sourceLine] } : {}),
    ...(hiddenLines?.has(line.sourceLine) ? { hidden: true } : {}),
  });
  let nextSectionCount = 1;
  score.sections.forEach((section) => {
    const sectionStartCount = Number.isInteger(section.startCount) ? section.startCount : nextSectionCount;
    if (lines[section.sourceLine - 1]) lines[section.sourceLine - 1] = { ...lines[section.sourceLine - 1], kind: 'section', label: section.label, provisionalLabel: section.provisionalLabel };
    section.allocations.forEach((allocation) => {
      if (allocation.sequenceSourceLine && lines[allocation.sequenceSourceLine - 1]) {
        lines[allocation.sequenceSourceLine - 1] = { ...lines[allocation.sequenceSourceLine - 1], kind: 'sequence' };
      }
      const movementLines = allocation.movementLines.length > 0
        ? allocation.movementLines
        : [{ sourceLine: allocation.sourceLine }];
      const fragmentsByMovement = movementLines.map(() => []);
      (allocation.fragments || []).forEach((fragment, fragmentIndex) => {
        const movementIndex = Math.min(fragmentIndex, movementLines.length - 1);
        fragmentsByMovement[movementIndex].push(fragment);
      });
      movementLines.forEach((movement, movementIndex) => {
        if (!lines[movement.sourceLine - 1]) return;
        const fragments = fragmentsByMovement[movementIndex];
        const row = fragments[0]?.row || allocation.startRow + Math.min(movementIndex, allocation.rowSpan - 1);
        const layoutRows = Math.max(1, fragments.length);
        lines[movement.sourceLine - 1] = {
          ...lines[movement.sourceLine - 1],
          kind: 'allocation',
          row,
          provisional: Boolean(allocation.provisional),
          durationLabel: allocation.durationLabel,
          allocationStart: movementIndex === 0,
          rowSpan: movementIndex === 0 ? allocation.rowSpan : undefined,
          countFragments: fragments,
          layoutRows: layoutRows > 1 ? layoutRows : undefined,
          ...variantMark(lines[movement.sourceLine - 1]),
        };
      });
    });
    (section.noteLines || []).forEach((note) => {
      if (lines[note.sourceLine - 1]) lines[note.sourceLine - 1] = { ...lines[note.sourceLine - 1], kind: 'note' };
    });
    // MCRO001-F0064 (T-MCRO-064): accepted @formation lines own their source
    // line (a quiet syntax tint, no movement row, no count anchors).
    (section.formationSourceLines || []).forEach((sourceLine) => {
      if (lines[sourceLine - 1]) lines[sourceLine - 1] = { ...lines[sourceLine - 1], kind: 'formation' };
    });
    nextSectionCount = Number.isInteger(section.endCount) ? section.endCount + 1 : sectionStartCount + Number(section.allocatedCountLength || 0);
  });
  // MCRO001-F0065 (T-MCRO-065): accepted @group/@variant tokens tint their
  // own lines with the group's colour (kind 'group' lines never hide — group
  // definitions are sheet-level; kind 'variant' tokens hide with their block
  // in a per-dancer view).
  (score.groupTokenLines || []).forEach((token) => {
    if (lines[token.sourceLine - 1]) {
      lines[token.sourceLine - 1] = { ...lines[token.sourceLine - 1], kind: token.kind, variant: token.name, ...variantMark(lines[token.sourceLine - 1]) };
    }
  });
  return lines;
}

// MCRO001-D001 §5.1 selection-toolbar structural actions expressed as the exact
// MCRO001-D002 §5 canvas grammar. Every action is an insertion-only line-prefix
// edit (`# `, `[8] `, `// `) over the captured selection, mirroring the F0013
// formatter's permitted transform: no source character is removed or reordered.
export function structuralSelectionEdit(source, start, end, kind) {
  const text = String(source ?? '');
  const from = Math.max(0, Math.min(Number(start) || 0, text.length));
  const to = Math.max(from, Math.min(Number.isInteger(Number(end)) ? Number(end) : from, text.length));
  const firstLineStart = text.lastIndexOf('\n', from - 1) + 1;
  const nextBreak = text.indexOf('\n', to);
  const regionEnd = nextBreak === -1 ? text.length : nextBreak;
  const insertions = [];
  let lineStart = firstLineStart;
  let sectionDecided = false;
  const region = text.slice(firstLineStart, regionEnd);
  region.split('\n').forEach((line) => {
    const currentStart = lineStart;
    lineStart += line.length + 1;
    if (!line.trim()) return;
    const trimmed = line.trimStart();
    if (kind === 'section') {
      if (sectionDecided) return;
      sectionDecided = true;
      if (/^#/.test(trimmed)) return;
      insertions.push({ at: currentStart, insert: '# ' });
      return;
    }
    if (kind === 'counts') {
      if (/^(#|\[)/.test(trimmed) || /^\/\//.test(trimmed)) return;
      insertions.push({ at: currentStart, insert: '[8] ' });
      return;
    }
    if (kind === 'note') {
      if (/^\/\//.test(trimmed) || /^#/.test(trimmed)) return;
      insertions.push({ at: currentStart, insert: '// ' });
    }
  });
  if (insertions.length === 0) return null;
  let next = text;
  let selectionStart = from;
  let selectionEnd = to;
  [...insertions].reverse().forEach(({ at, insert }) => {
    next = `${next.slice(0, at)}${insert}${next.slice(at)}`;
  });
  insertions.forEach(({ at, insert }) => {
    if (at <= selectionStart) selectionStart += insert.length;
    if (at <= selectionEnd) selectionEnd += insert.length;
  });
  // Per-line inserts let the collaborative editor submit one `text.insert`
  // per stable sourceLineId, preserving F0012 line identity (no join/split).
  const lineInsertions = insertions.map(({ at, insert }) => ({
    line: text.slice(0, at).split('\n').length - 1,
    offsetInLine: 0,
    insert,
  }));
  return { next, selectionStart, selectionEnd, insertions: lineInsertions };
}

function editableCanvasText(text, caretOffset, className, selectionStart, selectionEnd) {
  const offset = Number.isInteger(caretOffset) ? Math.max(0, Math.min(text.length, caretOffset)) : null;
  const start = Number.isInteger(selectionStart) ? Math.max(0, Math.min(text.length, selectionStart)) : null;
  const end = Number.isInteger(selectionEnd) ? Math.max(0, Math.min(text.length, selectionEnd)) : null;
  const boundaries = [...new Set([0, text.length, offset, start, end].filter(Number.isInteger))].sort((a, b) => a - b);
  return <span className={className}>{boundaries.slice(0, -1).map((from, index) => {
    const to = boundaries[index + 1];
    const selected = start !== null && end !== null && from < end && to > start;
    return <React.Fragment key={`${from}-${to}`}>{offset === from && <i className="mc-live-fake-caret" aria-hidden="true"></i>}<span className={selected ? 'mc-live-selection' : undefined}>{text.slice(from, to)}</span></React.Fragment>;
  })}{offset === text.length && <i className="mc-live-fake-caret" aria-hidden="true"></i>}</span>;
}

function highlightedCanvasLine(line, caretOffset, selectionStart, selectionEnd) {
  const section = line.text.match(/^(\s*#(?!#)\s*)(.*)$/);
  if (section) {
    const prefixCaret = Number.isInteger(caretOffset) && caretOffset < section[1].length ? caretOffset : undefined;
    const titleCaret = Number.isInteger(caretOffset) && caretOffset >= section[1].length ? caretOffset - section[1].length : undefined;
    return <span className="mc-live-section-title">
      {editableCanvasText(section[1], prefixCaret, 'mc-live-syntax', selectionStart, Math.min(selectionEnd, section[1].length))}
      {editableCanvasText(section[2], titleCaret, undefined, Math.max(0, selectionStart - section[1].length), Math.max(0, selectionEnd - section[1].length))}
      {line.provisionalLabel && !section[2] && <span className="mc-live-inferred-label">{line.label}</span>}
    </span>;
  }
  const sequence = line.text.match(/^(\s*##\s*)(.*)$/);
  if (sequence) return <>{editableCanvasText(sequence[1], Number.isInteger(caretOffset) && caretOffset < sequence[1].length ? caretOffset : undefined, 'mc-live-syntax', selectionStart, Math.min(selectionEnd, sequence[1].length))}{editableCanvasText(sequence[2], Number.isInteger(caretOffset) && caretOffset >= sequence[1].length ? caretOffset - sequence[1].length : undefined, undefined, Math.max(0, selectionStart - sequence[1].length), Math.max(0, selectionEnd - sequence[1].length))}</>;
  // MCRO001-F0064 (T-MCRO-064): an accepted @formation line tints only its
  // token, exactly like the other quiet syntax classes.
  const formation = line.text.match(/^(\s*@formation\s+)(.*)$/i);
  if (formation) {
    return <>{editableCanvasText(formation[1], Number.isInteger(caretOffset) && caretOffset < formation[1].length ? caretOffset : undefined, 'mc-live-syntax formation', selectionStart, Math.min(selectionEnd, formation[1].length))}{editableCanvasText(formation[2], Number.isInteger(caretOffset) && caretOffset >= formation[1].length ? caretOffset - formation[1].length : undefined, undefined, Math.max(0, selectionStart - formation[1].length), Math.max(0, selectionEnd - formation[1].length))}</>;
  }
  // MCRO001-F0065 (T-MCRO-065): accepted @group/@variant tokens tint only
  // their token, coloured by their group via the line's --mc-variant-colour.
  const groupToken = line.text.match(/^(\s*@group\s+)(.*)$/i);
  if (groupToken) {
    return <>{editableCanvasText(groupToken[1], Number.isInteger(caretOffset) && caretOffset < groupToken[1].length ? caretOffset : undefined, 'mc-live-syntax group', selectionStart, Math.min(selectionEnd, groupToken[1].length))}{editableCanvasText(groupToken[2], Number.isInteger(caretOffset) && caretOffset >= groupToken[1].length ? caretOffset - groupToken[1].length : undefined, undefined, Math.max(0, selectionStart - groupToken[1].length), Math.max(0, selectionEnd - groupToken[1].length))}</>;
  }
  const variantToken = line.text.match(/^(\s*@variant\s+)(.*)$/i);
  if (variantToken) {
    return <>{editableCanvasText(variantToken[1], Number.isInteger(caretOffset) && caretOffset < variantToken[1].length ? caretOffset : undefined, 'mc-live-syntax variant', selectionStart, Math.min(selectionEnd, variantToken[1].length))}{editableCanvasText(variantToken[2], Number.isInteger(caretOffset) && caretOffset >= variantToken[1].length ? caretOffset - variantToken[1].length : undefined, undefined, Math.max(0, selectionStart - variantToken[1].length), Math.max(0, selectionEnd - variantToken[1].length))}</>;
  }
  const allocation = line.text.match(/^(\s*\[[^\]]+\])(\s*)(.*)$/);
  if (allocation) {
    const syntaxEnd = allocation[1].length;
    const gapEnd = syntaxEnd + allocation[2].length;
    return <>{editableCanvasText(allocation[1], Number.isInteger(caretOffset) && caretOffset < syntaxEnd ? caretOffset : undefined, 'mc-live-syntax allocation', selectionStart, Math.min(selectionEnd, syntaxEnd))}{editableCanvasText(allocation[2], Number.isInteger(caretOffset) && caretOffset >= syntaxEnd && caretOffset < gapEnd ? caretOffset - syntaxEnd : undefined, undefined, Math.max(0, selectionStart - syntaxEnd), Math.max(0, selectionEnd - syntaxEnd))}{editableCanvasText(allocation[3], Number.isInteger(caretOffset) && caretOffset >= gapEnd ? caretOffset - gapEnd : undefined, undefined, Math.max(0, selectionStart - gapEnd), Math.max(0, selectionEnd - gapEnd))}</>;
  }
  const note = line.text.match(/^(\s*\/\/)(\s*)(.*)$/);
  if (note) {
    const syntaxEnd = note[1].length;
    const gapEnd = syntaxEnd + note[2].length;
    return <>{editableCanvasText(note[1], Number.isInteger(caretOffset) && caretOffset < syntaxEnd ? caretOffset : undefined, 'mc-live-syntax note', selectionStart, Math.min(selectionEnd, syntaxEnd))}{editableCanvasText(note[2], Number.isInteger(caretOffset) && caretOffset >= syntaxEnd && caretOffset < gapEnd ? caretOffset - syntaxEnd : undefined, undefined, Math.max(0, selectionStart - syntaxEnd), Math.max(0, selectionEnd - syntaxEnd))}{editableCanvasText(note[3], Number.isInteger(caretOffset) && caretOffset >= gapEnd ? caretOffset - gapEnd : undefined, 'mc-live-note-text', Math.max(0, selectionStart - gapEnd), Math.max(0, selectionEnd - gapEnd))}</>;
  }
  const bullet = line.text.match(/^(\s*[•*-])(\s*)(.*)$/);
  if (bullet) {
    const syntaxEnd = bullet[1].length;
    const gapEnd = syntaxEnd + bullet[2].length;
    return <>{editableCanvasText(bullet[1], Number.isInteger(caretOffset) && caretOffset < syntaxEnd ? caretOffset : undefined, 'mc-live-syntax bullet', selectionStart, Math.min(selectionEnd, syntaxEnd))}{editableCanvasText(bullet[2], Number.isInteger(caretOffset) && caretOffset >= syntaxEnd && caretOffset < gapEnd ? caretOffset - syntaxEnd : undefined, undefined, Math.max(0, selectionStart - syntaxEnd), Math.max(0, selectionEnd - syntaxEnd))}{editableCanvasText(bullet[3], Number.isInteger(caretOffset) && caretOffset >= gapEnd ? caretOffset - gapEnd : undefined, undefined, Math.max(0, selectionStart - gapEnd), Math.max(0, selectionEnd - gapEnd))}</>;
  }
  return editableCanvasText(line.text || '\u00a0', caretOffset, undefined, selectionStart, selectionEnd);
}

// A remote collaborator caret renders as a transparent metric ghost of the
// line's own formatted content: identical spans, identical classes, identical
// layout — so the injected caret/selection land exactly where the text sits,
// including wrapped lines and section-title offsets. Only the caret bar, the
// translucent selection wash and the name chip are visible; the layer is
// pointer-events:none and aria-hidden with the rest of the mirror, so typing,
// IME composition, scrolling and focus are untouched (D004 §5.3).
function RemoteCursorLayer({ line, cursor }) {
  const selection = Number.isInteger(cursor.selectionEnd)
    ? { start: Math.min(cursor.offset, cursor.selectionEnd), end: Math.max(cursor.offset, cursor.selectionEnd) }
    : { start: undefined, end: undefined };
  const label = String(cursor.label || 'Editor');
  return <span
    className="mc-remote-cursor-layer"
    data-remote-cursor={cursor.clientInstanceId}
    data-remote-user={cursor.userId}
    data-remote-offset={cursor.offset}
    data-remote-selection-end={Number.isInteger(cursor.selectionEnd) ? cursor.selectionEnd : undefined}
    style={{
      '--mc-remote-caret-color': cursor.color,
      '--mc-remote-selection-color': cursor.selectionColor,
      '--mc-remote-label': `"${label.replace(/[\r\n\f\u2028\u2029]/g, ' ').replace(/[\\"]/g, (character) => `\\${character}`)}"`,
    }}
  >{highlightedCanvasLine(line, cursor.offset, selection.start, selection.end)}</span>;
}

// MCRO001-F0060 requirement 7 (owner ruling 2026-08-26, screenshot-driven,
// WebUI #42): "how is this in any way good!!" — confining the rail to the
// gutter (above) stopped it ploughing through movement text, but the
// section header ("# Middle") and the [8]/[2x8] bar-count chip are
// DELIBERATELY hung back into that same gutter band (a negative left offset
// on .mc-live-section-title / .mc-live-bar-chip / .mc-live-span-bracket b —
// "the margin band where the bar-count annotations sit", per
// demonstrationTimingRailGeometry.js), so the rail's z-index:6 still painted
// straight through their glyphs at rest.
//
// Giving those two elements a higher z-index directly cannot fix this: the
// mirror that renders them has its OWN explicit z-index (1 at rest, 3 for
// documents with any wrapped/multi-row line), which makes it a stacking
// context of its own — a descendant's z-index only ever competes with
// siblings inside that SAME context, so nothing given to a header/chip can
// out-rank a context-level-6 sibling of the mirror itself. Lowering the
// mirror's z-index instead is equally unusable: at rest the mirror must stay
// UNDER the always-present textarea via explicit ordering (z-index:auto
// would still lose to the textarea's own explicit z-index, so the rail could
// never receive the pointerenter that starts hover-expand), and for
// multi-row documents the mirror is pointer-events:auto for its own custom
// hit-testing, so anything placed under it would stop receiving clicks
// across the whole canvas.
//
// The fix that touches neither: a THIRD sibling of the mirror and the rail,
// rendered AFTER the rail so it wins the paint order, at a genuinely higher
// z-index (MusicChoreography.css's .mc-gutter-glyph-halo), holding an opaque
// halo duplicate of just these two glyph types — background matches the
// canvas page chrome, padding/radius so it reads as a deliberate label chip
// (the "map-label" technique), never a bite out of the rail. It is
// aria-hidden and pointer-events:none throughout: the accessible text stays
// the mirror's own (already aria-hidden, decorative — the real source of
// truth is the underlying textarea's value) and every rail interaction
// passes straight through to the rail beneath.
//
// Positions are read off the REAL rendered header/chip elements (not
// recomputed from layout rules) so wrapped/reflowed documents stay correct
// automatically — the same "measure the live DOM" approach
// demonstrationTimingRailGeometry.js already uses for the gutter width
// itself. Recomputed on any resize of the canvas (ResizeObserver, matching
// DemonstrationTimingRail's own geometryVersion mechanism) and whenever the
// document's lines change (typing, section/allocation edits) — coalesced
// onto one animation frame per change so a burst of edits costs one
// measurement pass, not one per edit (see the incident note below).
//
// PERFORMANCE INCIDENT (2026-08-26, WebUI !263 pipeline 7202): the first cut
// of this effect depended on `[mirrorRef, containerRef, lines]` directly.
// `lines` is a brand-new array from ContinuousCanvasEditor's own
// `useMemo(() => canvasDocumentLines(value, score), [score, value])` on
// EVERY keystroke and every collaboration-driven document update — so the
// effect (tear down + recreate a ResizeObserver, then a synchronous
// querySelector/getBoundingClientRect pass over the whole document) reran on
// every single one of those, not just when a header/chip actually changed.
// Measured against the parent commit in CI: the SAME mocked-canvas Firefox
// test went from 3.1s to 25.1s, and a later test in the same file (which
// happens to assert the source textarea's value within a 5s window) timed
// out and came back empty — not because anything touched the controlled
// `value`/`onChange` path (it doesn't: this component only ever calls its
// own local `setChips`), but because the extra synchronous work, repeated on
// every render, was slow enough in Firefox specifically to blow the
// assertion's timeout during the busiest, most render-heavy part of the
// test. Chromium/WebKit absorbed the same regression with room to spare,
// which is exactly why it surfaced as "Firefox-only" without being a
// Firefox-only defect.
//
// Fixed by: (1) splitting the ResizeObserver's lifetime (mount/unmount only)
// from the measurement pass, so the observer is created once, not on every
// render — recreating one on every keystroke was real, measurable
// browser-internal bookkeeping cost, repeated for no reason; (2) coalescing
// the measurement pass itself onto one requestAnimationFrame per `lines`
// change instead of running it synchronously inline — a scheduled, cancel-
// and-reschedule rAF means a burst of several changes within the same frame
// (exactly what synchronous test typing produces, and what a fast real typist
// can too) still ends in exactly one DOM query/measure pass, not one per
// keystroke, while every actual state change still gets measured (the last
// scheduled frame always wins, so a reflow from editing unrelated body text
// still updates every header/chip's position correctly — nothing here trades
// correctness for speed, only redundant repeated work for the same result);
// (3) a plain (non-layout) effect throughout, since this overlay is
// decorative — it does not need to land before the same paint the way the
// rail's own drag geometry does.
function GutterGlyphHalo({ mirrorRef, containerRef, lines }) {
  const [chips, setChips] = useState([]);
  const linesRef = useRef(lines);
  linesRef.current = lines;
  const measureRef = useRef(() => {});
  measureRef.current = () => {
    const mirror = mirrorRef.current;
    const container = containerRef.current;
    if (!mirror || !container) { setChips([]); return; }
    const containerRect = container.getBoundingClientRect();
    const next = [];
    linesRef.current.forEach((line) => {
      // MCRO001-F0065 (T-MCRO-065): a line hidden by the per-dancer view must
      // not earn a gutter glyph either — the halo mirrors what is VISIBLE.
      if (line.hidden) return;
      const lineElement = mirror.querySelector(`.mc-live-line[data-source-line="${line.sourceLine}"]`);
      if (!lineElement) return;
      if (line.kind === 'section') {
        const title = lineElement.querySelector('.mc-live-section-title');
        const text = title?.textContent || '';
        if (!text.trim()) return;
        const rect = title.getBoundingClientRect();
        next.push({ key: `section-${line.sourceLine}`, top: rect.top - containerRect.top, left: rect.left - containerRect.left, text, variant: 'section' });
        return;
      }
      if (!line.allocationStart) return;
      // Single-bar allocations render the chip as .mc-live-bar-chip; a
      // multi-bar (spanning) allocation renders its duration label inside
      // the .mc-live-span-bracket instead — both are "the bar-count chip"
      // for this requirement, so both are covered by the one query.
      const chip = lineElement.querySelector('.mc-live-bar-chip, .mc-live-span-bracket b');
      const text = chip?.textContent || '';
      if (!text.trim()) return;
      const rect = chip.getBoundingClientRect();
      next.push({ key: `chip-${line.sourceLine}`, top: rect.top - containerRect.top, left: rect.left - containerRect.left, text, variant: line.provisional ? 'chip provisional' : 'chip' });
    });
    setChips(next);
  };
  // Mount/unmount only: the observer itself never needs to change, only what
  // it calls — always the LATEST measure(), via the ref above.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const trigger = () => measureRef.current();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(trigger) : null;
    observer?.observe(container);
    globalThis.addEventListener?.('resize', trigger);
    return () => {
      observer?.disconnect();
      globalThis.removeEventListener?.('resize', trigger);
    };
  }, [mirrorRef, containerRef]);
  // Re-measure whenever the document's lines change — coalesced onto one
  // animation frame (see the incident note above) so a burst of several
  // changes inside the same frame still costs exactly one query/measure pass.
  useEffect(() => {
    if (typeof requestAnimationFrame !== 'function') { measureRef.current(); return undefined; }
    const frame = requestAnimationFrame(() => measureRef.current());
    return () => cancelAnimationFrame(frame);
  }, [lines]);
  if (chips.length === 0) return null;
  return <div className="mc-gutter-glyph-halo" aria-hidden="true">
    {chips.map((chip) => <span key={chip.key} className={`mc-glyph-halo-chip ${chip.variant}`} style={{ top: `${chip.top}px`, left: `${chip.left}px` }}>{chip.text}</span>)}
  </div>;
}

// MCRO001-F0064 (T-MCRO-064): one compact stage rectangle rendering a
// section's numbered dancers at the given count context — a dot per known
// number at its interpolated position, plus each number's straight-line
// movement path between its consecutive anchors (thin, quiet). The whole
// rectangle is one role="img" summary; per-dot aria-labels carry the exact
// coordinates. Rendered as a quiet strip BESIDE the canvas, never overlaid on
// authored text — the MCRO001-F0060 lesson (decorative canvas garnish must
// stay out of the text's way).
export function FormationStage({ formations, count, className = '', highlight = null }) {
  const positions = formationAtCount(formations, count);
  if (positions.length === 0) return null;
  const dancers = positions.length;
  const coordinate = (value) => String(Math.round(Number(value) * 100) / 100);
  return <div className={`mc-formation-stage${className ? ` ${className}` : ''}`} role="img"
    aria-label={`Formation at count ${count}: ${dancers} ${dancers === 1 ? 'dancer' : 'dancers'}`}>
    <div className="mc-formation-stage-floor">
      <svg className="mc-formation-paths" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" focusable="false">
        {formationPaths(formations).map(({ number, from, to }, index) => <line
          key={`${number}-${index}`}
          x1={from.x} y1={from.y} x2={to.x} y2={to.y}
        />)}
      </svg>
      {positions.map(({ number, x, y }) => <span
        key={number}
        className={`mc-formation-dot${Number(highlight) === number ? ' highlighted' : ''}`}
        aria-label={`dancer ${number}, x ${coordinate(x)}, y ${coordinate(y)}`}
        style={{ left: `${x}%`, top: `${y}%` }}
      >{number}</span>)}
    </div>
  </div>;
}

export function ContinuousCanvasEditor({ value, onChange, onPaste, onCompositionStart, onCompositionEnd, inputRef, score, ariaLabel, placeholder, readOnly = false, onFocus, onKeyDown, onWorkspaceChange, demonstrationRail = null, remoteCursors = [], viewingAsView = null }) {
  const containerRef = useRef(null);
  const mirrorRef = useRef(null);
  const pointerAnchorRef = useRef(null);
  const [caret, setCaret] = useState({ sourceLine: 0, offset: 0, selectionStart: 0, selectionEnd: 0, collapsed: true, focused: false });
  const lines = useMemo(() => canvasDocumentLines(value, score, viewingAsView), [score, value, viewingAsView]);
  const hasExpandedLayout = lines.some(({ layoutRows }) => layoutRows > 1);
  const provisionalRows = score.sections.reduce((total, section) => total + Number(section.provisionalRows || 0), 0);
  const syncCaret = (target, focused = true) => {
    const start = Number(target?.selectionStart || 0);
    const end = Number(target?.selectionEnd || 0);
    const before = String(target?.value || '').slice(0, start);
    const previousBreak = before.lastIndexOf('\n');
    setCaret({ sourceLine: before.split('\n').length, offset: start - previousBreak - 1, selectionStart: start, selectionEnd: end, collapsed: start === end, focused });
  };
  useEffect(() => {
    const textarea = inputRef.current;
    if (!textarea?.addEventListener) return undefined;
    const showReviewSelection = (event) => {
      const start = Number(event.detail?.start);
      const end = Number(event.detail?.end);
      if (!Number.isInteger(start) || !Number.isInteger(end) || start === end) {
        setCaret((current) => ({ ...current, focused: false }));
        return;
      }
      const before = String(textarea.value || '').slice(0, start);
      const previousBreak = before.lastIndexOf('\n');
      setCaret({ sourceLine: before.split('\n').length, offset: start - previousBreak - 1, selectionStart: start, selectionEnd: end, collapsed: false, focused: true });
    };
    textarea.addEventListener('studioaeye:review-selection', showReviewSelection);
    return () => textarea.removeEventListener('studioaeye:review-selection', showReviewSelection);
  }, [inputRef]);
  const activeLine = lines[caret.sourceLine - 1];
  const sectionCaret = caret.focused && caret.collapsed && activeLine?.kind === 'section';
  const syncScroll = (event) => {
    if (!mirrorRef.current) return;
    mirrorRef.current.scrollTop = event.currentTarget.scrollTop;
    mirrorRef.current.scrollLeft = event.currentTarget.scrollLeft;
    onWorkspaceChange?.(event.currentTarget);
  };
  useLayoutEffect(() => {
    const textarea = inputRef.current;
    const container = containerRef.current;
    const mirror = mirrorRef.current;
    if (!textarea || !container || !mirror) return;
    textarea.style.height = 'auto';
    container.style.height = 'auto';
    const height = Math.max(544, textarea.scrollHeight, mirror.scrollHeight);
    textarea.style.height = `${height}px`;
    container.style.height = `${height}px`;
  }, [inputRef, lines, value]);

  const sourcePositionAtPoint = (clientX, clientY) => {
    const candidates = [...(mirrorRef.current?.querySelectorAll('.mc-live-line') || [])];
    if (candidates.length === 0) return 0;
    const lineElement = candidates.find((element) => { const rect = element.getBoundingClientRect(); return clientY >= rect.top && clientY <= rect.bottom; })
      || candidates.reduce((nearest, element) => {
        const rect = element.getBoundingClientRect();
        const distance = clientY < rect.top ? rect.top - clientY : clientY - rect.bottom;
        return !nearest || distance < nearest.distance ? { element, distance } : nearest;
      }, null)?.element;
    const sourceLine = Number(lineElement?.dataset.sourceLine || 1);
    const line = lines[sourceLine - 1] || lines[0];
    const content = lineElement?.querySelector('.mc-live-line-content');
    if (!content) return line.sourceStart;
    const textNodes = [];
    const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) textNodes.push(walker.currentNode);
    const point = document.caretPositionFromPoint?.(clientX, clientY)
      || (() => { const range = document.caretRangeFromPoint?.(clientX, clientY); return range ? { offsetNode: range.startContainer, offset: range.startOffset } : null; })();
    if (point?.offsetNode && content.contains(point.offsetNode)) {
      const nodeIndex = textNodes.indexOf(point.offsetNode);
      const displayedOffset = textNodes.slice(0, Math.max(0, nodeIndex)).reduce((total, node) => total + node.textContent.length, 0) + Number(point.offset || 0);
      return line.sourceStart + Math.min(line.text.length, displayedOffset);
    }
    let bestOffset = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    let consumed = 0;
    textNodes.forEach((node) => {
      for (let offset = 0; offset <= node.textContent.length; offset += 1) {
        const range = document.createRange();
        range.setStart(node, offset); range.collapse(true);
        const rect = range.getBoundingClientRect();
        const distance = Math.abs(clientX - rect.left) + Math.abs(clientY - rect.top) * 2;
        if (distance < bestDistance) { bestDistance = distance; bestOffset = consumed + offset; }
      }
      consumed += node.textContent.length;
    });
    return line.sourceStart + Math.min(line.text.length, bestOffset);
  };
  const selectAtPoint = (event, extend = false) => {
    const textarea = inputRef.current;
    if (!textarea) return;
    const position = sourcePositionAtPoint(event.clientX, event.clientY);
    if (!extend || pointerAnchorRef.current === null) pointerAnchorRef.current = position;
    const anchor = pointerAnchorRef.current;
    textarea.focus();
    textarea.setSelectionRange(Math.min(anchor, position), Math.max(anchor, position), position < anchor ? 'backward' : 'forward');
    syncCaret(textarea);
    onWorkspaceChange?.(textarea);
  };
  const pointerDown = (event) => {
    if (!hasExpandedLayout || event.button !== 0) return;
    event.preventDefault();
    pointerAnchorRef.current = null;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    selectAtPoint(event);
  };
  const pointerMove = (event) => {
    if (!hasExpandedLayout || pointerAnchorRef.current === null || !(event.buttons & 1)) return;
    event.preventDefault();
    selectAtPoint(event, true);
  };
  const pointerUp = (event) => {
    if (!hasExpandedLayout) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    pointerAnchorRef.current = null;
  };

  return <div ref={containerRef} className={`mc-continuous-canvas${hasExpandedLayout ? ' expanded-layout' : ''}`} role="region" aria-label="Formatted choreography score">
    <div ref={mirrorRef} className="mc-continuous-canvas-mirror" aria-hidden="true" onPointerDown={pointerDown} onPointerMove={pointerMove} onPointerUp={pointerUp}>
      {lines.map((line) => {
        const selectionStart = Math.max(0, Math.min(line.text.length, caret.selectionStart - line.sourceStart));
        const selectionEnd = Math.max(0, Math.min(line.text.length, caret.selectionEnd - line.sourceStart));
        const lineSelected = caret.focused && !caret.collapsed && caret.selectionEnd >= line.sourceStart && caret.selectionStart <= line.sourceStart + line.text.length;
        // MCRO001-F0065 (T-MCRO-065): variant-carrying lines wear their
        // group's colour (token tint + quiet row wash via CSS vars); hidden
        // lines keep their exact geometry (visibility:hidden, so the
        // transparent textarea, count anchors and rail geometry stay
        // aligned) while showing nothing — the per-dancer view is a filter
        // over the mirror rendering only, never over the collaborative text.
        const variantColour = line.variant !== undefined ? groupColor(line.variant) : null;
        const liveDisplayRows = Math.max(Number(line.rowSpan) || 1, Number(line.layoutRows) || 1);
        return <span
        key={line.sourceLine}
        className={`mc-live-line ${line.kind}${line.row % 2 === 0 ? ' shade' : ''}${line.provisional ? ' provisional' : ''}${liveDisplayRows > 1 ? ' spanning' : ''}${line.variant !== undefined ? ' variant' : ''}${line.hidden ? ' variant-hidden' : ''}`}
        data-source-line={line.sourceLine}
        data-row={line.row || undefined}
        data-duration={line.allocationStart ? line.durationLabel : undefined}
        data-row-span={line.rowSpan || undefined}
        data-layout-rows={line.layoutRows || undefined}
        style={{
          ...(liveDisplayRows > 1 ? { '--mc-live-span-rows': liveDisplayRows, ...(line.layoutRows > 1 ? { '--mc-live-layout-rows': line.layoutRows } : {}) } : {}),
          ...(variantColour ? { '--mc-variant-colour': variantColour, '--mc-variant-wash': groupSelectionColor(line.variant) } : {}),
        }}
      ><span className="mc-live-line-content">{highlightedCanvasLine(line, caret.focused && caret.collapsed && line.sourceLine === caret.sourceLine ? caret.offset : undefined, lineSelected ? selectionStart : undefined, lineSelected ? selectionEnd : undefined)}</span>{(line.countFragments || []).map((fragment, fragmentIndex) => <span
        key={`count-fragment-${fragment.countStart}-${fragment.countEnd}`}
        className="mc-live-count-fragment"
        data-source-line={line.sourceLine}
        data-count-start={fragment.countStart}
        data-count-end={fragment.countEnd}
        data-row={fragment.row}
        data-fragment-index={fragmentIndex}
        aria-hidden="true"
        style={{ top: `${fragmentIndex * 1.85}em` }}
      />)}{remoteCursors.filter((cursor) => cursor.sourceLine === line.sourceLine).map((cursor) => <RemoteCursorLayer
        key={`${cursor.clientInstanceId}:${cursor.sourceLine}:${cursor.offset}:${cursor.selectionEnd ?? -1}`}
        line={line}
        cursor={cursor}
      />)}{line.allocationStart && liveDisplayRows === 1 && <span className="mc-live-bar-chip" aria-hidden="true">{line.durationLabel}</span>}{line.allocationStart && liveDisplayRows > 1 && <span className="mc-live-span-bracket"><i className="top"></i><i className="stem"></i><i className="bottom"></i><b>{line.durationLabel}</b></span>}</span>;
      })}
    </div>
    <textarea
      ref={inputRef}
      className={`mc-blank-notes mc-continuous-canvas-input${sectionCaret || hasExpandedLayout ? ' section-caret-proxy' : ''}`}
      aria-label={ariaLabel}
      value={value}
      onChange={(event) => { syncCaret(event.currentTarget, globalThis.document?.activeElement === event.currentTarget); onChange(event); }}
      onPaste={onPaste}
      onCompositionStart={onCompositionStart}
      onCompositionEnd={onCompositionEnd}
      onFocus={onFocus}
      onBlur={(event) => syncCaret(event.currentTarget, false)}
      onSelect={(event) => { syncCaret(event.currentTarget); onWorkspaceChange?.(event.currentTarget); }}
      onKeyUp={(event) => { syncCaret(event.currentTarget); onWorkspaceChange?.(event.currentTarget); }}
      onKeyDown={(event) => { if (event.key === 'Tab') onWorkspaceChange?.(event.currentTarget); onKeyDown?.(event); }}
      onScroll={syncScroll}
      placeholder={placeholder}
      readOnly={readOnly}
      spellCheck="true"
    />
    {demonstrationRail && React.cloneElement(demonstrationRail, { geometryRootRef: mirrorRef })}
    {demonstrationRail && <GutterGlyphHalo mirrorRef={mirrorRef} containerRef={containerRef} lines={lines} />}
    {provisionalRows > 0 && <p className="mc-continuous-review" role="status">{provisionalRows} eight-count {provisionalRows === 1 ? 'row is' : 'rows are'} provisional; add [8] to confirm.</p>}
  </div>;
}

export function BlankRoutineComposer({ onClose, onCreate, onInterpret, saving, draftKey }) {
  const recovered = useMemo(() => readComposerDraft(draftKey), [draftKey]);
  const [title, setTitle] = useState(recovered?.title || '');
  const [notes, setNotes] = useState(recovered?.notes || '');
  const [reviewedCounts, setReviewedCounts] = useState(recovered?.reviewedCounts || {});
  const [sourceRevision, setSourceRevision] = useState(recovered?.sourceRevision || 0);
  const [showRecovered, setShowRecovered] = useState(Boolean(recovered));
  const [draftStatus, setDraftStatus] = useState(recovered ? 'Recovered a draft saved on this device.' : 'Draft is saved on this device as you write.');
  const [assisted, setAssisted] = useState({ status: 'idle', proposal: null, error: '' });
  const [pasteFormat, setPasteFormat] = useState(() => recovered?.formatCheckpoint && recovered.notes === recovered.formatCheckpoint.formattedSource
    ? { status: 'applied', message: 'Formatting applied. Your original paste is saved.', ...recovered.formatCheckpoint }
    : emptyPasteFormat());
  const notesRef = useRef(null);
  const notesValueRef = useRef(notes);
  const pasteIntentRef = useRef(null);
  const sourceRevisionRef = useRef(sourceRevision);
  const { snapshot: parsedSnapshot, analysis } = useMemo(() => analyseBlueprintNotes(notes), [notes]);
  const canvasScore = useMemo(() => projectChoreoCanvas(notes), [notes]);
  const hasCanvasScore = canvasScore.sections.some(({ allocations }) => allocations.length > 0);
  const hasProvisionalCanvasRows = canvasScore.sections.some(({ provisionalRows }) => provisionalRows > 0);
  const snapshot = useMemo(() => hasCanvasScore ? snapshotFromCanvasScore(canvasScore) : snapshotWithReviewedCounts(parsedSnapshot, analysis.sections, reviewedCounts), [analysis.sections, canvasScore, hasCanvasScore, parsedSnapshot, reviewedCounts]);
  const musicStructure = useMemo(() => describeDancerMusicStructure(snapshot.sections, analysis.sections), [snapshot.sections, analysis.sections]);
  const totalCounts = snapshot.sections.at(-1)?.endCount || 0;
  const canSubmit = !saving && Boolean(title.trim()) && snapshot.sections.length > 0;

  useEffect(() => {
    if (!title && !notes) {
      try { globalThis.localStorage?.removeItem(composerDraftStorageKey(draftKey)); } catch { /* Storage is optional. */ }
      return;
    }
    try {
      const formatCheckpoint = pasteFormat.status === 'applied'
        ? { beforeSource: pasteFormat.beforeSource, formattedSource: pasteFormat.formattedSource }
        : undefined;
      globalThis.localStorage?.setItem(composerDraftStorageKey(draftKey), JSON.stringify({ version: COMPOSER_DRAFT_VERSION, title, notes, reviewedCounts, sourceRevision, formatCheckpoint, updatedAt: new Date().toISOString() }));
      if (!showRecovered) setDraftStatus('Draft saved on this device.');
    } catch { setDraftStatus('Local draft saving is unavailable in this browser.'); }
  }, [draftKey, notes, pasteFormat, reviewedCounts, showRecovered, sourceRevision, title]);

  const sourceChanged = (setter) => (event) => {
    setter(event.target.value);
    setSourceRevision((current) => { sourceRevisionRef.current = current + 1; return current + 1; });
    setAssisted((current) => current.status === 'ready' || current.status === 'fallback' ? { ...current, status: 'stale' } : current);
  };

  const clearDraft = () => {
    try { globalThis.localStorage?.removeItem(composerDraftStorageKey(draftKey)); } catch { /* Storage is optional. */ }
    setTitle(''); setNotes(''); notesValueRef.current = ''; setReviewedCounts({}); setSourceRevision(0); sourceRevisionRef.current = 0; setShowRecovered(false); setAssisted({ status: 'idle', proposal: null, error: '' }); setPasteFormat(emptyPasteFormat()); setDraftStatus('Local draft cleared.');
  };

  const formatPastedSource = async (sourceText, requestedRevision) => {
    if (!onInterpret) return;
    setPasteFormat({ status: 'reading', message: 'Choreo is reading your pasted notes…', beforeSource: sourceText, formattedSource: '' });
    try {
      const sourceHash = await sourceDigest(sourceText);
      if (sourceRevisionRef.current !== requestedRevision || notesValueRef.current !== sourceText) {
        setPasteFormat({ status: 'unchanged', message: 'Paste changed while Choreo was reading it. No formatting was applied.', beforeSource: sourceText, formattedSource: '' });
        return;
      }
      const request = {
        mode: 'format_source',
        title: title.trim() || 'Untitled routine',
        sourceText,
        sourceRevision: requestedRevision,
      };
      if (/^[a-f0-9]{64}$/.test(sourceHash)) request.sourceHash = sourceHash;
      const result = await onInterpret(request);
      const formattedText = result?.formattedSourceText ?? result?.formattedText ?? result?.proposal?.formattedSourceText ?? result?.proposal?.formattedText;
      const returnedRevision = result?.sourceRevision ?? requestedRevision;
      const returnedHash = result?.hashes?.original ?? result?.sourceHash ?? sourceHash;
      const hashMatches = sourceHash.startsWith('fallback-') || returnedHash === sourceHash;
      if (sourceRevisionRef.current !== requestedRevision || notesValueRef.current !== sourceText || returnedRevision !== requestedRevision || !hashMatches) {
        setPasteFormat({ status: 'unchanged', message: 'Paste changed while Choreo was reading it. No formatting was applied.', beforeSource: sourceText, formattedSource: '' });
        return;
      }
      if (result?.status === 'source_unchanged' || typeof formattedText !== 'string' || formattedText === sourceText) {
        setPasteFormat({ status: 'unchanged', message: 'This paste is already structured. No changes were made.', beforeSource: sourceText, formattedSource: '' });
        return;
      }
      const caretMap = insertionOnlyCaretMap(sourceText, formattedText);
      if (!caretMap) throw new Error('The formatting response tried to replace or remove source text.');
      const textarea = notesRef.current;
      const selectionStart = textarea?.selectionStart ?? sourceText.length;
      const selectionEnd = textarea?.selectionEnd ?? selectionStart;
      const scrollTop = textarea?.scrollTop ?? 0;
      const restoreFocus = globalThis.document?.activeElement === textarea;
      notesValueRef.current = formattedText;
      setNotes(formattedText);
      setSourceRevision((current) => { sourceRevisionRef.current = current + 1; return current + 1; });
      setPasteFormat({ status: 'applied', message: 'Formatting applied. Your original paste is saved.', beforeSource: sourceText, formattedSource: formattedText });
      const restore = () => {
        if (!textarea) return;
        if (restoreFocus) textarea.focus();
        textarea.setSelectionRange(caretMap[Math.min(selectionStart, sourceText.length)], caretMap[Math.min(selectionEnd, sourceText.length)]);
        textarea.scrollTop = scrollTop;
      };
      if (typeof globalThis.requestAnimationFrame === 'function') globalThis.requestAnimationFrame(restore);
      else globalThis.setTimeout?.(restore, 0);
    } catch (cause) {
      if (sourceRevisionRef.current === requestedRevision && notesValueRef.current === sourceText) {
        setPasteFormat({ status: 'error', message: `Choreo could not format this paste. Your original is unchanged. ${message(cause)}`, beforeSource: sourceText, formattedSource: '' });
      }
    }
  };

  const notesChanged = (event) => {
    const next = event.target.value;
    notesValueRef.current = next;
    setNotes(next);
    const nextRevision = sourceRevisionRef.current + 1;
    sourceRevisionRef.current = nextRevision;
    setSourceRevision(nextRevision);
    setAssisted((current) => current.status === 'ready' || current.status === 'fallback' ? { ...current, status: 'stale' } : current);
    const pasteIntent = pasteIntentRef.current;
    pasteIntentRef.current = null;
    if (!pasteIntent || !isLargePaste(pasteIntent.text)) {
      if (pasteFormat.status === 'applied') setPasteFormat(emptyPasteFormat());
      return;
    }
    if (isAlreadyStructuredPaste(pasteIntent.text)) {
      setPasteFormat({ status: 'unchanged', message: 'This paste is already structured. No changes were made.', beforeSource: next, formattedSource: '' });
      return;
    }
    formatPastedSource(next, nextRevision);
  };

  const pasted = (event) => {
    pasteIntentRef.current = { text: event.clipboardData?.getData('text/plain') || '' };
  };

  const undoPasteFormatting = () => {
    if (pasteFormat.status !== 'applied' || notesValueRef.current !== pasteFormat.formattedSource) return;
    const original = pasteFormat.beforeSource;
    notesValueRef.current = original;
    setNotes(original);
    setSourceRevision((current) => { sourceRevisionRef.current = current + 1; return current + 1; });
    setPasteFormat({ status: 'unchanged', message: 'Original paste restored.', beforeSource: original, formattedSource: '' });
    const focus = () => { notesRef.current?.focus(); notesRef.current?.setSelectionRange(original.length, original.length); };
    if (typeof globalThis.requestAnimationFrame === 'function') globalThis.requestAnimationFrame(focus);
    else globalThis.setTimeout?.(focus, 0);
  };

  const focusSourceLine = (line) => {
    const focus = () => {
      const textarea = notesRef.current;
      if (!textarea) return;
      const rows = notes.split(/\r?\n/);
      const start = rows.slice(0, Math.max(0, line - 1)).reduce((length, row) => length + row.length + 1, 0);
      textarea.focus();
      textarea.setSelectionRange(start, start + (rows[line - 1]?.length || 0));
    };
    if (typeof globalThis.requestAnimationFrame === 'function') globalThis.requestAnimationFrame(focus);
    else globalThis.setTimeout?.(focus, 0);
  };

  const requestAssistance = async () => {
    if (!onInterpret || !notes.trim()) return;
    const requestedRevision = sourceRevision;
    setAssisted({ status: 'loading', proposal: null, error: '' });
    try {
      const proposal = await onInterpret({ title: title.trim(), sourceText: notes, sourceRevision: requestedRevision });
      const returnedRevision = proposal?.sourceRevision ?? requestedRevision;
      if (returnedRevision !== sourceRevisionRef.current) setAssisted({ status: 'stale', proposal, error: '' });
      else setAssisted({ status: proposal?.fallbackUsed || proposal?.fallback || proposal?.status === 'fallback' ? 'fallback' : 'ready', proposal, error: '' });
    } catch (cause) {
      setAssisted({ status: 'error', proposal: null, error: message(cause) });
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    const sourceDocument = { schemaVersion: 1, title, body: notes, sourceRevision };
    // MCRO001-F0164 / T-MCRO-165 (WebUI issue #58): no shareScope here — the
    // create body must stay scope-free so choreo's own create default applies
    // (shareScope(input.shareScope, 'private'), MCRO001-F0161 /
    // MCRO001-D021 §5.5, choreography.service.ts:832). Owner ruling,
    // verbatim: "I must be able to see all access by any path and I must be
    // able to control what rights each access path gives." Hard-coding
    // 'organisation' here silently widened every new canvas.
    const created = await onCreate({ title: title.trim(), trackTitle: title.trim(), artist: '', durationMs: 180000, bpm: undefined, firstCountOffsetMs: undefined, tapTimesMs: [], audioFile: null, snapshot: { ...snapshot, sourceDocument } });
    if (created !== false) try { globalThis.localStorage?.removeItem(composerDraftStorageKey(draftKey)); } catch { /* Storage is optional. */ }
  };

  const onKeyDown = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && canSubmit) { event.preventDefault(); event.currentTarget.requestSubmit(); }
  };

  const proposalQuestions = assisted.proposal?.unresolved || assisted.proposal?.questions || [];
  const proposalSections = assisted.proposal?.proposal?.sections || [];
  return <section className="mc-blank-workspace" aria-label="New choreography routine">
    <header className="mc-blank-toolbar"><button type="button" className="mc-quiet" onClick={onClose}>← Back to routines</button><span className="mc-kicker">Choreo draft</span><button type="submit" form="mc-blank-form" className="mc-primary" disabled={!canSubmit} aria-keyshortcuts="Control+Enter Meta+Enter">{saving ? 'Creating…' : 'Create structure'}</button></header>
    {showRecovered && <div className="mc-draft-recovered" role="status"><span><strong>Draft recovered.</strong> Your writing stayed on this device.</span><button type="button" className="mc-text-button" onClick={clearDraft}>Discard recovered draft</button></div>}
    <form id="mc-blank-form" className={`mc-composer ${hasCanvasScore ? 'mc-composer-canvas' : ''}`} onSubmit={submit} onKeyDown={onKeyDown}>
      <div className={`mc-blank-page ${hasCanvasScore ? 'mc-blank-page-canvas' : ''}`}>
        <label className="mc-visually-hidden" htmlFor="mc-blank-heading">Routine title</label><input id="mc-blank-heading" className="mc-blank-title" aria-label="Routine title" value={title} onChange={sourceChanged(setTitle)} placeholder="Untitled routine" autoFocus />
        <label className="mc-visually-hidden" htmlFor="mc-blank-notes">Write choreography</label><ContinuousCanvasEditor inputRef={notesRef} value={notes} score={canvasScore} onChange={notesChanged} onPaste={pasted} ariaLabel="Describe the song structure" placeholder={'Start typing what you hear…\n\n#a\n[8] Walk out slowly\n[8] Hit the pose'} />
        {pasteFormat.status !== 'idle' && <div className={`mc-paste-format ${pasteFormat.status}`} role="status" aria-live="polite"><span>{pasteFormat.status === 'reading' ? '✦' : pasteFormat.status === 'error' ? '!' : '✓'}</span><strong>{pasteFormat.message}</strong>{pasteFormat.status === 'applied' && <button type="button" onClick={undoPasteFormatting}>Undo formatting</button>}</div>}
        <p id="mc-writing-help" className="mc-writing-help">Use ordinary headings and movement notes. Your exact writing stays here; Choreo only proposes structure beside it.</p>
        <footer className="mc-choreo-assist"><span aria-hidden="true">✦</span><div><p role="status" aria-live="polite" aria-atomic="true">{snapshot.sections.length > 0 ? <>Choreo sees <strong>{snapshot.sections.length} {snapshot.sections.length === 1 ? 'part' : 'parts'}</strong> across <strong>{totalCounts} counts</strong>. {analysis.provisional || hasProvisionalCanvasRows ? 'Some counts need review.' : 'All counts were written explicitly.'}</> : <>Paste or type naturally. Choreo recognises phrase and section headings, then keeps the movement underneath each one.</>}</p><small>{draftStatus}</small></div></footer>
      </div>
      {!hasCanvasScore && <aside className="mc-interpretation" aria-labelledby="mc-interpretation-title">
        <header><div><span className="mc-kicker">Live interpretation</span><h2 id="mc-interpretation-title">Structure to review</h2></div>{analysis.sectionsDetected > 0 && <span className={`mc-confidence ${analysis.provisional ? 'provisional' : 'explicit'}`}>{analysis.provisional ? 'Provisional' : 'Explicit'}</span>}</header>
        {analysis.sectionsDetected === 0 ? <div className="mc-interpretation-empty"><strong>Your outline will appear here.</strong><p>Try “PHRASE 1 — melody A” or “Intro — 8 counts”. Movement below a heading remains attached as notes.</p></div> : <>
          {analysis.preamble.length > 0 && <div className="mc-context-note"><strong>Page context kept outside the timed canvas</strong><p>{analysis.preamble.join(' ')}</p></div>}
          <section className="mc-music-lens" aria-labelledby="mc-music-lens-title">
            <div className="mc-music-lens-head"><div><span className="mc-kicker">Dancer music map</span><h3 id="mc-music-lens-title">Counts → bars → form</h3></div><span className="mc-meter-chip">4/4 lens</span></div>
            <div className="mc-music-totals"><strong>{musicStructure.totalCounts} counts</strong><span>{musicStructure.fullEightCountBlocks} full × 8{musicStructure.trailingCounts ? ` + ${musicStructure.trailingCounts}` : ''}</span>{musicStructure.musicalBars !== null && <span>{musicStructure.musicalBars} musical bars</span>}</div>
            <div className="mc-music-equation" aria-label="One dancer eight-count is two musical bars; four eight-counts make one eight-bar form section; four form sections make one 32-bar form cycle"><span><b>1 × 8</b><small>2 bars</small></span><i>→</i><span><b>4 × 8</b><small>8-bar section</small></span><i>→</i><span><b>4 sections</b><small>32-bar cycle</small></span></div>
            {musicStructure.formCycles.length > 0 ? <ol className="mc-form-cycles">{musicStructure.formCycles.map((cycle) => <li key={cycle.id}><div><strong>{cycle.label}</strong><span>{cycle.eightCountBlocks} × 8 · {cycle.musicalBars} bars · counts {cycle.startCount}–{cycle.endCount}</span></div><em>{cycle.certainty}</em></li>)}</ol> : <p className="mc-form-awaiting">Four adjacent 8-bar sections will appear here as a possible 32-bar form cycle.</p>}
            <p className="mc-form-note"><strong>A/B form and Verse/Chorus are separate.</strong> In jazz, a chorus can mean one complete pass through the form; song roles are only shown when your text names them.</p>
          </section>
          <ol className="mc-structure-list">{analysis.sections.map((section, sectionIndex) => { const key = reviewKey(section); const reviewed = Number.isInteger(reviewedCounts[key]); const phrases = reviewedCounts[key] || section.countLength / 8; const musicSection = musicStructure.sections[sectionIndex]; return <li key={key} className={section.inferred ? 'inferred' : ''}>
            <div className="mc-structure-card-head"><button type="button" className="mc-source-link" onClick={() => focusSourceLine(section.sourceLine)} title={`Jump to source line ${section.sourceLine}`}>{section.label}</button><span className={`mc-state ${section.provisional ? reviewed ? 'reviewed' : 'provisional' : 'explicit'}`}>{section.provisional ? reviewed ? 'Reviewed' : section.inferred ? 'Inferred' : 'Provisional' : 'Explicit'}</span></div>
            <p>{section.movementLines} movement {section.movementLines === 1 ? 'line' : 'lines'} · source line {section.sourceLine}</p>
            {musicSection && <div className="mc-musical-identity"><span>{musicSection.eightCountBlocks !== null ? `${musicSection.eightCountBlocks} × 8-count${musicSection.eightCountBlocks === 1 ? '' : 's'}` : `${musicSection.countLength}-count exception`}</span>{musicSection.musicalBars !== null && <span>{musicSection.musicalBars} musical {musicSection.musicalBars === 1 ? 'bar' : 'bars'}</span>}{musicSection.isEightBarFormSection && <span>8-bar form section</span>}</div>}
            {musicSection && (musicSection.formLetter || musicSection.songRole) && <div className="mc-musical-roles">{musicSection.formLetter && <span>Form {musicSection.formLetter}</span>}{musicSection.songRole && <span>Song role · {musicSection.songRole}</span>}</div>}
            {section.barLengths?.length > 0 && <div className="mc-bar-signature" aria-label={`${section.label} explicit bars: ${section.barLengths.join(', ')} counts`}>{section.barLengths.map((length, index) => <span key={`${length}-${index}`} className={length === 6 ? 'blue-six' : ''}>{length === 6 ? 'Blue 6' : length}</span>)}</div>}
            {section.provisional ? <label className="mc-count-review"><span>Length to use</span><span><input aria-label={`${section.label} 8-count phrases`} type="number" min="1" max="64" value={phrases} onChange={(event) => { const value = Number(event.target.value); if (Number.isInteger(value) && value >= 1 && value <= 64) setReviewedCounts((current) => ({ ...current, [key]: value })); }} /> × 8 counts</span></label> : <strong className="mc-explicit-count">{section.countLength} counts from your text</strong>}
            {section.inferred && <p className="mc-inference-note">This leading section was inferred from movement before the first numbered heading. Check its label and length.</p>}
          </li>; })}</ol>
          {analysis.unresolved.some(({ code }) => code !== 'COUNT_LENGTH_PROVISIONAL') && <section className="mc-review-notes" aria-labelledby="mc-review-title"><h3 id="mc-review-title">Questions before rehearsal</h3><ul>{analysis.unresolved.filter(({ code }) => code !== 'COUNT_LENGTH_PROVISIONAL').map((item) => <li key={`${item.code}-${item.line}`}>{item.message}</li>)}</ul></section>}
        </>}
        {onInterpret && <section className={`mc-assisted-state ${assisted.status}`} aria-labelledby="mc-assisted-title"><div><h3 id="mc-assisted-title">Assisted interpretation</h3><p>{assisted.status === 'idle' && 'Ask the server-side assistant to examine ambiguous movement. Your current structure will not be replaced.'}{assisted.status === 'loading' && 'Choreo is preparing a separate proposal…'}{assisted.status === 'ready' && 'A model proposal is ready for review. It has not changed your structure.'}{assisted.status === 'fallback' && 'The preferred model was unavailable, so the configured fallback model prepared this review-only proposal.'}{assisted.status === 'stale' && 'This proposal is stale because the source changed. Request a fresh interpretation before using it.'}{assisted.status === 'error' && `Assisted interpretation failed: ${assisted.error}. Your baseline structure is unchanged.`}</p></div><button type="button" className="mc-quiet" onClick={requestAssistance} disabled={assisted.status === 'loading' || !notes.trim()}>{assisted.status === 'loading' ? 'Interpreting…' : assisted.status === 'stale' ? 'Refresh proposal' : 'Ask Choreo AI'}</button>{proposalSections.length > 0 && <div className="mc-proposal-outline"><strong>Review-only proposal{assisted.proposal?.model ? ` · ${assisted.proposal.model}` : ''}</strong><small>Compared with the {analysis.sectionsDetected}-part baseline; nothing below is applied automatically.</small><ol>{proposalSections.slice(0, 12).map((section, index) => { const baseline = analysis.sections[index]; const proposedLength = Number(section.countLength) || (Number(section.endCount) - Number(section.startCount) + 1) || null; const differs = !baseline || section.label !== baseline.label || (proposedLength && proposedLength !== baseline.countLength); return <li key={section.id || `${section.label}-${index}`}><span>{section.label || `Part ${index + 1}`}{proposedLength ? ` · ${proposedLength} counts` : ' · counts unresolved'}</span><em>{differs ? 'Suggestion differs' : 'Matches baseline'}</em></li>; })}</ol>{proposalSections.length > 12 && <small>+{proposalSections.length - 12} more proposed parts</small>}</div>}{proposalQuestions.length > 0 && <div className="mc-proposal-questions"><strong>Questions for you</strong><ul>{proposalQuestions.map((question, index) => <li key={question.code || question.id || index}><span>{question.prompt || question.message || question.text || 'Review this interpretation.'}</span>{question.reason && <small>{question.reason}</small>}</li>)}</ul></div>}</section>}
        <p className="mc-human-note"><strong>Nothing is published from this page.</strong> Create structure opens an editable draft blueprint for another review.</p>
      </aside>}
      {hasCanvasScore && <span className="mc-ai-structure-lens" aria-hidden="true"><span>AI Structure</span><b>‹</b></span>}
    </form>
  </section>;
}

export function sourceFromSnapshot(snapshot) {
  const exactSource = snapshot.sourceDocument?.body;
  if (typeof exactSource === 'string' && exactSource.trim()) return exactSource;
  return (snapshot.sections || []).map((section) => {
    const blocks = (snapshot.blocks || [])
      .filter((block) => block.sectionId === section.id || (Number(block.startCount) >= Number(section.startCount) && Number(block.startCount) <= Number(section.endCount)))
      .sort((a, b) => Number(a.startCount) - Number(b.startCount));
    const notes = String(section.practiceNote || '').split(/\r?\n/).filter(Boolean);
    return [`# ${section.label}`, ...blocks.map((block, index) => {
      const countLength = Math.max(1, Number(block.countLength) || 8);
      const token = countLength > 8 && countLength % 8 === 0 ? `[${countLength / 8}×8]` : `[${countLength}]`;
      return `${token}${notes[index] ? ` ${notes[index]}` : ''}`;
    })].join('\n');
  }).join('\n\n');
}

export function scoreFromSnapshot(snapshot) {
  return {
    schemaVersion: 1,
    sourceText: snapshot.sourceDocument?.body || '',
    sections: (snapshot.sections || []).map((section, sectionIndex) => {
      const blocks = (snapshot.blocks || [])
        .filter((block) => block.sectionId === section.id || (Number(block.startCount) >= Number(section.startCount) && Number(block.startCount) <= Number(section.endCount)))
        .sort((a, b) => Number(a.startCount) - Number(b.startCount));
      const notes = String(section.practiceNote || '').split(/\r?\n/).filter(Boolean);
      const sectionStartCount = Number(section.startCount) || 1;
      const allocations = blocks.map((block, blockIndex) => {
        const countLength = Math.max(1, Number(block.countLength) || 8);
        const rowSpan = Math.max(1, Math.ceil(countLength / 8));
        const countStart = Number(block.startCount) || sectionStartCount;
        const fragments = allocationFragmentsFor(countStart, countLength, sectionStartCount);
        const allocation = {
          id: `snapshot-${section.id}-allocation-${blockIndex + 1}`,
          countLength,
          rowSpan,
          durationLabel: countLength > 8 && countLength % 8 === 0 ? `[${countLength / 8}×8]` : `[${countLength}]`,
          countStart,
          countEnd: countStart + countLength - 1,
          fragments,
          startRow: fragments[0]?.row || 1,
          endRow: fragments.at(-1)?.row || fragments[0]?.row || 1,
          displayRowSpan: fragments.length,
          sourceLine: 1,
          sequenceTitle: null,
          sequenceSourceLine: null,
          movementLines: notes[blockIndex] ? [{ text: notes[blockIndex], sourceLine: 1 }] : [],
        };
        return allocation;
      });
      const allocatedCountLength = blocks.reduce((total, block) => total + Math.max(1, Number(block.countLength) || 8), 0);
      const allocatedRows = Math.ceil(allocatedCountLength / 8);
      const rows = Math.max(4, Math.ceil((Number(section.endCount) - sectionStartCount + 1) / 8));
      const rowCounts = Array.from({ length: rows }, () => 8);
      allocations.forEach((allocation) => {
        allocation.fragments.forEach((fragment) => {
          rowCounts[fragment.row - 1] = Math.max(rowCounts[fragment.row - 1] || 0, fragment.endCell);
        });
      });
      return {
        id: `snapshot-canvas-section-${sectionIndex + 1}`,
        label: section.label,
        sourceLine: 1,
        countStart: sectionStartCount,
        countEnd: Number(section.endCount),
        allocatedCountLength,
        allocations,
        allocatedRows,
        rows,
        rowCounts,
        state: allocatedCountLength === 32 ? 'explicit' : allocatedCountLength < 32 ? 'incomplete' : 'overflow',
      };
    }),
  };
}

// D001 §5.1 selection toolbar on the writing surface when the community review
// layer does not own the shared toolbar (draft-only revision, or Review=Hide,
// which removes every comment affordance without removing authoring actions).
export function CanvasStructuralToolbar({ sourceRef, source, actions = [], commentsSuppressed = false }) {
  const [prompt, setPrompt] = useState(null);
  const toolbarRef = useRef(null);
  const suppressCaptureRef = useRef(false);
  const commentsSuppressedRef = useRef(commentsSuppressed);
  useEffect(() => { commentsSuppressedRef.current = commentsSuppressed; }, [commentsSuppressed]);
  // MCRO001-F0060/T-MCRO-060: same rail edit-mode suppression as
  // CommunityReviewLayer's "Add comment" toolbar (owner ruling 2026-08-26) —
  // this toolbar's structural actions (make section, propose counts, Ask
  // Choreo) are a selection-triggered popup of the same shape and must fold
  // away for the same reason while the rail is being used.
  useEffect(() => { if (commentsSuppressed) setPrompt(null); }, [commentsSuppressed]);
  const enabled = actions.length > 0;
  useEffect(() => {
    const target = sourceRef?.current;
    if (!target?.addEventListener || !enabled) {
      setPrompt(null);
      return undefined;
    }
    const updateSelection = () => {
      // Escape restores the selection programmatically; browsers re-fire a
      // `select` event for that restore, which must not reopen the toolbar.
      if (suppressCaptureRef.current) {
        suppressCaptureRef.current = false;
        return;
      }
      // MCRO001-F0060: commentsSuppressedRef is the React-idiomatic prop-fed
      // signal; the dataset read is the same signal with zero render-cycle
      // latency (DemonstrationTimingRail.js sets it directly) — see
      // CommunityReview.js's own updateSelection for why that gap matters.
      if (commentsSuppressedRef.current || globalThis.document?.body?.dataset?.mcRailEditModeActive === '1') {
        if (target.selectionStart !== target.selectionEnd) {
          suppressCaptureRef.current = true;
          target.setSelectionRange(target.selectionStart, target.selectionStart);
        }
        return;
      }
      const start = Number(target.selectionStart);
      const end = Number(target.selectionEnd);
      if (!Number.isInteger(start) || !Number.isInteger(end) || start === end) {
        setPrompt(null);
        return;
      }
      setPrompt({ start, end, quote: String(source || '').slice(start, end), position: selectionToolbarPosition(target, source, end) });
    };
    const events = ['select', 'mouseup', 'pointerup', 'keyup', 'touchend'];
    events.forEach((name) => target.addEventListener(name, updateSelection));
    return () => events.forEach((name) => target.removeEventListener(name, updateSelection));
  }, [enabled, source, sourceRef]);
  useEffect(() => {
    if (!prompt) return undefined;
    const dismiss = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      const target = sourceRef?.current;
      suppressCaptureRef.current = true;
      target?.focus?.();
      target?.setSelectionRange?.(prompt.start, prompt.end);
      setPrompt(null);
    };
    const clickAway = (event) => {
      if (toolbarRef.current?.contains(event.target) || event.target === sourceRef?.current) return;
      setPrompt(null);
    };
    globalThis.document?.addEventListener('keydown', dismiss);
    globalThis.document?.addEventListener('pointerdown', clickAway);
    return () => {
      globalThis.document?.removeEventListener('keydown', dismiss);
      globalThis.document?.removeEventListener('pointerdown', clickAway);
    };
  }, [prompt, sourceRef]);
  if (!prompt || !enabled) return null;
  return <SelectionToolbar toolbarRef={toolbarRef} position={prompt.position} quote={prompt.quote} onDismiss={() => { sourceRef?.current?.focus?.(); setPrompt(null); }}>
    {actions.map((action) => <button
      type="button"
      key={action.id}
      className={`mc-selection-structural${action.disabled ? ' unavailable' : ''}`}
      disabled={action.disabled === true}
      title={action.description}
      onClick={() => {
        const range = { sourceStart: prompt.start, sourceEnd: prompt.end, exactQuote: prompt.quote };
        setPrompt(null);
        action.run(range);
      }}
    >{action.label}</button>)}
  </SelectionToolbar>;
}

// Owner-approved original filler: deliberately meaningless movement phrases
// that only demonstrate the canvas grammar (never real lyrics).
const CANVAS_PLACEHOLDER = 'Start typing what you hear…\n\n# Section A\n[8] Step left, clap twice, hold the turn\n[8] Jump back, side slide, snap and sway';

const canvasWorkspaceMemory = new Map();

// Read-only exactly as the engine reports it: while the collaboration phase is
// loading/admitting/readonly/recovery/resyncing/reconnecting the whole editor
// renders disabled, just as the old readOnly prop did.
const CANVAS_READONLY_PHASES = ['loading', 'admitting', 'readonly', 'recovery', 'resyncing', 'reconnecting'];

// MCRO001-F0061 / T-MCRO-061 (MCRO001-D004 §10.3 (a)): the EXACT saved source
// the person last wrote, straight off the routine snapshot the parent already
// holds in React state. This is the only text the canvas may show before the
// live collaboration document arrives.
//
// Deliberately NOT reconstructed from `sections`/`blocks` when
// `sourceDocument.body` is missing: a snapshot-derived reconstruction is a
// DIFFERENT document from the one that was saved, and inventing it would put
// text on screen the person never wrote. A legacy sourceless snapshot therefore
// behaves exactly as it did before F0061 — the visible gap is honest, and the
// snapshot still drives the score projection (`scoreFromSnapshot`) as before.
export function canvasSourceFromSnapshot(snapshot) {
  const body = snapshot?.sourceDocument?.body;
  return typeof body === 'string' ? body : '';
}

export function RoutineCanvasEditor({ collaboration, snapshot, canEdit, orgId, routineId, revisionId, displayedRevisionId, capabilities = {}, reviewDisplay = 'inline', onChangeReviewDisplay = null, routineTitle = '', registerWorkspaceCapture, demonstrationBoundary = controlledMappedDemonstrationEvidenceBoundary(), demonstrationPlaybackContext = null, viewingAs = '', onChangeViewingAs = null, onPublish = null, publishBusy = false, onRevisionSaved = null }) {
  const documentLines = useMemo(() => orderedLines(collaboration.document), [collaboration.document]);
  const projectedSource = useMemo(() => documentLines.map((line) => line.text).join('\n'), [documentLines]);
  const [compositionDraft, setCompositionDraft] = useState(null);
  const compositionRef = useRef(null);
  const [announcement, setAnnouncement] = useState('');
  // MCRO001-F0060/T-MCRO-060 (owner ruling 2026-08-26): hovering, focusing or
  // dragging the demonstration timing rail is "edit mode" — the rail may
  // expand past the gutter AND the canvas's comment/structural-selection
  // popups must be unable to open for the same duration, folding back
  // together. DemonstrationTimingRail is the sole source of truth for this
  // (onEditModeChange); CommunityReviewLayer/CanvasStructuralToolbar are the
  // sole consumers (commentsSuppressed) — lifted here because the rail and
  // the comment surface are DOM siblings with no other shared state.
  const [railEditModeActive, setRailEditModeActive] = useState(false);
  // MCRO001-F0061 / T-MCRO-061 (MCRO001-D004 §10.3): the Canvas tab is
  // conditionally rendered, so leaving it UNMOUNTS this editor and returning
  // REMOUNTS it. Until F0061 the visible text came exclusively from
  // `collaboration.document`, which is null until the `joined-draft` frame
  // arrives — so every remount showed a genuinely EMPTY textarea for the whole
  // duration of socket teardown, new socket, auth callback, namespace
  // handshake, join, server live-draft materialisation and joined-draft. That
  // window is serialised round trips, not bytes: the largest sheet snapshot is
  // 2.1 KB and a rejoin replays nothing (MAX(server_sequence) = 0). The saved
  // text was in memory on the parent the whole time (its load effect keys on
  // [orgId, routineId], not on the tab) and simply was not drawn.
  //
  // `documentLive` is the single authority for which of the two sources is on
  // screen, and it is deliberately the document's PRESENCE, not a phase name:
  // the phase list below describes admission progress, whereas the only thing
  // that makes text safe to edit is having the authoritative document to
  // express the edit against.
  const documentLive = Boolean(collaboration.document);
  const snapshotSource = useMemo(() => canvasSourceFromSnapshot(snapshot), [snapshot]);
  // §10.3 (c): the live document is authoritative the INSTANT it arrives, even
  // when it differs from the snapshot (someone else may have edited since the
  // snapshot was fetched). Straight replacement — never a merge, never a diff,
  // never a preference for what is already on screen. There is nothing to
  // merge by construction, because (b) below forbids any local edit reaching
  // this text before the document exists.
  const source = compositionDraft ?? (documentLive ? projectedSource : snapshotSource);
  // A stored empty string is a real saved source; a missing source is not.
  const showingSavedVersion = !documentLive && typeof snapshot?.sourceDocument?.body === 'string';
  const explicitScore = useMemo(() => projectChoreoCanvas(source), [source]);
  const fallbackScore = useMemo(() => scoreFromSnapshot(snapshot), [snapshot]);
  // Any authored section boundary makes the live source authoritative for the
  // visual score (T-MCRO-012 case F: a lone `#` already formats live); the
  // snapshot-derived projection remains only for sourceless legacy snapshots.
  const hasExplicitScore = explicitScore.sections.length > 0;
  const score = hasExplicitScore ? explicitScore : fallbackScore;
  // MCRO001-F0064 (T-MCRO-064): the shared rehearsal/playback count is the
  // canvas's "current count context" for the formation stages; it defaults to
  // 1, where every section holds its first anchor.
  const canvasFormationCount = Number(demonstrationPlaybackContext?.count) || 1;
  const formationSections = score.sections.filter((section) => (section.formations || []).length > 0);
  // MCRO001-F0065 (T-MCRO-065): the "viewing as" cast roster is every number
  // the sheet's groups name, and the view map is the mirror filter for the
  // selected dancer ('' = full team: nothing hidden, variants colour-tagged).
  const castNumbers = [...new Set((score.groups || []).flatMap((group) => group.numbers || []))].sort((a, b) => a - b);
  const viewingAsNumber = viewingAs !== '' && Number.isSafeInteger(Number(viewingAs)) && Number(viewingAs) >= 1 ? Number(viewingAs) : null;
  const viewingAsView = useMemo(() => (castNumbers.length > 0 ? variantLineView(score, viewingAsNumber) : null), [score, castNumbers.length, viewingAsNumber]);
  const sourceRef = useRef(null);
  // MCRO001-F0061 / T-MCRO-061 (MCRO001-D004 §10.3 (b)): a STRICT TIGHTENING of
  // the phase rule, never a relaxation. Showing saved text the person cannot
  // yet edit is a small, visible, honest cost; accepting a keystroke that goes
  // nowhere — or that later conflicts — is not. `!documentLive` covers 'idle'
  // (the frame before bootstrap) and any future phase that could reach here
  // without an authoritative document.
  const readOnly = CANVAS_READONLY_PHASES.includes(collaboration.phase) || !documentLive;
  const saving = collaboration.phase === 'saving' || collaboration.pendingCount > 0;
  // Demonstration timing maps live behind their own authority seam (candidate
  // canEditTiming + capability), exactly as before: they are not gated by the
  // live-editing admission phase that guards the source document.
  const railCanEdit = canEdit === undefined ? !readOnly : canEdit === true;
  const workspaceKey = `${String(orgId || '')}:${String(routineId || '')}`;
  const demonstrationCandidates = orderedDemonstrations(readApprovedMappedDemonstrations(demonstrationBoundary, {
    organisationId: orgId,
    routineId,
    revisionId: displayedRevisionId,
  }));
  // MCRO001-F0085 / T-MCRO-085: served records whose timing map failed hard
  // validation are honestly invalid, not absent — handed to the rail so each
  // renders as an error-state line instead of a silent drop (choreo #39).
  const demonstrationErrors = readServedInvalidDemonstrations(demonstrationBoundary, {
    organisationId: orgId,
    routineId,
    revisionId: displayedRevisionId,
  });
  // Companion slice to F0085 (choreo #46): the StageInformationPanel describes
  // what is mapped from the SAME reads the rail consumes — no refetch. A
  // boundary without readDemonstrationsLoadState (the E2E-injected double) is
  // treated as settled: its list is synchronous, so its [] IS settled truth.
  const demonstrationLoadState = typeof demonstrationBoundary?.readDemonstrationsLoadState === 'function'
    ? demonstrationBoundary.readDemonstrationsLoadState({
      organisationId: orgId,
      routineId,
      revisionId: displayedRevisionId,
    })
    : { settled: true, failed: false };
  const demonstrationBars = rehearsalBarOptions(snapshot, displayedRevisionId);
  const hasPlaybackContext = Number.isFinite(Number(demonstrationPlaybackContext?.count))
    && Number.isFinite(Number(demonstrationPlaybackContext?.bpm))
    && Number.isFinite(Number(demonstrationPlaybackContext?.rehearsalRate));
  const focusOnIds = demonstrationCandidates
    .filter((candidate) => hasPlaybackContext
      && focusOnClassification(candidate, demonstrationCandidates, {
        ...demonstrationPlaybackContext,
        revisionId: displayedRevisionId,
        // MCRO001-F0029: demonstrationPriority.js's focusOnClassification
        // reads context.focusOnRateThreshold (falling back to its own
        // DEFAULT_FOCUS_ON_RATE_THRESHOLD = 1.3 when absent/non-finite/<=0);
        // see capabilities.focusOnRateThreshold above for where this value
        // is sourced from the served capabilities payload.
        focusOnRateThreshold: capabilities.focusOnRateThreshold,
      }).focusOn)
    .map(({ demonstrationId }) => demonstrationId);
  const rememberWorkspace = useCallback((textarea = sourceRef.current) => {
    const previous = canvasWorkspaceMemory.get(workspaceKey) || {};
    const isActiveEditor = textarea && globalThis.document?.activeElement === textarea;
    canvasWorkspaceMemory.set(workspaceKey, {
      ...previous,
      ...(isActiveEditor ? {
        selectionStart: textarea.selectionStart,
        selectionEnd: textarea.selectionEnd,
        selectionDirection: textarea.selectionDirection,
        scrollTop: textarea.scrollTop,
        scrollLeft: textarea.scrollLeft,
        windowScrollX: globalThis.scrollX || 0,
        windowScrollY: globalThis.scrollY || 0,
      } : {}),
    });
  }, [workspaceKey]);

  useLayoutEffect(() => {
    registerWorkspaceCapture?.(rememberWorkspace);
    return () => registerWorkspaceCapture?.(null);
  }, [registerWorkspaceCapture, rememberWorkspace]);

  // The workspace restores once the projected document has hydrated far enough
  // to hold the remembered selection; the projection arrives asynchronously from
  // the collaboration bootstrap, unlike the old synchronous snapshot source.
  const workspaceRestoredRef = useRef(false);
  useLayoutEffect(() => {
    const saved = canvasWorkspaceMemory.get(workspaceKey);
    const textarea = sourceRef.current;
    if (saved && textarea && !workspaceRestoredRef.current
      && textarea.value.length >= Math.max(Number(saved.selectionStart) || 0, Number(saved.selectionEnd) || 0)) {
      workspaceRestoredRef.current = true;
      textarea.setSelectionRange(saved.selectionStart, saved.selectionEnd, saved.selectionDirection);
      textarea.scrollTop = saved.scrollTop;
      textarea.scrollLeft = saved.scrollLeft;
      if (!/jsdom/i.test(globalThis.navigator?.userAgent || '')) globalThis.scrollTo?.(saved.windowScrollX || 0, saved.windowScrollY || 0);
    }
    return () => rememberWorkspace();
  }, [rememberWorkspace, workspaceKey, projectedSource]);

  // Every discrete textarea gesture (typing, Enter, Backspace over a newline,
  // paste, cut) becomes the exact MCRO-COLLAB/1 operations for the affected
  // sourceLineIds; the visible text is always the optimistic projection of the
  // durable document, never a locally-diverging draft.
  // MCRO001-F0061 / T-MCRO-061 (MCRO001-D004 §10.3 (b)): pre-join text on
  // screen is DISPLAY state and is never queue-applicable state — otherwise the
  // diff below would run against `snapshotSource`, translate it into
  // MCRO-COLLAB/1 operations and push them at the durable IndexedDB queue,
  // exactly the shape of failure that poisoned a client's persisted queue in
  // the MCRO001-F0024 incident family when a live draft's content changed under
  // the same draft id.
  //
  // The document-presence term deliberately lives in `readOnly` above and ONLY
  // there, rather than being repeated here as belt-and-braces. Every caller of
  // this function is already behind that one gate, so a duplicate `if
  // (!collaboration.document) return;` here would be unreachable — and a check
  // that cannot fail is not a check: it was mutation-tested, survived, and
  // (worse) masked the mutation of the real gate, hiding a genuine regression
  // in T-MCRO-061 case D. One gate, provably falsifiable, is the protection.
  // The second, real layer is in the engine, not this component:
  // useChoreographyCollaboration.submitOperations refuses outright without a
  // canonical document and an admitted socket.
  const submitSourceEdit = (next) => {
    try {
      // Diff against the live optimistic document at event time: browsers fire
      // an input event after a committed composition, and keystrokes can land
      // between renders; both must translate against the current lines, never a
      // stale render closure (which would duplicate operations).
      const liveDocument = collaboration.currentDocument?.() || collaboration.document;
      const previous = orderedLines(liveDocument).map((line) => line.text).join('\n');
      if (previous === next) return;
      const operations = sourceEditOperations(liveDocument, previous, next);
      if (operations.length) collaboration.submitOperations(operations);
    } catch (error) { setAnnouncement(error.message); }
  };
  const updateSource = (event) => {
    if (readOnly) return;
    const body = event.target.value;
    if (compositionRef.current) { setCompositionDraft(body); return; }
    submitSourceEdit(body);
  };
  const compositionStarted = () => {
    // MCRO001-F0061 (§10.3 (b)): an IME composition is an edit. It must not
    // start against pre-join text — `compositionEnded` returns immediately when
    // no composition was started, so this closes the whole IME path too.
    if (readOnly) return;
    compositionRef.current = { source: projectedSource };
    collaboration.startComposition();
    setCompositionDraft(projectedSource);
  };
  const compositionEnded = async (event) => {
    const started = compositionRef.current; compositionRef.current = null;
    if (!started) return;
    const composed = event.currentTarget.value;
    const previousParts = started.source.split('\n'); const nextParts = composed.split('\n');
    const changes = previousParts.map((text, index) => text !== nextParts[index]);
    const changedIndex = changes.indexOf(true);
    const singleLine = previousParts.length === nextParts.length && changedIndex >= 0
      && changes.lastIndexOf(true) === changedIndex && documentLines[changedIndex];
    try {
      const committed = singleLine
        ? await collaboration.commitComposition(documentLines[changedIndex].sourceLineId, previousParts[changedIndex], nextParts[changedIndex])
        : await collaboration.commitComposition(documentLines[0]?.sourceLineId, '', '');
      if (!singleLine && committed) submitSourceEdit(composed);
    } catch (error) { setAnnouncement(error.message); }
    setCompositionDraft(null);
  };
  const cursorAnnouncedRef = useRef(false);
  const announceCursorRef = useRef(null);
  const announceCursor = (textarea) => {
    if (!textarea || compositionRef.current || readOnly) return;
    const start = Number(textarea.selectionStart) || 0; const end = Number(textarea.selectionEnd ?? start) || 0;
    const before = String(textarea.value || '').slice(0, start);
    const lineStart = before.lastIndexOf('\n') + 1;
    const line = documentLines[before.split('\n').length - 1];
    if (!line) return;
    cursorAnnouncedRef.current = true;
    collaboration.sendCursor({ sourceLineId: line.sourceLineId, field: 'source', offset: start - lineStart,
      ...(end !== start ? { selectionEnd: end - lineStart } : {}) });
  };
  announceCursorRef.current = announceCursor;
  const workspaceChanged = (textarea) => { rememberWorkspace(textarea); announceCursor(textarea); };
  // Presence keep-alive: the ephemeral cursor channel expires at the normative
  // heartbeat window, so an idle-but-present caret re-announces its live
  // textarea position on the heartbeat cadence (and late joiners receive every
  // active caret within one interval). Nothing is sent before the editor has
  // ever placed a caret.
  useEffect(() => {
    const timer = setInterval(() => { if (cursorAnnouncedRef.current) announceCursorRef.current?.(sourceRef.current); }, 20_000);
    return () => clearInterval(timer);
  }, []);
  // Remote carets project against the same visible optimistic document as the
  // text itself: canonical anchors fold through the local pending queue, then
  // resolve to the visible line order. Colour is the deterministic per-person
  // palette shared with the presence strip. Hidden during an active local IME
  // composition so the composition fence and draft text stay undisturbed.
  const remoteCursors = useMemo(() => {
    if (compositionDraft !== null) return [];
    return projectRemoteCursors(collaboration.document, collaboration.cursors, collaboration.pendingOperations || [])
      .map((placement) => ({ ...placement,
        userId: placement.actor.userId,
        color: collaboratorColor(placement.actor.userId),
        selectionColor: collaboratorSelectionColor(placement.actor.userId),
        label: String(placement.actor.displayName || placement.actor.initials || 'Editor'),
      }));
  }, [collaboration.cursors, collaboration.document, collaboration.pendingOperations, compositionDraft]);
  // "Save canvas revision" now creates a durable checkpoint of the exact saved
  // collaborative draft; the continuous queue is the persistence layer.
  const onSave = async () => {
    const reason = window.prompt('Save a durable canvas revision of this exact saved draft?', 'Canvas revision'); if (!reason) return;
    try { const result = await collaboration.checkpoint(reason); await onRevisionSaved?.(result); setAnnouncement(`Draft revision ${result.revisionNumber} saved.`); }
    catch (error) { setAnnouncement(error.message); }
  };

  // D001 §5.1 “Ask Choreo”: an asynchronous review-only proposal bound to the
  // exact source it was requested for. Later typing marks it stale; failure or
  // an unavailable interpreter leaves the deterministic canvas untouched and
  // reports one local retry status (D002 §13). Nothing is ever auto-applied.
  const [assist, setAssist] = useState({ status: 'idle', proposal: null, error: '', sourceAtRequest: '' });
  const askChoreo = async () => {
    const requestedSource = source;
    setAssist({ status: 'loading', proposal: null, error: '', sourceAtRequest: requestedSource });
    try {
      const proposal = payload(await choreographyApi.interpretBlueprint(orgId, {
        title: String(routineTitle || '').trim() || 'Untitled routine',
        sourceText: requestedSource,
        sourceRevision: Number(collaboration.document?.serverSequence) || 0,
      }));
      setAssist((current) => current.status === 'loading' && current.sourceAtRequest === requestedSource
        ? { status: proposal?.fallbackUsed || proposal?.fallback || proposal?.status === 'fallback' ? 'fallback' : 'ready', proposal, error: '', sourceAtRequest: requestedSource }
        : current);
    } catch (cause) {
      setAssist((current) => current.status === 'loading' && current.sourceAtRequest === requestedSource
        ? { status: 'error', proposal: null, error: message(cause), sourceAtRequest: requestedSource }
        : current);
    }
  };
  const assistStale = ['ready', 'fallback'].includes(assist.status) && assist.sourceAtRequest !== source;
  const assistSections = assist.proposal?.proposal?.sections || assist.proposal?.sections || [];
  const assistQuestions = assist.proposal?.unresolved || assist.proposal?.questions || [];

  // D001 §5.1 structural actions expressed as the exact D002 §5 grammar: each
  // one is an insertion-only line-prefix edit over the captured selection,
  // submitted through the same MCRO-COLLAB/1 operation queue as typing.
  const applyStructuralEdit = (kind, range) => {
    if (readOnly || compositionRef.current) return;
    const liveDocument = collaboration.currentDocument?.() || collaboration.document;
    const liveLines = orderedLines(liveDocument);
    const previous = liveLines.map((line) => line.text).join('\n');
    const edit = structuralSelectionEdit(previous, range.sourceStart, range.sourceEnd, kind);
    if (!edit) {
      setAnnouncement('The selected lines already carry that marker.');
      return;
    }
    try {
      // One `text.insert` per stable sourceLineId: the exact D002 grammar
      // prefix lands on each captured line without join/split identity churn.
      const operations = edit.insertions.map(({ line, offsetInLine, insert }) => {
        const target = liveLines[line];
        if (!target) throw new Error('The selection no longer matches the live document.');
        return { type: 'text.insert', lineId: target.sourceLineId, offset: offsetInLine, text: insert };
      });
      collaboration.submitOperations(operations);
    } catch (error) {
      setAnnouncement(error.message);
      return;
    }
    const textarea = sourceRef.current;
    const restore = () => {
      if (!textarea) return;
      textarea.focus();
      textarea.setSelectionRange(edit.selectionStart, edit.selectionEnd);
    };
    if (typeof globalThis.requestAnimationFrame === 'function') globalThis.requestAnimationFrame(restore);
    else globalThis.setTimeout?.(restore, 0);
    setAnnouncement(kind === 'section' ? 'Section marker added before the selection.'
      : kind === 'counts' ? 'Explicit [8] allocation added to the selected movement.'
        : 'Teaching-note marker added; the selection no longer consumes counts.');
  };
  const structuralActions = readOnly ? [] : [
    { id: 'make-section', label: 'Make section', description: 'Start a # form section at the selection', run: (range) => applyStructuralEdit('section', range) },
    { id: 'teaching-note', label: 'Treat as teaching note', description: 'Keep the selected lines as // notes without counts', run: (range) => applyStructuralEdit('note', range) },
    { id: 'propose-counts', label: 'Propose counts', description: 'Allocate the selected lines as explicit [8] eight-counts', run: (range) => applyStructuralEdit('counts', range) },
    { id: 'ask-choreo', label: 'Ask Choreo', description: 'Request a review-only interpretation proposal', run: () => askChoreo() },
  ];
  // D003 §4/§5/§7 owner-approved amendment: the community layer now also owns
  // the shared toolbar on a live draft (the routine's current collaboration
  // document), not only a published displayed revision, provided Review is
  // not Hide. The server (CommunityReviewLayer's getCommunity/authority) is
  // the actual contribution-authority gate; this only decides which binding
  // (published revision vs. current live draft) the layer mounts against.
  const liveDraftId = collaboration.document?.draftId;
  const liveDraftServerSequence = collaboration.document?.serverSequence;
  const reviewLayerActive = Boolean(orgId && routineId && reviewDisplay !== 'hide'
    && ((revisionId && displayedRevisionId === revisionId) || (canEdit && liveDraftId)));
  // The badge is the single, non-shifting status indicator (no block progress line above the
  // canvas). Its short text is the phase label; any more detailed transient message from
  // collaboration (e.g. "Reconnecting…", "Opening the durable draft…") rides along as a
  // tooltip/aria-label instead of its own reflowing element.
  // MCRO001-F0061 / T-MCRO-061 (§10.3 (d)): the same single badge, now also
  // carrying the provenance of the text on screen. No new chrome.
  // MCRO001-F0148/F0150 (D004 §12.3, choreo issue #41): the bootstrap
  // envelope's divergence provenance rides in the badge text and detail.
  // statusLabel consumes it ONLY in the live branch, so no non-live phase
  // can ever claim a saved state (F0152); with a pre-F0148 server the fields
  // are null and the badge degrades to the plain phase label.
  const divergence = (collaboration.draftUpdatedAt || collaboration.lastPublishedAt || collaboration.baseRevisionId)
    ? { draftUpdatedAt: collaboration.draftUpdatedAt, lastPublishedAt: collaboration.lastPublishedAt, baseRevisionId: collaboration.baseRevisionId }
    : null;
  const saveStateLabel = statusLabel(collaboration.phase, collaboration.pendingCount, showingSavedVersion, divergence);
  const saveStateDetail = showingSavedVersion
    ? `Showing the last saved version of this canvas. ${collaboration.message || 'It becomes editable when live editing connects.'}`
    : [collaboration.message, divergenceDetail(divergence)].filter(Boolean).join(' ') || saveStateLabel;

  return <section className="mc-routine-canvas" aria-label="Choreo Canvas editor">
    <header className="mc-routine-canvas-toolbar"><div><span className="mc-kicker">Choreo Canvas</span><h2>{routineTitle || 'Routine'}</h2></div><div className="mc-collab-actions">{castNumbers.length > 0 && <label className="mc-field mc-viewing-as-field"><span>Viewing as</span><select aria-label="Viewing as" value={viewingAs === '' ? '' : String(Number(viewingAs))} onChange={(event) => onChangeViewingAs?.(event.target.value)}><option value="">Full team</option>{castNumbers.map((number) => <option key={number} value={String(number)}>{`Dancer ${number}`}</option>)}</select></label>}<CollaborationPeople people={collaboration.people} /><strong className={`mc-save-state ${collaboration.phase}${divergence ? ' diverged' : ''}`} data-canvas-source={documentLive ? 'live' : 'saved'} data-divergence={divergence ? 'true' : undefined} role="status" title={saveStateDetail} aria-label={saveStateDetail}>{saveStateLabel}</strong>{!readOnly && <button type="button" className="mc-primary" onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save revision'}</button>}{!readOnly && onPublish && <button type="button" className="mc-quiet" onClick={onPublish} disabled={publishBusy || saving}>{publishBusy ? 'Publishing…' : 'Publish'}</button>}{onChangeReviewDisplay && <label className="mc-field mc-review-display-field"><select aria-label="Review display" value={reviewDisplay} onChange={(event) => onChangeReviewDisplay(event.target.value)}>{COMMUNITY_REVIEW_DISPLAYS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}</select></label>}</div></header>
    {announcement && <div className="mc-notice" role="status">{announcement}</div>}
    <div className={`mc-community-canvas-layout mc-review-${reviewDisplay}-layout`}>
      <div className="mc-blank-page mc-blank-page-canvas">
        <label className="mc-visually-hidden" htmlFor="mc-routine-canvas-source">Choreography source</label>
        <ContinuousCanvasEditor
          inputRef={sourceRef}
          value={source}
          score={score}
          viewingAsView={viewingAsView}
          onChange={updateSource}
          onCompositionStart={compositionStarted}
          onCompositionEnd={compositionEnded}
          ariaLabel="Choreography source"
          placeholder={CANVAS_PLACEHOLDER}
          readOnly={readOnly}
          onWorkspaceChange={workspaceChanged}
          remoteCursors={remoteCursors}
          demonstrationRail={<DemonstrationTimingRail
            candidates={demonstrationCandidates}
            errorCandidates={demonstrationErrors}
            revisionId={displayedRevisionId}
            sections={snapshot?.sections || []}
            authoredBars={demonstrationBars}
            canEdit={railCanEdit}
            onSaveTimingMap={demonstrationBoundary?.onSaveTimingMap}
            focusOnIds={focusOnIds}
            onEditModeChange={setRailEditModeActive}
          />}
        />
        {/* Choreo #46 (companion to the F0085 rail slice): the stage
            information surface sits directly under the canvas it describes —
            the panel the owner reported as missing entirely. It renders
            whenever this canvas has a revision to describe (populated,
            degraded, invalid, empty, loading or failed-and-retryable);
            without a revision there is genuinely no stage to describe. */}
        {displayedRevisionId && <StageInformationPanel
          candidates={demonstrationCandidates}
          errorCandidates={demonstrationErrors}
          authoredBars={demonstrationBars}
          revisionId={displayedRevisionId}
          publishedRevisionId={revisionId || ''}
          canEdit={railCanEdit}
          loadState={demonstrationLoadState}
          onRetry={demonstrationBoundary?.refetchDemonstrations}
        />}
        {formationSections.length > 0 && <div className="mc-canvas-formations">
          {formationSections.map((section) => <div className="mc-canvas-formation" key={section.id}>
            <span className="mc-canvas-formation-section">{section.label}</span>
            <FormationStage formations={section.formations} count={canvasFormationCount} highlight={viewingAsNumber} />
          </div>)}
        </div>}
        {demonstrationCandidates.length > 0 && !hasPlaybackContext && <span className="mc-visually-hidden">Focus-on rate treatment will appear when live Rehearsal rate context is available.</span>}
        {!readOnly && <p className="mc-writing-help">Click any section, sequence or movement to edit its exact source. The musical structure stays behind the text.</p>}
        {assist.status !== 'idle' && <section className={`mc-canvas-assist ${assistStale ? 'stale' : assist.status}`} aria-label="Ask Choreo proposal">
          <div role="status" aria-live="polite">
            {assist.status === 'loading' && <p>Choreo is preparing a separate review-only proposal…</p>}
            {assistStale && <p>This proposal is stale because the source changed. Ask Choreo again for a fresh interpretation.</p>}
            {!assistStale && assist.status === 'ready' && <p>A review-only proposal is ready. Nothing has been applied to your canvas.</p>}
            {!assistStale && assist.status === 'fallback' && <p>The preferred model was unavailable, so the configured fallback model prepared this review-only proposal.</p>}
            {assist.status === 'error' && <p>Ask Choreo is unavailable right now: {assist.error} Your canvas and its deterministic structure are unchanged.</p>}
          </div>
          {['ready', 'fallback'].includes(assist.status) && assistSections.length > 0 && <ol className="mc-canvas-assist-outline">{assistSections.slice(0, 8).map((assistSection, index) => <li key={assistSection.id || `${assistSection.label}-${index}`}>{assistSection.label || `Part ${index + 1}`}{Number(assistSection.countLength) ? ` · ${assistSection.countLength} counts` : ''}</li>)}</ol>}
          {['ready', 'fallback'].includes(assist.status) && assistQuestions.length > 0 && <ul className="mc-canvas-assist-questions">{assistQuestions.map((question, index) => <li key={question.code || question.id || index}>{question.prompt || question.message || question.text || 'Review this interpretation.'}</li>)}</ul>}
          <div className="mc-canvas-assist-actions">
            {(assist.status === 'error' || assistStale) && <button type="button" className="mc-quiet" onClick={askChoreo}>Ask Choreo again</button>}
            <button type="button" className="mc-quiet" onClick={() => setAssist({ status: 'idle', proposal: null, error: '', sourceAtRequest: '' })}>Dismiss</button>
          </div>
        </section>}
      </div>
      {reviewLayerActive
        ? <CommunityReviewLayer orgId={orgId} routineId={routineId} revisionId={revisionId} draftId={(!revisionId || displayedRevisionId !== revisionId) ? liveDraftId : undefined} draftServerSequence={(!revisionId || displayedRevisionId !== revisionId) ? liveDraftServerSequence : undefined} source={source} sourceRef={sourceRef} capabilities={capabilities} display={reviewDisplay} selectionActions={structuralActions} sections={snapshot?.sections || []} commentsSuppressed={railEditModeActive} />
        : <>
          {orgId && routineId && !(revisionId && displayedRevisionId === revisionId) && !canEdit && reviewDisplay !== 'hide' && <aside className="mc-community-review mc-review-unavailable" aria-label="Community review unavailable for draft"><span className="mc-kicker">Community review</span><strong>Published revision only</strong><p>Comments and media stay attached to the immutable published Canvas. Publish this draft or open its published revision before adding review material.</p></aside>}
          <CanvasStructuralToolbar sourceRef={sourceRef} source={source} actions={structuralActions} commentsSuppressed={railEditModeActive} />
        </>}
    </div>
  </section>;
}

function controlledMappedDemonstrationEvidenceBoundary() {
  // The protected browser contract may inject the future #920 boundary only
  // into an explicitly flagged development build. Production stays fail-closed.
  if (process.env.REACT_APP_CHOREO_MAPPED_DEMONSTRATION_E2E !== '1'
      || process.env.NODE_ENV === 'production' || typeof window === 'undefined') return undefined;
  return window.__STUDIOAEYE_MAPPED_DEMONSTRATION_BOUNDARY__;
}

// MCRO001-F0026 web wiring (MCRO001-D006 §2/§4/§7): a 409 on save means the
// server rejected a stale optimistic write — never retry-and-overwrite. These
// three codes are the exact ones choreographyDemonstrationTiming.service.ts
// raises (backend rht/studioaeye-choreo, choreographyDemonstrationTiming.service.ts).
const DEMONSTRATION_MAP_STALE_CODES = new Set(['MAP_VERSION_STALE', 'REVISION_STALE', 'SYNC_POINT_STALE']);

// The backend error envelope (middleware/errorHandler.ts) is
// { success: false, error: <code>, message, details }, i.e. the machine code
// lives at response.data.error (a string), not response.data.error.code.
const demonstrationErrorCode = (error) => error?.response?.data?.error;

export function demonstrationConflictMessage(error) {
  if (error?.response?.status !== 409) return '';
  const code = demonstrationErrorCode(error);
  if (!DEMONSTRATION_MAP_STALE_CODES.has(code)) return '';
  return 'This demonstration was changed elsewhere — reload to see the latest.';
}

// approvedCandidate() (mappedDemonstrationProjection.js) expects a richer,
// moderation-aware raw shape (sequenceLabel, eligible, mapState,
// authorizationState, rendition.{authorizationState,availability,source})
// that predates F0026 landing. The F0026 backend has no moderation/approval
// concept at all: choreography_demonstration_timing_maps has no state column,
// and choreographyDemonstrationTimingService.list() already filters to
// deleted_at IS NULL rows the caller has choreography_view authority to read
// (backend choreographyDemonstrationTiming.service.ts:452-463). So every
// record this endpoint returns IS, by construction, the approved/eligible
// set — this function synthesizes the fields approvedCandidate() checks so
// they pass, rather than inventing a second authorization gate the backend
// does not have.
export function demonstrationRecordToRawCandidate(record, { sections, authoredBars, canEditTiming, mediaSource, mediaPending }) {
  return {
    demonstrationId: record.demonstrationId,
    sequenceLabel: `Demonstration · priority ${Number(record.priority) + 1}`,
    eligible: true,
    mapState: 'saved',
    authorizationState: 'approved',
    revisionId: record.revisionId,
    priority: record.priority,
    colour: record.railColour,
    canEditTiming,
    expectedMapVersion: record.expectedMapVersion,
    mediaId: record.mediaId,
    timingMap: {
      revisionId: record.revisionId,
      renditionId: record.renditionId,
      durationMs: record.durationMs,
      mediaStartMs: record.mediaStartMs,
      mediaEndMs: record.mediaEndMs,
      startCount: record.startCount,
      endCount: record.endCount,
      sections,
      authoredBars,
      syncPoints: record.syncPoints,
    },
    expectedRenditionVersion: record.renditionVersion,
    rendition: {
      renditionId: record.renditionId,
      version: record.renditionVersion,
      authorizationState: 'approved',
      // F0085 follow-up (post-merge review of !282): three honest states, not
      // two. 'loading' is the REAL in-flight signal — the boundary marks
      // exactly the mediaIds whose content fetch is still running — so the
      // owner sees "media loading, showing counts from the saved map" while
      // an 86 MB blob downloads instead of the false "media unavailable" the
      // binary mapping produced (which also left the projection's own
      // 'media-loading' branch unreachable).
      availability: mediaSource ? 'available' : (mediaPending ? 'loading' : 'unavailable'),
      source: mediaSource || '',
    },
  };
}

// timingMapMutationRequest() (demonstrationTimingRailModel.js) always sends
// { demonstrationId, revision: { revisionId }, timingMap, priorityOrder,
// expectedMapVersion, mediaId?, relation?, rendition? } — this flattens that
// into the backend's expected top-level shape for POST (create) / PATCH
// (retime + optional priorityOrder). See choreo backend
// choreographyDemonstrationTiming.service.ts + choreography.routes.ts:339-359.
export function demonstrationMutationPayload(request) {
  const timingMap = request?.timingMap || {};
  return {
    revisionId: request?.revision?.revisionId ?? request?.revisionId,
    mediaId: request?.mediaId,
    renditionId: timingMap.renditionId,
    // Both producers of this request send `expectedVersion`, not `version`:
    // DemonstrationWorkspace.save() builds `rendition: { renditionId,
    // expectedVersion }` and demonstrationTimingRailModel.timingMapMutationRequest()
    // copies candidate.renditionMutation through unchanged. Reading only
    // `rendition.version` left renditionVersion permanently undefined on the wire.
    renditionVersion: request?.rendition?.version
      ?? request?.rendition?.expectedVersion
      ?? request?.renditionVersion,
    durationMs: timingMap.durationMs,
    mediaStartMs: timingMap.mediaStartMs,
    mediaEndMs: timingMap.mediaEndMs,
    startCount: timingMap.startCount,
    endCount: timingMap.endCount,
    syncPoints: timingMap.syncPoints,
    ...(Array.isArray(request?.priorityOrder) ? { priorityOrder: request.priorityOrder } : {}),
  };
}

// Builds the production demonstrationBoundary consumed by RoutineCanvasEditor
// (rail) / Rehearsal (backdrop) / DemonstrationTimingRail's onSaveTimingMap.
// readApprovedSavedDemonstrations() is synchronous by contract
// (mappedDemonstrationProjection.js readApprovedMappedDemonstrations calls it
// without awaiting), so the async HTTP list lives in component state fetched
// by useEffect (see useMappedDemonstrationBoundary below) and this function
// only ever reads that already-resolved state back out synchronously —
// exactly the pattern this file already uses for `capabilities`.
function buildRealMappedDemonstrationBoundary({
  recordsByRevision,
  failedRevisions,
  pendingRevisions,
  refetchDemonstrations,
  mediaSourceByMediaId,
  pendingMediaIds,
  sections,
  authoredBars,
  canEditTiming,
  onSaveTimingMap,
}) {
  return {
    readApprovedSavedDemonstrations: (context) => {
      const records = recordsByRevision.get(String(context?.revisionId || '')) || [];
      return records.map((record) => demonstrationRecordToRawCandidate(record, {
        sections,
        authoredBars,
        canEditTiming,
        mediaSource: mediaSourceByMediaId.get(String(record.mediaId || '')) || '',
        mediaPending: pendingMediaIds.has(String(record.mediaId || '')),
      }));
    },
    // Companion slice to F0085 (choreo #46, StageInformationPanel): lets a
    // caller tell the three list states apart, which readApprovedSavedDemonstrations's
    // [] cannot — "still fetching", "fetch failed" (retryable, honest error —
    // never a fake empty), and "settled" ([] then means genuinely empty).
    // Absent from the E2E-injected double by construction: consumers must
    // treat a missing method as settled (the controlled harness serves the
    // list synchronously, so its empty state IS settled-truthful).
    readDemonstrationsLoadState: (context) => {
      const revisionId = String(context?.revisionId || '');
      if (failedRevisions.has(revisionId)) return { settled: true, failed: true };
      if (recordsByRevision.has(revisionId)) return { settled: true, failed: false };
      return { settled: !pendingRevisions.has(revisionId), failed: false };
    },
    refetchDemonstrations,
    onSaveTimingMap,
    // F0026's persisted maps are counts-only (startCount/endCount resolved
    // through syncPoints); there is no separate playback-rate capability gate
    // server-side, so every effective rate the F0028/F0029 projection derives
    // is supported here — the HTML5 video element itself is the only limit,
    // and MappedDemonstrationBackdrop already falls back to frame projection
    // if setting video.playbackRate throws.
    supportsPlaybackRate: () => true,
  };
}

// Fetches the live F0026 demonstration list (event-driven: on mount/identity
// change and after every successful own mutation — no polling, per the task's
// explicit "next stale save 409s and shows reload" concurrency model) and
// resolves each distinct mediaId's authenticated content into a cached blob
// object URL (the only way this backend serves video: every route either
// streams an authenticated proxy or 302s an approved_external_reference —
// see backend routes.ts sendPrivateContent/mediaAccess; there is no bare
// signed URL a <video src> could use without the caller's own Bearer token).
// canEditTiming is the SAME routine-level authority mirror as `canEdit`
// (server's requireEdit has no per-map ownership distinction), applied
// uniformly to every candidate built from the fetched list.
function useMappedDemonstrationBoundary({ orgId, routineId, revisionIds, sections, authoredBars, canEditTiming, enabled = true }) {
  const [recordsByRevision, setRecordsByRevision] = useState(new Map());
  const [mediaSourceByMediaId, setMediaSourceByMediaId] = useState(new Map());
  // F0085 follow-up (post-merge review of !282): which mediaIds' content
  // fetches are in flight RIGHT NOW — the observable window between the
  // demonstrations list arriving and each blob settling. Surfaced through the
  // boundary so candidates project honest 'media-loading' degradation ("map
  // present — media loading…") instead of a false 'media-unavailable' while
  // an 86 MB fetch runs; without it the projection's 'media-loading' branch
  // was unreachable dead code.
  const [pendingMediaIds, setPendingMediaIds] = useState(() => new Set());
  // Companion slice to F0085 (choreo #46): which revisions' list fetches are
  // in flight (pendingRevisions) and which settled as FAILURES
  // (failedRevisions). recordsByRevision alone cannot distinguish "still
  // loading" from "confirmed empty" or "fetch failed" — all three read as []
  // — and the StageInformationPanel needs the honest difference: a failed
  // list fetch must surface a retryable error, never a fake "nothing mapped".
  const [pendingRevisions, setPendingRevisions] = useState(() => new Set());
  const [failedRevisions, setFailedRevisions] = useState(() => new Set());
  const [refreshToken, setRefreshToken] = useState(0);
  const [conflictNotice, setConflictNotice] = useState('');
  const mediaUrlCacheRef = useRef(new Map());
  // revisionIds arrives as a fresh array literal from the caller every render
  // (e.g. [latestRevision?.revisionId, routine?.publishedRevisionId]); a
  // useMemo keyed on that array's identity would never hit its cache (a new
  // array is a new reference every render) and would re-fire the fetch
  // effect below every render — an infinite render loop. Depending on this
  // joined string instead gives the effect a primitive, referentially-stable
  // key so it only re-runs when the actual set of ids changes.  (unit
  // separator) cannot appear in a UUID/revision id, so the join is unambiguous.
  const revisionIdsKey = [...new Set((revisionIds || []).map((value) => String(value || '')).filter(Boolean))]
    .sort().join('');
  const dedupedRevisionIds = useMemo(
    () => revisionIdsKey ? revisionIdsKey.split('') : [],
    [revisionIdsKey],
  );
  const refetch = useCallback(() => setRefreshToken((current) => current + 1), []);

  useEffect(() => {
    // `enabled` is false whenever the E2E-injected boundary
    // (window.__STUDIOAEYE_MAPPED_DEMONSTRATION_BOUNDARY__) is the one
    // actually in use — this hook still runs unconditionally (React hooks
    // cannot be called conditionally) but must never issue live network
    // calls in that mode, or the Playwright specs' strict route allowlists
    // (which throw on any unexpected request) fail.
    if (!enabled || !orgId || !routineId || dedupedRevisionIds.length === 0
        || typeof choreographyApi.listDemonstrations !== 'function') {
      setRecordsByRevision((current) => current.size === 0 ? current : new Map());
      // Disabled/no-fetch mode: nothing is in flight and nothing can fail —
      // every revision reads as settled (and empty unless another source
      // serves it, e.g. the E2E-injected boundary).
      setPendingRevisions((current) => current.size === 0 ? current : new Set());
      setFailedRevisions((current) => current.size === 0 ? current : new Set());
      setPendingMediaIds((current) => current.size === 0 ? current : new Set());
      return undefined;
    }
    let active = true;
    setPendingRevisions(new Set(dedupedRevisionIds));
    Promise.all(dedupedRevisionIds.map((revisionId) => Promise.resolve()
      .then(() => choreographyApi.listDemonstrations(orgId, routineId, revisionId))
      .then((response) => [revisionId, listFrom(payload(response), ['demonstrations']), false])
      .catch(() => [revisionId, [], true])))
      .then((entries) => {
        if (!active) return;
        setRecordsByRevision(new Map(entries.map(([revisionId, records]) => [revisionId, records])));
        setFailedRevisions(new Set(entries.filter(([, , failed]) => failed).map(([revisionId]) => revisionId)));
        setPendingRevisions(new Set());
      });
    return () => { active = false; };
    // dedupedRevisionIds is memoized on the primitive revisionIdsKey above, so
    // its identity is stable whenever the actual id set is unchanged — safe
    // and exhaustive as a dependency.
  }, [enabled, orgId, routineId, dedupedRevisionIds, refreshToken]);

  useEffect(() => {
    if (!enabled) return undefined;
    const mediaIds = [...new Set([...recordsByRevision.values()].flat().map((record) => String(record.mediaId || '')).filter(Boolean))];
    const missing = mediaIds.filter((mediaId) => !mediaUrlCacheRef.current.has(mediaId));
    if (!orgId || missing.length === 0) return undefined;
    let active = true;
    // F0085 follow-up: mark exactly the in-flight ids BEFORE the first byte
    // moves (union, not replace — a refetch while an earlier fetch is still
    // running must not un-mark it), so the rail names the fetch honestly for
    // its whole duration.
    setPendingMediaIds((current) => {
      const next = new Set(current);
      missing.forEach((mediaId) => next.add(mediaId));
      return next;
    });
    // Settle PER MEDIA, not after Promise.all: one slow 86 MB fetch must not
    // keep every other already-settled video's line falsely
    // 'media-unavailable' (the pre-follow-up batching did exactly that).
    missing.map((mediaId) => Promise.resolve()
      .then(() => choreographyApi.getMediaContent(orgId, mediaId))
      .then((response) => {
        const blob = response?.data instanceof Blob ? response.data : null;
        const url = blob ? URL.createObjectURL(blob) : '';
        mediaUrlCacheRef.current.set(mediaId, url);
        return url;
      })
      .catch(() => { mediaUrlCacheRef.current.set(mediaId, ''); return ''; })
      .then(() => {
        // Id-scoped pending clear is safe from a stale closure (union above
        // means a superseding run's ids are never touched); the object-URL
        // state publish stays active-guarded exactly as before.
        setPendingMediaIds((current) => {
          if (!current.has(mediaId)) return current;
          const next = new Set(current);
          next.delete(mediaId);
          return next;
        });
        if (active) setMediaSourceByMediaId((current) => {
          // Choreo #47 settled-source guarantee (re-land of !286): a source,
          // once published for a mediaId, is NEVER reassigned or retracted by
          // a later publish — the publish diff is monotonic per mediaId. Each
          // settle may only ADD ids that are not published yet (always with
          // their cache url, which is itself immutable per mediaId), so a
          // late B-media settle cannot churn — or, under a future regression,
          // replace — the already-playing A-media's entry. When nothing new
          // resolved, return the SAME Map: a no-op settle must not publish a
          // fresh Map identity, because the boundary memo below keys on it
          // and every rebuild re-derives every raw candidate (and via
          // Rehearsal's setCount the count-authoritative drill timer) — the
          // churn that stalled F0027 Case D's mid-loop seek on pipeline
          // 7845 job 172211.
          let changed = false;
          const next = new Map(current);
          mediaUrlCacheRef.current.forEach((url, resolvedId) => {
            if (next.has(resolvedId) || !url) return;
            next.set(resolvedId, url);
            changed = true;
          });
          return changed ? next : current;
        });
      }));
    return () => { active = false; };
  }, [orgId, recordsByRevision]);

  useEffect(() => () => { mediaUrlCacheRef.current.forEach((url) => { if (url) URL.revokeObjectURL(url); }); }, []);

  const onSaveTimingMap = useCallback(async (request) => {
    setConflictNotice('');
    const revisionId = request?.revision?.revisionId ?? request?.revisionId;
    const isCreate = !request?.demonstrationId || !Number.isInteger(request?.expectedMapVersion);
    try {
      // MCRO001-F0058 hardening: see retryOnRateLimit's own comment — covers
      // every demonstration write through this one boundary (manual
      // "Add as a demonstration", the F0028 rail's retime/reorder, and this
      // Feature's own attach-time/reconcile auto-saves alike), so a
      // transient 429 is paced rather than reported as a save failure.
      const response = await retryOnRateLimit(() => (
        isCreate
          ? choreographyApi.createDemonstration(orgId, routineId, demonstrationMutationPayload(request))
          : choreographyApi.updateDemonstration(orgId, routineId, request.demonstrationId, {
            expectedMapVersion: request.expectedMapVersion,
            ...demonstrationMutationPayload(request),
          })
      ));
      const data = payload(response);
      refetch();
      return { committed: data?.committed === true, demonstration: data?.demonstration };
    } catch (error) {
      // MCRO001-F0058 hardening (owner/coordinator, pipeline 7008 job 146184
      // investigation): a CREATE can lose a genuine race against this
      // Feature's own auto-create/reconcile hooks — a manual "Add as a
      // demonstration" and an auto/reconcile save both issue the exact same
      // demonstrationId-less CREATE shape for the same (revision, media),
      // and the backend's uq_choreo_demo_map_revision_media key correctly
      // refuses the SECOND one with 409 DEMONSTRATION_MEDIA_DUPLICATE
      // (studioaeye-choreo choreographyDemonstrationTiming.service.ts
      // create()). Before this fix the loser silently reported "could not
      // be saved" even though a map now genuinely exists for that video — a
      // real user hits this exact race the instant reconcile and a manual
      // save target the same video, and losing their deliberately-authored
      // span/markers to a crude auto-created default would be the wrong
      // outcome. Retry ONCE as an UPDATE against the winning record,
      // carrying the SAME user-authored timingMap content, so the user's
      // deliberate choice is what actually persists — this is refinement of
      // the auto-created default (D006 §2's own framing), not data loss.
      if (isCreate && demonstrationErrorCode(error) === 'DEMONSTRATION_MEDIA_DUPLICATE') {
        try {
          const listResponse = await retryOnRateLimit(() => choreographyApi.listDemonstrations(orgId, routineId, revisionId));
          const existing = listFrom(payload(listResponse), ['demonstrations'])
            .find((record) => String(record.mediaId) === String(request?.mediaId));
          if (existing) {
            const retryResponse = await retryOnRateLimit(() => choreographyApi.updateDemonstration(orgId, routineId, existing.demonstrationId, {
              expectedMapVersion: existing.expectedMapVersion,
              ...demonstrationMutationPayload(request),
            }));
            const retryData = payload(retryResponse);
            refetch();
            return { committed: retryData?.committed === true, demonstration: retryData?.demonstration };
          }
        } catch {
          // Best-effort retry only — never mask a genuine, different
          // failure; fall through to the ordinary failure path below.
        }
      }
      const conflict = demonstrationConflictMessage(error);
      if (conflict) setConflictNotice(conflict);
      refetch();
      return { committed: false };
    }
  }, [orgId, routineId, refetch]);

  const boundary = useMemo(() => buildRealMappedDemonstrationBoundary({
    recordsByRevision, failedRevisions, pendingRevisions, refetchDemonstrations: refetch,
    mediaSourceByMediaId, pendingMediaIds, sections, authoredBars, canEditTiming, onSaveTimingMap,
  }), [recordsByRevision, failedRevisions, pendingRevisions, refetch, mediaSourceByMediaId, pendingMediaIds, sections, authoredBars, canEditTiming, onSaveTimingMap]);

  // MCRO001-F0058: recordsByRevision only gains a key for a given revisionId
  // once its listDemonstrations fetch has actually settled (success OR
  // caught failure both call setRecordsByRevision(new Map(entries)) above),
  // never before. Exposing the raw map (rather than just `boundary`, whose
  // readApprovedSavedDemonstrations() returns [] for BOTH "confirmed empty"
  // and "not fetched yet") is what lets a caller tell those two apart — the
  // no-clobber check below is only trustworthy once the fetch is known-settled.
  return { boundary, refetch, conflictNotice, clearConflictNotice: () => setConflictNotice(''), recordsByRevision };
}

// F0026's stored timing maps have no separate rendition/relation-versioning
// concept (see demonstrationRecordToRawCandidate's comment above): there is
// exactly one media blob per mediaId and no version history for it
// server-side. relationId/renditionId are therefore synthesized
// deterministically FROM mediaId (stable across saves — required so retime
// vs. create is decided correctly by DemonstrationWorkspace/backend, not by
// this synthesis), and expectedRelationVersion/expectedRenditionVersion are
// pinned at 0 rather than fabricated, documented here exactly like that
// comment documents its own synthesis.
const DEMONSTRATION_RELATION_VERSION = 0;
const DEMONSTRATION_RENDITION_VERSION = 0;
// Approved frame step: matches stepTimingSyncPoint's own default
// (demonstrationTimingMap.js), i.e. the one frame-step convention this app
// already uses everywhere frame nudging happens — not a new value.
const DEMONSTRATION_FRAME_STEP_MS = 40;

// MediaLibrary's "Timing editor" can be opened on ANY media item the viewer's
// own library lists — personal scope since MCRO001-F0159 (MCRO001-D021 §1.1;
// the pre-F0159 wording "org-wide searched" is struck) — not just ones that
// already have a saved F0026 demonstration
// (useMappedDemonstrationBoundary above only ever resolves content for
// mediaIds it already found in a fetched demonstration list). This hook
// resolves { blob object URL, real probed duration } for an arbitrary,
// growing set of requested mediaIds, on demand, exactly once per mediaId,
// using the SAME choreographyApi.getMediaContent(...) -> blob -> object URL
// path useMappedDemonstrationBoundary's own effect already uses (never a
// second, different fetch strategy). Duration is probed off a real
// <video>/loadedmetadata event rather than fabricated: DemonstrationWorkspace
// uses durationMs as the range slider's max AND as the seeded default
// mediaEndMs for a brand-new draft (DemonstrationWorkspace.js:209-211), and
// never corrects it after mount — a wrong placeholder would silently cap or
// mis-seed a saved sync point, so eligibility stays honestly false until the
// real duration is known.
function useDemonstrationMediaPreviews(orgId) {
  const [previewByMediaId, setPreviewByMediaId] = useState(new Map());
  const cacheRef = useRef(new Map()); // mediaId -> { url, durationMs } | 'pending' | 'failed'
  const requestPreview = useCallback((mediaId) => {
    const id = String(mediaId || '');
    if (!id || !orgId || cacheRef.current.has(id)) return;
    cacheRef.current.set(id, 'pending');
    Promise.resolve()
      .then(() => choreographyApi.getMediaContent(orgId, id))
      .then((response) => {
        const blob = response?.data instanceof Blob ? response.data : null;
        if (!blob) throw new Error('No media content returned.');
        const url = URL.createObjectURL(blob);
        return new Promise((resolve, reject) => {
          const probe = document.createElement('video');
          probe.preload = 'metadata';
          probe.onloadedmetadata = () => {
            const durationMs = Math.round((probe.duration || 0) * 1000);
            resolve({ url, durationMs });
          };
          // A metadata-decode failure orphans the object URL created just
          // above (the .catch()/cacheRef path below only ever stores the
          // string 'failed' for this mediaId, never a {url} shape, so
          // nothing else in this hook would revoke it) — revoke it here,
          // at the one point that knows this specific url is now unusable.
          probe.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read video metadata.')); };
          probe.src = url;
        });
      })
      .then(({ url, durationMs }) => {
        if (!Number.isInteger(durationMs) || durationMs <= 0) {
          URL.revokeObjectURL(url);
          cacheRef.current.set(id, 'failed');
        } else {
          cacheRef.current.set(id, { url, durationMs });
        }
      })
      .catch(() => { cacheRef.current.set(id, 'failed'); })
      .finally(() => setPreviewByMediaId(new Map(cacheRef.current)));
  }, [orgId]);
  useEffect(() => () => {
    cacheRef.current.forEach((entry) => { if (entry && typeof entry === 'object' && entry.url) URL.revokeObjectURL(entry.url); });
  }, []);
  return { previewByMediaId, requestPreview };
}

// Builds the synchronous getDemonstrationEligibility(...) contract
// DemonstrationWorkspace requires (see that file's demonstrationMediaBoundary):
// eligible is a REAL determination — a playable preview must have already
// resolved (requestPreview populates previewByMediaId asynchronously; until
// it has, this reports ineligible rather than guessing) AND at least one
// complete authored eight-count bar must exist for the current revision
// (completeDemonstrationBars, the same helper DemonstrationWorkspace itself
// uses). Never returns eligible: true without both being true — see task
// brief: "an approved rendition exists and a complete authored eight-count
// bar is available" is the actual gate, not an always-true stub.
function buildDemonstrationEligibility({ previewByMediaId, requestPreview, authoredBars }) {
  return ({ mediaId, revisionId }) => {
    const id = String(mediaId || '');
    if (completeDemonstrationBars(authoredBars, revisionId).length === 0) {
      return { eligible: false, reason: 'No complete eight-count bar is authored yet for this revision.' };
    }
    const entry = previewByMediaId.get(id);
    if (!entry || entry === 'pending') {
      requestPreview(id);
      return { eligible: false, reason: 'Resolving this video for timing setup — try again in a moment.' };
    }
    if (entry === 'failed') {
      return { eligible: false, reason: 'This video could not be opened for timing setup.' };
    }
    // relationId/renditionId are synthesized deterministically from mediaId
    // (see the constants' comment above) — this is the SAME honesty pattern
    // demonstrationRecordToRawCandidate uses for the rail, not a fabricated
    // extra authorization gate: the only real gates are the preview resolving
    // (proves the content is actually fetchable) and the bar-completeness
    // check above.
    return {
      eligible: true,
      relationId: `media-relation-${id}`,
      expectedRelationVersion: DEMONSTRATION_RELATION_VERSION,
      renditionId: `media-rendition-${id}`,
      expectedRenditionVersion: DEMONSTRATION_RENDITION_VERSION,
      durationMs: entry.durationMs,
      approvedPreviewUrl: entry.url,
      frameStepMs: DEMONSTRATION_FRAME_STEP_MS,
    };
  };
}

export function Rehearsal({ orgId, routineId, draftSnapshot = null, routine = null, dirty = false, canEdit = false, onReturnCanvas, demonstrationBoundary = controlledMappedDemonstrationEvidenceBoundary(), playbackSession = null, setPlaybackSession = null, focusOnRateThreshold, viewingAs = '' }) {
  const { user } = useAuth();
  // MCRO001-F0065 (T-MCRO-065): the "viewing as" cast number shared with the
  // Canvas workspace; null = full team. It flows into every position
  // projection below so the Now/Next readout uses the dancer's effective
  // choreography and the D014 stage strip highlights their dot.
  const viewerRaw = Number(viewingAs);
  const viewer = viewingAs !== '' && Number.isSafeInteger(viewerRaw) && viewerRaw >= 1 ? viewerRaw : null;
  const [view, setView] = useState(null); const [loadFailed, setLoadFailed] = useState(false); const [mode, setMode] = useState('count_led'); const [loop, setLoop] = useState({ startCount: 1, endCount: 8 }); const [notice, setNotice] = useState(''); const [active, setActive] = useState(false); const [sourceChoice, setSourceChoice] = useState('auto'); const audioRef = useRef(null);
  // #25's untouched-initial-state sentinel — see the loopDefaultKey block below.
  const LOOP_PLACEHOLDER = { startCount: 1, endCount: 8 };
  // T-MCRO-027: purely observational — the honest effective video rate reported by
  // the backdrop for display next to the speed control. Never read to drive count,
  // projection or playback; fails closed (null) whenever the backdrop reports not
  // visible, including on its own unmount/failure.
  // MCRO001-F0036/T-MCRO-036 case F: `hasCandidates` is carried through the same
  // report so the no-map hint reuses this one live boundary read rather than adding
  // a second one; it defaults to true (hint absent) until the backdrop has reported,
  // so a loading/unmounted boundary never flashes a false "no maps" claim.
  const [videoProjection, setVideoProjection] = useState({ visible: false, effectiveVideoRate: null, fallback: false, hasCandidates: true });
  const onProjectionChange = useCallback((next) => setVideoProjection(next), []);
  // MCRO001-F0037/T-MCRO-037 cases A-C: mirror is a per-actor view preference, never
  // routine state. It is read once from optional storage (a private-mode or corrupt
  // read falls back to off) and written back on every deliberate toggle.
  const [mirrored, setMirrored] = useState(readMirrorPreference);
  const toggleMirror = useCallback(() => setMirrored((current) => {
    const next = !current;
    writeMirrorPreference(next);
    return next;
  }), []);
  // MCRO001-F0037/T-MCRO-037 cases D/E: stage mode is layout only. The maximised
  // layout is applied unconditionally and first, then element fullscreen is requested
  // as a bonus — an absent, throwing or refused fullscreen API therefore still yields
  // a complete, usable maximised surface instead of a half-applied one.
  const [stageMode, setStageMode] = useState(false);
  const stageRef = useRef(null);
  const releaseFullscreen = useCallback(() => {
    try {
      if (typeof document !== 'undefined' && document.fullscreenElement) document.exitFullscreen?.();
    } catch (_) { /* leaving fullscreen is best-effort; the layout is authoritative */ }
  }, []);
  const exitStageMode = useCallback(() => {
    setStageMode(false);
    releaseFullscreen();
  }, [releaseFullscreen]);
  const toggleStageMode = useCallback(() => setStageMode((current) => {
    if (current) {
      releaseFullscreen();
      return false;
    }
    try {
      stageRef.current?.requestFullscreen?.()?.catch?.(() => { /* CSS fallback already applied */ });
    } catch (_) { /* CSS fallback already applied */ }
    return true;
  }), [releaseFullscreen]);
  // A browser-initiated fullscreen exit (Escape, window chrome) must return the
  // ordinary layout too, so the two never disagree.
  useEffect(() => {
    if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') return undefined;
    const sync = () => { if (!document.fullscreenElement) setStageMode(false); };
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);
  useEffect(() => releaseFullscreen, [releaseFullscreen]);
  const [localPlaybackSession, setLocalPlaybackSession] = useState(emptyDemonstrationPlaybackSession);
  const controlledSession = playbackSession && typeof setPlaybackSession === 'function';
  const session = controlledSession ? playbackSession : localPlaybackSession;
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const count = Number(session.count) || 1;
  const selectedRate = Number(session.rehearsalRate) || 1;
  const explicitFocusId = String(session.explicitFocusId || '');
  const focusRate = Number(session.focusPlaybackRateOverride);
  const effectiveRate = explicitFocusId && Number.isFinite(focusRate) && focusRate > 0
    ? focusRate
    : selectedRate;
  const replaceSession = useCallback((next) => {
    sessionRef.current = next;
    if (controlledSession) setPlaybackSession(next);
    else setLocalPlaybackSession(next);
  }, [controlledSession, setPlaybackSession]);
  const clearFocus = useCallback((focusMessage = '') => {
    const current = sessionRef.current;
    const next = withoutFocus(current, current.explicitFocusId ? focusMessage : '');
    applyLivePlaybackRate(audioRef.current, next.rehearsalRate);
    replaceSession(next);
  }, [replaceSession]);
  // Choreo #47 (re-land of !286): the boundary arrives through a prop the
  // workspace re-derives whenever ITS demonstration state moves — every
  // per-media content settle (and every pendingMediaIds mark/clear) publishes
  // new boundary state. Keying setCount on the prop made the COUNT AUTHORITY
  // a new function on every publish, re-arming the drill-loop interval effect
  // below mid-loop — the identity churn that stalled F0027 Case D's seek 3/3
  // on pipeline 7845 (job 172211). The boundary is read through a ref
  // refreshed every render instead: setCount stays referentially stable
  // across media publishes, the drill interval is never torn down by media
  // state, and the focus path below still sees the CURRENT boundary at call
  // time (the only property the old dependency actually guaranteed).
  const demonstrationBoundaryRef = useRef(demonstrationBoundary);
  demonstrationBoundaryRef.current = demonstrationBoundary;
  const setCount = useCallback((nextValue) => {
    const current = sessionRef.current;
    const nextCountValue = typeof nextValue === 'function'
      ? nextValue(Number(current.count) || 1)
      : nextValue;
    const nextCount = Number(nextCountValue);
    if (!Number.isFinite(nextCount)) return;
    if (!current.explicitFocusId) {
      replaceSession({ ...current, count: nextCount });
      return;
    }
    const map = current.focusTimingMap;
    if (!map || nextCount < map.startCount || nextCount > map.endCount) {
      const restored = withoutFocus({ ...current, count: nextCount }, 'Focus-on ended at the mapped video boundary. Selected rehearsal speed restored.');
      applyLivePlaybackRate(audioRef.current, restored.rehearsalRate);
      replaceSession(restored);
      return;
    }
    if (!current.focusRateLocked) {
      replaceSession({ ...current, count: nextCount });
      return;
    }
    const nextFocus = focusPlaybackRateForCount(
      map,
      nextCount,
      Number(view?.track?.bpm),
      current.focusRevisionId,
    );
    if (!nextFocus) {
      const restored = withoutFocus({ ...current, count: nextCount }, 'Focus-on playback could not continue at the next saved timing boundary. Video was withheld and selected rehearsal speed restored.');
      applyLivePlaybackRate(audioRef.current, restored.rehearsalRate);
      replaceSession(restored);
      return;
    }
    if (nextFocus.segmentIndex !== current.focusSegmentIndex) {
      let supported = true;
      try {
        const liveBoundary = demonstrationBoundaryRef.current;
        supported = typeof liveBoundary?.supportsPlaybackRate !== 'function'
          || liveBoundary.supportsPlaybackRate(1) === true;
      } catch (_) {
        supported = false;
      }
      const applied = supported
        ? applyExplicitFocusPlaybackRate(audioRef.current, nextFocus.rate)
        : null;
      if (applied === null) {
        const restored = withoutFocus({ ...current, count: nextCount }, 'Focus-on playback could not continue at the next saved timing boundary. Video was withheld and selected rehearsal speed restored.');
        applyLivePlaybackRate(audioRef.current, restored.rehearsalRate);
        replaceSession(restored);
        return;
      }
      replaceSession({
        ...current,
        count: nextCount,
        focusPlaybackRateOverride: applied,
        focusSegmentIndex: nextFocus.segmentIndex,
        focusMessage: `Focus-on active · selected ${Math.round(Number(current.rehearsalRate) * 100)}% · effective ${Math.round(applied * 100)}% · video 1x.`,
      });
      return;
    }
    replaceSession({ ...current, count: nextCount });
  }, [replaceSession, view?.track?.bpm]);
  const loadGenerationRef = useRef(0);
  const appliedServerViewRef = useRef(null);
  const inMemoryRef = useRef({ snapshot: null, dirty: false });
  // #25: the bounded-loop start/end pickers must default to the first and last
  // authored count of the routine when the drill control opens, instead of the
  // hardcoded { startCount: 1, endCount: 8 } below — which almost always resolved
  // straight back to the routine's own first eight-count bar (dance is authored in
  // eight-count bars), forcing the owner to scroll the End picker through the whole
  // routine every time. Applied once per routine/revision so a deliberate custom
  // pick (chooseLoopStart/chooseLoopEnd/markLoopEdge/nudgeLoopEdge) is never
  // overwritten back to the default on a later render.
  const loopDefaultAppliedForRef = useRef(null);
  const isDraftPreview = Boolean(draftSnapshot) && sourceChoice !== 'published';
  const isDraftView = isDraftPreview || view?.projectionSource === 'draft';
  useEffect(() => { inMemoryRef.current = { snapshot: draftSnapshot, dirty }; }, [draftSnapshot, dirty]);
  // T-MCRO-025 case J: no draft projection is ever carried across actor,
  // organisation or routine changes; each identity starts from a clean load.
  useEffect(() => {
    appliedServerViewRef.current = null;
    setView(null);
    setSourceChoice('auto');
    setActive(false);
    setNotice('');
    // #25: also drop any custom loop range back to the untouched placeholder so
    // the new routine gets its own fresh default (see the loopDefaultKey block
    // below) instead of carrying over the previous routine's pick — a stale
    // carry-over confused the bar-matching fallbacks into producing a value from
    // neither routine, reproduced locally as a routine switch landing on
    // "Loop 9–24" instead of the new routine's real default.
    loopDefaultAppliedForRef.current = null;
    setLoop(LOOP_PLACEHOLDER);
  }, [orgId, routineId, user?.id]);
  const load = useCallback(() => {
    const generation = loadGenerationRef.current + 1;
    loadGenerationRef.current = generation;
    setLoadFailed(false);
    if (draftSnapshot && sourceChoice !== 'published') {
      appliedServerViewRef.current = null;
      setView(draftRehearsalView(draftSnapshot, routine || { routineId }, count, dirty, viewer));
      setNotice('');
      return Promise.resolve();
    }
    const source = rehearsalRequestSource(canEdit, sourceChoice);
    const applied = appliedServerViewRef.current;
    if (applied && applied.key === `${orgId}:${routineId}` && applied.source === source && applied.view?.projectionSource === 'draft') {
      // T-MCRO-025 cases C/K: stepping through an acknowledged draft recomputes
      // the deterministic position locally instead of refetching the no-store
      // projection; the response identity stays the acknowledged one.
      const stepped = { ...applied.view, position: projectRehearsalPosition(applied.view.snapshot, count, viewer) };
      appliedServerViewRef.current = { ...applied, view: stepped };
      setView(stepped);
      setNotice('');
      return Promise.resolve();
    }
    const inMemoryAtRequest = inMemoryRef.current;
    return choreographyApi.getRehearsal(orgId, routineId, count, source).then((response) => {
      if (generation !== loadGenerationRef.current) return;
      const next = payload(response);
      if (next?.projectionSource === 'draft' && !canEdit) {
        // T-MCRO-025 cases F/J: a draft projection reaching a non-editor is
        // discarded unread; only published content may render for this actor.
        setNotice('Published rehearsal is unavailable for this routine.');
        return;
      }
      // T-MCRO-025 case C: a stale acknowledged response never replaces newer
      // in-memory edits; the in-memory snapshot always wins.
      if (next?.projectionSource === 'draft' && shouldDiscardDraftProjection(inMemoryAtRequest, inMemoryRef.current)) return;
      // Draft audio grant propagation is out of #936 scope: a draft projection
      // stays honest and never claims server-granted audio until an authorised
      // binding exists.
      const appliedView = next?.projectionSource === 'draft'
        ? { ...next, track: { ...next.track, audioAvailable: false, audioUnavailableReason: next.track?.audioUnavailableReason || 'Server-granted audio is not bound to a draft preview yet' } }
        : next;
      const previousServerView = appliedServerViewRef.current?.key === `${orgId}:${routineId}`
        ? appliedServerViewRef.current.view
        : null;
      const previousTopology = previousServerView
        ? `${previousServerView?.projectionSource}:${previousServerView?.revisionId ?? 'none'}`
        : null;
      const nextTopology = `${appliedView?.projectionSource}:${appliedView?.revisionId ?? 'none'}`;
      appliedServerViewRef.current = { key: `${orgId}:${routineId}`, source, view: appliedView };
      setView(appliedView);
      // T-MCRO-025 case G: the active count is retained when valid and clamped
      // safely when the selected source topology differs. Clamp only when the
      // applied topology actually changes (source switch or new revision);
      // stepping within one topology must never clamp, so a count stepped past
      // the end still reaches the honest end/withdrawal state (T-MCRO-027).
      if (previousTopology !== nextTopology) {
        setCount((current) => Math.min(Math.max(1, current), rehearsalCountLimit(appliedView?.snapshot)));
      }
      // T-MCRO-025 case I: only published projections may enter the published
      // rehearsal cache; draft responses are no-store and stay memory-only.
      if (appliedView?.projectionSource !== 'draft') saveRehearsalSnapshot(user?.id, orgId, routineId, appliedView);
      setNotice('');
    }).catch((error) => {
      if (generation !== loadGenerationRef.current) return undefined;
      // T-MCRO-025 case J: an explicit draft request that fails authority or
      // validation falls back only to an independently authorised published
      // projection, with no draft metadata or server draft error exposed.
      if (source === 'draft') {
        return choreographyApi.getRehearsal(orgId, routineId, count, 'published').then((response) => {
          if (generation !== loadGenerationRef.current) return;
          const fallback = payload(response);
          if (fallback?.projectionSource === 'draft') return;
          appliedServerViewRef.current = { key: `${orgId}:${routineId}`, source: 'published', view: fallback };
          setSourceChoice('published');
          setView(fallback);
          setCount((current) => Math.min(Math.max(1, current), rehearsalCountLimit(fallback?.snapshot)));
          saveRehearsalSnapshot(user?.id, orgId, routineId, fallback);
          setNotice('Draft preview is unavailable for your access. Showing the published version.');
        }).catch((fallbackError) => {
          if (generation !== loadGenerationRef.current) return;
          setNotice(rehearsalLoadFailureNotice(fallbackError));
          setLoadFailed(true);
        });
      }
      const cached = loadRehearsalSnapshot(user?.id, orgId, routineId);
      if (cached) {
        setView({ ...cached, position: projectRehearsalPosition(cached.snapshot, count, viewer) });
        setNotice(`Offline · cached revision ${cached.revisionNumber || cached.revisionId}. Audio and video are unavailable.`);
      } else { setNotice(rehearsalLoadFailureNotice(error)); setLoadFailed(true); }
      return undefined;
    });
  }, [count, dirty, draftSnapshot, orgId, routine, routineId, user?.id, canEdit, sourceChoice, viewer]);
  const loopBars = useMemo(() => rehearsalBarOptions(view?.snapshot, view?.revisionId), [view?.revisionId, view?.snapshot]);
  // #25: the bounded-loop pickers must default to the whole routine — first
  // bar's start through last bar's end — the moment its bars are known, instead
  // of leaving the hardcoded initial { 1, 8 } (LOOP_PLACEHOLDER) in place.
  //
  // This is a plain useEffect, deliberately not a render-phase setLoop call:
  // that alternative computed the right value in plain jsdom/RTL tests, but
  // under this app's actual <React.StrictMode> + dev server (the real
  // e2e/browser path) the double-render discipline left state stuck at the
  // placeholder — verified in a real Playwright run, not simulated (CI pipeline
  // 6187). A plain effect costs one extra render before the default lands
  // (component tests that assert the default value at mount need `await
  // waitFor(...)`, same as they already do for other async-loaded state in this
  // file), which is a small, well-understood cost against a pattern that broke
  // silently in production-mode React.
  //
  // loopDefaultAppliedForRef tracks *whether this key has been resolved*, not
  // *what the value is* — every setLoop call site below (not just this effect)
  // marks it via markLoopKeyResolved(), synchronously in the same interaction.
  // That distinction is load-bearing: an earlier version compared the current
  // value against the { 1, 8 } placeholder to decide whether to apply the
  // default, which is ambiguous — narrowing to the routine's own first bar is a
  // completely ordinary user choice that produces the exact same { 1, 8 }, and
  // got silently overwritten back to the full-routine default by this effect
  // finally running after that choice (reproduced locally as pipeline 6201's
  // "case C" failure, and independently by CI pipeline flake runs at roughly
  // 1-in-4 under load — a value comparison can never tell "untouched" apart
  // from "touched to the same numbers"). Because .current is dereferenced fresh
  // at the moment each effect/handler actually runs (never a stale closure),
  // whichever one runs first — a user's pick or this effect — wins outright,
  // and the loser's own guard check (ref already resolved) makes it a no-op.
  //
  // Keyed by view.revisionId ALONE, deliberately not orgId/routineId too: those
  // are props that update on the render immediately after a routine switch,
  // while `view` (and loopBars, derived from it) only catches up one render
  // later via the reset-view effect above. Mixing a prop that updates
  // immediately with state that updates a beat later produced a transient
  // "phantom" key (new routineId + stale view) that got marked resolved for a
  // combination that is never revisited, permanently starving the real key of
  // its default — reproduced locally as a routine switch carrying over the
  // previous routine's custom range instead of getting its own. loopBars is
  // entirely a function of view.snapshot/revisionId (see the useMemo above), so
  // the key for "have I defaulted for this loopBars" must be drawn from exactly
  // those same inputs, atomically. The reset-view effect above also resets
  // `loop` itself back to LOOP_PLACEHOLDER and clears this ref on every routine
  // switch, so a carried-over custom pick can never be mistaken for "already
  // resolved for the new routine" — or the reverse.
  const loopDefaultKey = view?.revisionId ?? 'none';
  const markLoopKeyResolved = () => { loopDefaultAppliedForRef.current = loopDefaultKey; };
  useEffect(() => {
    if (!loopBars.length || loopDefaultAppliedForRef.current === loopDefaultKey) return;
    markLoopKeyResolved();
    setLoop({ startCount: loopBars[0].startCount, endCount: loopBars[loopBars.length - 1].endCount });
  }, [loopBars, loopDefaultKey]);
  const selectedStartBar = loopBars.find((bar) => bar.startCount === loop.startCount) || loopBars[0] || null;
  const endBars = selectedStartBar ? loopBars.filter((bar) => bar.startCount >= selectedStartBar.startCount) : [];
  // #25: when the current `loop.endCount` does not land on an exact bar, fall back
  // to the LAST bar in range rather than the first — the first-bar fallback here,
  // combined with the hardcoded initial endCount of 8, is what silently defaulted
  // the End picker to the routine's own first bar in practice.
  const selectedEndBar = endBars.find((bar) => bar.endCount === loop.endCount) || endBars[endBars.length - 1] || null;
  useEffect(() => {
    if (active && view?.snapshot) {
      setView((current) => ({ ...current, position: projectRehearsalPosition(current.snapshot, count, viewer) }));
    } else load();
    return () => { loadGenerationRef.current += 1; };
  }, [load, active, count, viewer]);
  // Keeps `loop` resolvable to an exact authored bar pairing after loopBars
  // itself changes (e.g. a revision reload) while the numeric range stays the
  // same. Re-derives the bar pairing from the *current* committed loop via the
  // functional-updater form — not from the outer selectedStartBar/selectedEndBar
  // closure, which can be stale by the time this effect actually runs — so a
  // real interaction that landed in the meantime is read fresh, never reverted.
  // (chooseLoopStart/chooseLoopEnd/markLoopStart/markLoopEnd/nudgeLoop/
  // loopThisSection already only ever set an exact pairing, so this is a no-op
  // for all of them; it only has real work to do when loopBars itself changes.)
  useEffect(() => {
    setLoop((current) => {
      const start = loopBars.find((bar) => bar.startCount === current.startCount) || loopBars[0] || null;
      if (!start) return current;
      const ends = loopBars.filter((bar) => bar.startCount >= start.startCount);
      const end = ends.find((bar) => bar.endCount === current.endCount) || ends[ends.length - 1] || null;
      if (!end) return current;
      return (current.startCount === start.startCount && current.endCount === end.endCount)
        ? current
        : { startCount: start.startCount, endCount: end.endCount };
    });
  }, [loopBars]);
  useEffect(() => {
    if (!controlledSession) return;
    const candidateBpm = Number(view?.track?.bpm);
    const bpm = Number.isFinite(candidateBpm) && candidateBpm > 0 ? candidateBpm : null;
    const current = sessionRef.current;
    if (!Object.is(current.bpm, bpm)) replaceSession({ ...current, bpm });
  }, [controlledSession, replaceSession, view?.track?.bpm]);
  useEffect(() => { if (!active || mode !== 'count_led') return undefined; const bpm = Number(view?.track?.bpm) || 120; const timer = window.setInterval(() => setCount((current) => current >= loop.endCount ? loop.startCount : current + 1), Math.max(80, 60000 / (bpm * effectiveRate))); return () => window.clearInterval(timer); }, [active, effectiveRate, mode, loop.startCount, loop.endCount, setCount, view?.track?.bpm]);
  useEffect(() => () => { audioRef.current?.pause?.(); }, []);
  useEffect(() => () => {
    if (controlledSession) setPlaybackSession((current) => withoutFocus(current));
  }, [controlledSession, setPlaybackSession]);
  const exitDrill = () => { setActive(false); audioRef.current?.pause?.(); audioRef.current = null; clearFocus('Drill stopped. Selected rehearsal speed restored.'); setNotice('Drill stopped at the selected boundary.'); };
  const chooseLoopStart = (value) => {
    const start = loopBars.find((bar) => bar.startCount === Number(value));
    if (!start) return;
    const validEnds = loopBars.filter((bar) => bar.startCount >= start.startCount);
    const existingEnd = validEnds.find((bar) => bar.endCount === loop.endCount);
    clearFocus('Drill range changed. Selected rehearsal speed restored.');
    markLoopKeyResolved();
    setLoop({ startCount: start.startCount, endCount: (existingEnd || validEnds[0] || start).endCount });
  };
  const chooseLoopEnd = (value) => {
    const end = endBars.find((bar) => bar.startCount === Number(value));
    if (end && selectedStartBar) {
      clearFocus('Drill range changed. Selected rehearsal speed restored.');
      markLoopKeyResolved();
      setLoop({ startCount: selectedStartBar.startCount, endCount: end.endCount });
    }
  };
  // #25 "jump to start/end of routine" affordance: reuses the exact ⏮/⏭ icon
  // and mc-section-jump sizing already established for section jumps, next to
  // the picker it edits, rather than inventing new chrome. Defaulting already
  // opens the picker at the full routine, so these exist for the moment after
  // a deliberate custom pick when the owner wants one edge back at an extreme
  // without re-scrolling the select.
  const jumpLoopStartToRoutineStart = () => {
    if (!loopBars.length) return;
    chooseLoopStart(loopBars[0].startCount);
  };
  const jumpLoopEndToRoutineEnd = () => {
    if (!loopBars.length) return;
    chooseLoopEnd(loopBars[loopBars.length - 1].startCount);
  };
  const loopStartAtRoutineStart = Boolean(loopBars.length) && selectedStartBar?.startCount === loopBars[0].startCount;
  const loopEndAtRoutineEnd = Boolean(loopBars.length) && selectedEndBar?.endCount === loopBars[loopBars.length - 1].endCount;
  // boundsOverride lets a caller (e.g. loopThisSection) start the drill with an
  // exact { startCount, endCount } computed in the same synchronous handler,
  // instead of relying on setLoop + a later render's stale createDrill closure.
  const createDrill = async (boundsOverride = null) => {
    const bounds = boundsOverride || loop;
    try {
      clearFocus('New drill started. Selected rehearsal speed restored.');
      if (isDraftView) {
        setCount(Math.max(1, Number(bounds.startCount) || 1));
        setActive(true);
        setNotice(`Draft preview loop active: counts ${bounds.startCount}–${bounds.endCount}. Nothing has been published.`);
        return;
      }
      const result = payload(await choreographyApi.createDrill(orgId, routineId, { mode, playbackRate: selectedRate, ...bounds }));
      setCount(result.startCount);
      if (mode === 'music_led') {
        const grant = payload(await choreographyApi.getAudioAccess(orgId, routineId));
        let audioUrl;
        if (grant.sourceType === 'external_reference') {
          const parts = String(grant.resolverPath).split('/');
          const resolved = payload(await choreographyApi.resolveAudioGrant(orgId, parts.at(-2)));
          audioUrl = resolved.storageRef;
        } else {
          const content = await choreographyApi.getAudioContent(grant.resolverPath);
          audioUrl = URL.createObjectURL(content.data);
        }
        const audio = new Audio(audioUrl);
        if (grant.sourceType !== 'external_reference') audio.addEventListener('ended', () => URL.revokeObjectURL(audioUrl), { once: true });
        applyLivePlaybackRate(audio, selectedRate);
        const beatMs = 60000 / (Number(view?.track?.bpm) || 120);
        const offset = Number(view?.track?.firstCountOffsetMs || 0);
        const startSeconds = Math.max(0, (offset + (result.startCount - 1) * beatMs) / 1000);
        const endSeconds = Math.max(startSeconds, (offset + result.endCount * beatMs) / 1000);
        audio.currentTime = startSeconds;
        audio.addEventListener('timeupdate', () => { const nextCount = Math.floor(((audio.currentTime * 1000) - offset) / beatMs) + 1; setCount(Math.min(result.endCount, Math.max(result.startCount, nextCount))); if (audio.currentTime >= endSeconds) audio.currentTime = startSeconds; });
        audioRef.current = audio;
        await audio.play();
      }
      setActive(true);
      setNotice(`Loop active: counts ${result.startCount}–${result.endCount} at ${Math.round(result.playbackRate * 100)}%.`);
    } catch (error) {
      setActive(false);
      setNotice(`Could not start this mode: ${message(error)}`);
    }
  };
  // T-MCRO-025 case G: source selection is read-only; it never saves, publishes
  // or mutates either source, and a running loop stops at the source boundary.
  const chooseSource = (event) => {
    audioRef.current?.pause?.();
    audioRef.current = null;
    setActive(false);
    clearFocus('Rehearsal source changed. Selected rehearsal speed restored.');
    setSourceChoice(event.target.value === 'published' ? 'published' : 'draft');
  };
  const activateFocus = useCallback(async ({ candidate, classification }) => {
    const current = sessionRef.current;
    const startCount = candidate?.timingMap?.startCount;
    const rateLocked = classification?.reasons?.includes('rate') === true;
    const startFocus = rateLocked
      ? focusPlaybackRateForCount(candidate?.timingMap, startCount, Number(view?.track?.bpm), view?.revisionId)
      : { rate: Number(current.rehearsalRate), segmentIndex: null };
    let supported = Boolean(candidate?.demonstrationId && Number.isFinite(startCount)
      && startFocus && Number.isFinite(startFocus.rate) && startFocus.rate > 0);
    try {
      supported = supported && (typeof demonstrationBoundary?.supportsPlaybackRate !== 'function'
        || demonstrationBoundary.supportsPlaybackRate(rateLocked ? 1 : startFocus.rate) === true);
    } catch (_) {
      supported = false;
    }
    const applied = supported
      ? applyExplicitFocusPlaybackRate(audioRef.current, startFocus.rate)
      : null;
    if (applied === null) return false;
    const next = {
      ...current,
      count: startCount,
      explicitFocusId: candidate.demonstrationId,
      focusPlaybackRateOverride: applied,
      focusTimingMap: candidate.timingMap,
      focusAuthorityKey: mappedDemonstrationAuthorityKey(candidate),
      focusRevisionId: view?.revisionId,
      focusRateLocked: rateLocked,
      focusSegmentIndex: startFocus.segmentIndex,
      focusSequenceLabel: candidate.sequenceLabel,
      focusMessage: `Focus-on active · selected ${Math.round(Number(current.rehearsalRate) * 100)}% · effective ${Math.round(applied * 100)}%${rateLocked ? ' · video 1x' : ''}.`,
    };
    replaceSession(next);
    setNotice('');
    return true;
  }, [demonstrationBoundary, replaceSession, view?.revisionId, view?.track?.bpm]);
  const changeSelectedRate = (value) => {
    const current = sessionRef.current;
    const nextRate = applyLivePlaybackRate(audioRef.current, value);
    const next = withoutFocus(
      { ...current, rehearsalRate: nextRate },
      current.explicitFocusId ? `Selected rehearsal speed changed to ${Math.round(nextRate * 100)}%. Focus-on ended.` : '',
    );
    replaceSession(next);
    if (active) setNotice(`Speed changed to ${Math.round(nextRate * 100)}% without restarting the loop.`);
  };
  const returnFromFocus = () => clearFocus('Focus-on ended. Selected rehearsal speed restored.');
  const draftStatusLabel = isDraftPreview && dirty ? 'Draft preview · unsaved' : 'Draft preview';
  const sourceStatus = isDraftView
    ? <div className="mc-preview-status" role="status">{draftStatusLabel}</div>
    : (canEdit && view?.projectionSource === 'published'
      ? <div className="mc-preview-status mc-published-status" role="status">Published version {view.revisionNumber}</div>
      : null);
  const acknowledgedIdentity = !isDraftPreview && view?.projectionSource === 'draft'
    ? <p className="mc-source-identity">{`Acknowledged draft revision ${view.revisionNumber ?? '—'} · source hash ${view.sourceHash || '—'}`}</p>
    : null;
  const publishedRevisionNumber = routine?.revisions?.find((revision) => revision.revisionId === routine?.publishedRevisionId)?.revisionNumber
    || (view?.projectionSource === 'published' ? view.revisionNumber : null);
  const sourceSelector = canEdit && routine?.draftRevisionId && routine?.publishedRevisionId
    ? <label className="mc-field mc-source-choice"><span>Rehearsal source</span><select aria-label="Rehearsal source" value={sourceChoice === 'auto' ? 'draft' : sourceChoice} onChange={chooseSource}><option value="draft">Draft preview</option><option value="published">{publishedRevisionNumber ? `Published version ${publishedRevisionNumber}` : 'Published version'}</option></select></label>
    : null;
  // MCRO001-F0068 / T-MCRO-068 cases A/D (WebUI #51, D005 §4.1 (a)/(c)): the
  // surface never waits on GET …/rehearsal. Until the projection arrives the
  // shell renders at once from what the workspace already holds (routine title,
  // source choice, the way back to Canvas) with an honest "—" count and inert
  // transport — nothing invented — and a quiet pending note. A failure is a
  // retriable product-language statement, never the transport error string.
  if (!view) {
    return <section className="mc-rehearsal mc-rehearsal-pending" aria-busy={!loadFailed}>{sourceSelector}
      <section className="mc-panel mc-rehearsal-shell" aria-labelledby="mc-rehearsal-shell-title">
        <span className="mc-kicker">Rehearsal</span><h2 id="mc-rehearsal-shell-title">{routine?.title || 'Rehearsal'}</h2>
        <div className="mc-rehearsal-shell-transport" role="group" aria-label="Rehearsal transport"><span className="mc-rehearsal-shell-count" aria-label="Current count">Count —</span><button type="button" disabled>Play</button></div>
        {loadFailed
          ? <div className="mc-alert" role="alert">{notice || REHEARSAL_UNAVAILABLE_NOTICE}<button type="button" onClick={() => { setNotice(''); load(); }}>Try again</button></div>
          : <p className="mc-muted mc-rehearsal-pending-note" aria-live="polite">{notice || 'Loading rehearsal…'}</p>}
        <button type="button" className="mc-quiet" onClick={() => onReturnCanvas?.()}>Back to Canvas</button>
      </section>
    </section>;
  }
  if (isDraftView && !hasRehearsalContent(view.snapshot)) return <section className="mc-rehearsal">{sourceStatus}{acknowledgedIdentity}{sourceSelector}<section className="mc-panel mc-empty-draft" aria-labelledby="mc-empty-draft-title"><span className="mc-kicker">Private rehearsal</span><h2 id="mc-empty-draft-title">This draft has no rehearsal content yet</h2><p>Return to Canvas to add a section, movement block or cue. No timing, media or publication state has been inferred.</p><button type="button" className="mc-primary" onClick={() => { clearFocus(); onReturnCanvas?.(); }}>Back to Canvas</button></section></section>;
  const position = projectRehearsalPosition(view.snapshot, count, viewer);
  const nowCard = rehearsalCardContent(position, 'now');
  const nextCard = rehearsalCardContent(position, 'next');
  const matchingMedia = (view.media || []).filter((item) => (!item.startCount || count >= item.startCount) && (!item.endCount || count <= item.endCount));
  const openMedia = async (mediaId) => { try { const access = payload(await choreographyApi.getMediaAccess(orgId, mediaId)); if (/^https:\/\//i.test(access.storageRef || '')) window.open(access.storageRef, '_blank', 'noopener,noreferrer'); else { const content = await choreographyApi.getMediaContent(orgId, mediaId); const url = URL.createObjectURL(content.data); window.open(url, '_blank', 'noopener,noreferrer'); window.setTimeout(() => URL.revokeObjectURL(url), 60000); } } catch (error) { setNotice(message(error)); } };
  // T-MCRO-007/D006 §3: a plain "play the current loop range" without first
  // requiring Start/End bar selection. Reuses the existing createDrill/exitDrill
  // (active/loop) machinery verbatim — no second playback engine. The explicit
  // bar selectors remain for deliberate range choice; this is only an easier
  // default path alongside them.
  const togglePlayPause = () => { if (active) exitDrill(); else createDrill(null); };
  // Easy bar/section targeting: jump directly to a saved bar's start count using
  // the same setCount already used by Previous/Next, so all focus-session
  // boundary logic in setCount is reused rather than reimplemented.
  const jumpToBar = (value) => {
    const bar = loopBars.find((candidate) => candidate.startCount === Number(value));
    if (bar) setCount(bar.startCount);
  };
  // One-click "Loop this section": sets loop bounds to the current section's
  // full bar range (from already-computed loopBars/position.currentSection) and
  // starts the existing bounded-loop drill, instead of requiring the dropdowns
  // to be walked to matching entries by hand.
  const currentSectionId = position.currentSection?.id;
  const currentSectionBars = currentSectionId ? loopBars.filter((bar) => bar.sectionId === currentSectionId) : [];
  const loopThisSection = () => {
    if (!currentSectionBars.length) return;
    const startCount = Math.min(...currentSectionBars.map((bar) => bar.startCount));
    const endCount = Math.max(...currentSectionBars.map((bar) => bar.endCount));
    const bounds = { startCount, endCount };
    clearFocus('Drill range changed. Selected rehearsal speed restored.');
    markLoopKeyResolved();
    setLoop(bounds);
    createDrill(bounds);
  };
  // Arrow-key transport: ArrowLeft/ArrowRight step one count (same setCount as
  // Previous/Next); Shift+ArrowLeft/ArrowRight step a whole bar. Ignored while
  // focus is inside a text input/select so existing controls are unaffected.
  //
  // MCRO001-F0036/T-MCRO-036: additive video-editor-style shuttle aliases layered
  // on top of the exact same handlers — none of these introduce a second count,
  // rate or playback path. J/L alias ArrowLeft/ArrowRight; Shift+J/L alias
  // Shift+ArrowLeft/ArrowRight; K aliases Space; ','/'.' nudge the existing
  // 0.50-1.50 selected-rate control by its own step; '0' resets it to 100%;
  // Home/End jump to the current loop's start/end count through the existing
  // setCount path. All ignored while focus is inside a text input/select/textarea.
  const stepCount = (direction, wholeBar) => {
    const walkthrough = mode === 'walkthrough';
    const step = wholeBar ? 8 : 1;
    if (direction > 0) setCount(walkthrough && !wholeBar ? nextRehearsalCount(position, count) : count + step);
    else setCount(walkthrough && !wholeBar ? previousRehearsalCount(position, count) : Math.max(1, count - step));
  };
  // MCRO001-F0038/T-MCRO-038 cases A-D: A/B loop marking and single-bar edge nudges.
  // Every result is resolved by rehearsalStudio.js against the same routine-wide
  // authored bar vocabulary the Start/End 8-beat bar selectors use, so the selectors
  // and the A/B controls can never disagree, and a refused operation (null) leaves
  // the previous range completely untouched instead of clamping silently.
  const applyLoopRange = (next) => {
    if (!next) return;
    clearFocus('Drill range changed. Selected rehearsal speed restored.');
    markLoopKeyResolved();
    setLoop(next);
  };
  const markLoopStart = () => applyLoopRange(markLoopEdge(loopBars, loop, 'start', count));
  const markLoopEnd = () => applyLoopRange(markLoopEdge(loopBars, loop, 'end', count));
  const nudgeLoop = (edge, direction) => applyLoopRange(nudgeLoopEdge(loopBars, loop, edge, direction));
  const loopNudgeDisabled = (edge, direction) => nudgeLoopEdge(loopBars, loop, edge, direction) === null;
  const canMarkLoopStart = markLoopEdge(loopBars, loop, 'start', count) !== null;
  const canMarkLoopEnd = markLoopEdge(loopBars, loop, 'end', count) !== null;
  // MCRO001-F0038/T-MCRO-038 case E: section jumps move only the count, through the
  // existing setCount path, and are disabled — not silently inert — with nowhere to go.
  const snapshotSections = view?.snapshot?.sections || [];
  const previousSectionCount = sectionJumpTarget(snapshotSections, count, -1);
  const nextSectionCount = sectionJumpTarget(snapshotSections, count, 1);
  const jumpSection = (direction) => {
    const target = direction > 0 ? nextSectionCount : previousSectionCount;
    if (target === null) return;
    setCount(target);
  };
  const handleTransportKeyDown = (event) => {
    const tag = event.target?.tagName;
    if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
    // MCRO001-F0037 cases B/D: single-key aliases for the mirror and stage toggles,
    // and Escape as the non-fullscreen stage exit (a real fullscreen exit arrives via
    // the fullscreenchange listener instead). MCRO001-F0038 case H: A/B marking and
    // section jumps alias their own buttons exactly. All under the existing guard.
    if (event.key === 'Escape') {
      if (!stageMode) return;
      event.preventDefault();
      exitStageMode();
      return;
    }
    if (event.key === 'm' || event.key === 'M') {
      event.preventDefault();
      toggleMirror();
      return;
    }
    if (event.key === 'f' || event.key === 'F') {
      event.preventDefault();
      toggleStageMode();
      return;
    }
    if (event.key === 'a' || event.key === 'A') {
      event.preventDefault();
      markLoopStart();
      return;
    }
    if (event.key === 'b' || event.key === 'B') {
      event.preventDefault();
      markLoopEnd();
      return;
    }
    if (event.key === '[') {
      event.preventDefault();
      jumpSection(-1);
      return;
    }
    if (event.key === ']') {
      event.preventDefault();
      jumpSection(1);
      return;
    }
    if (event.key === ' ' || event.code === 'Space' || event.key === 'k' || event.key === 'K') {
      event.preventDefault();
      togglePlayPause();
      return;
    }
    if (event.key === 'ArrowRight' || event.key === 'l' || event.key === 'L') {
      event.preventDefault();
      stepCount(1, event.shiftKey);
    } else if (event.key === 'ArrowLeft' || event.key === 'j' || event.key === 'J') {
      event.preventDefault();
      stepCount(-1, event.shiftKey);
    } else if (event.key === ',' || event.key === '<') {
      event.preventDefault();
      changeSelectedRate(Math.max(0.5, Number((selectedRate - 0.05).toFixed(2))));
    } else if (event.key === '.' || event.key === '>') {
      event.preventDefault();
      changeSelectedRate(Math.min(1.5, Number((selectedRate + 0.05).toFixed(2))));
    } else if (event.key === '0') {
      event.preventDefault();
      changeSelectedRate(1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setCount(loop.startCount);
    } else if (event.key === 'End') {
      event.preventDefault();
      setCount(loop.endCount);
    }
  };
  // MCRO001-F0036/T-MCRO-036: the scrub timeline's min/max span the current loop
  // range, widened to include the active mapped demonstration's range (if any) and
  // the current count itself, so the thumb is always representable on the track.
  const mappedRangeForScrub = videoProjection.visible && videoProjection.mappedRange
    ? videoProjection.mappedRange
    : null;
  const scrubMin = Math.min(loop.startCount, mappedRangeForScrub?.startCount ?? loop.startCount, count);
  const scrubMax = Math.max(loop.endCount, mappedRangeForScrub?.endCount ?? loop.endCount, count);
  // Case F: the no-map hint depends only on the live boundary's own report, never a
  // second read, and stays absent until the backdrop has actually reported in.
  const showNoMapHint = videoProjection.hasCandidates === false;
  // MCRO001-F0038/T-MCRO-038 case G: observational rate-versus-threshold readout. It
  // reports the rate F0027 sends upward against the threshold F0029 actually applies,
  // and returns '' the instant the backdrop is not visible, so it never goes stale.
  const effectiveVideoRateLabel = videoRateReadout(videoProjection, focusOnRateThreshold);
  const positionLabel = rehearsalCountLabel(position);
  return <section
    ref={stageRef}
    className={`mc-rehearsal${stageMode ? ' stage-mode' : ''}`}
    onKeyDown={handleTransportKeyDown}
  >
    {!stageMode && sourceStatus}{!stageMode && acknowledgedIdentity}{!stageMode && sourceSelector}
    <div className="mc-performance">
      {!stageMode && <header><span>{view.track?.title}</span><div><strong>{position.sectionTitle || 'Between sections'}</strong>{position.sequenceTitle && <small>{position.sequenceTitle}</small>}</div></header>}
      <MappedDemonstrationBackdrop
        boundary={demonstrationBoundary}
        organisationId={orgId}
        routineId={routineId}
        revisionId={view.revisionId}
        count={count}
        bpm={view.track?.bpm}
        rehearsalRate={effectiveRate}
        selectedRehearsalRate={selectedRate}
        playing={active}
        explicitFocusId={explicitFocusId}
        explicitFocusAuthorityKey={session.focusAuthorityKey}
        focusOnRateThreshold={focusOnRateThreshold}
        onActivateFocus={activateFocus}
        onFocusUnavailable={() => clearFocus('Focus-on authority or media is no longer available. Video was withheld and selected rehearsal speed restored.')}
        onProjectionChange={onProjectionChange}
        mirrored={mirrored}
        stagePositionLabel={positionLabel}
        onStageSeek={setCount}
        stageSeekBounds={{ min: scrubMin, max: scrubMax }}
      >{/* MCRO001-F0064 (T-MCRO-064): the current section's numbered-dancer
           stage rides under the big count, projected at the live absolute
           count; absent when the section has no formations at all. */}
        <div className="mc-big-count" aria-label={`Count ${position.displayCount}`}>{position.displayCount}
          {position.formations?.length > 0 && <FormationStage formations={position.formations} count={position.absoluteCount} className="mc-formation-stage-rehearsal" highlight={viewer} />}
        </div></MappedDemonstrationBackdrop>
      {/* #28: the big "Count N" readout is the intentional cyclic beat-within-bar
          (1..8), not the routine's absolute position — that is correct for
          dancers counting a phrase, so it stays as-is. What was missing is any
          unambiguous reference for *where* that beat sits: a loop starting at
          absolute count 57 reads as "1" with nothing nearby to say otherwise.
          This readout gives the real absolute count always, and — while the
          bounded loop is actually engaged — the loop's own start/end next to
          it, so the owner can see loop start, loop end and the live playhead
          together without hunting in the separate drill-loop control cluster. */}
      <div className="mc-rehearsal-status"><span className="mc-rehearsal-status-now">{positionLabel}</span><span className="mc-rehearsal-status-absolute">{`Count ${position.absoluteCount}`}{active ? ` · Loop ${loop.startCount}–${loop.endCount}` : ''}</span>{active && (nextCard.text) && <span className="mc-rehearsal-status-next">Next · {nextCard.text}</span>}</div>
      <RehearsalScrubTimeline min={scrubMin} max={scrubMax} count={count} mappedRange={mappedRangeForScrub} loopRange={loop} onSeek={setCount} label="Rehearsal position" />
      {showNoMapHint && !stageMode && <p className="mc-no-map-hint">No demonstration video is mapped to this routine yet. <button type="button" className="mc-quiet" onClick={() => { clearFocus(); onReturnCanvas?.(); }}>Add one from Canvas</button></p>}
      {!stageMode && <div className="mc-rehearsal-cards">
        <section className="mc-rehearsal-bar mc-now-card" aria-label="Now choreography" aria-live="polite"><span>Now</span>{nowCard.barLabel && <em>{nowCard.barLabel}{nowCard.barProgress ? ` ${nowCard.barProgress}` : ''}</em>}<strong>{nowCard.text}</strong>{nowCard.detail && <p>{nowCard.detail}</p>}</section>
        <section className="mc-rehearsal-bar mc-next-card" aria-label="Next choreography"><span>Next</span>{nextCard.barLabel && <em>{nextCard.barLabel}</em>}<strong>{nextCard.text}</strong>{nextCard.detail && <p>{nextCard.detail}</p>}</section>
      </div>}
      {!stageMode && matchingMedia.length > 0 && <div className="mc-matching-media"><span>Examples at this count</span>{matchingMedia.map((item) => <button key={item.mediaId} onClick={() => openMedia(item.mediaId)}>▶ {item.title}<small>{item.teachingPoint}</small></button>)}</div>}
      <div className="mc-transport">
        <div className="mc-transport-primary">
          <button type="button" className="mc-transport-playpause" aria-pressed={active} onClick={togglePlayPause}>{active ? '⏸ Pause' : '▶ Play'}</button>
          <button type="button" className="mc-section-jump" aria-label="Previous section" title="Previous section ( [ )" disabled={previousSectionCount === null} onClick={() => jumpSection(-1)}>⏮</button>
          <div className="mc-stepper">
            <button onClick={() => setCount(mode === 'walkthrough' ? previousRehearsalCount(position, count) : Math.max(1, count - 1))}>← Previous</button>
            <button onClick={() => setCount(mode === 'walkthrough' ? nextRehearsalCount(position, count) : count + 1)}>Next →</button>
          </div>
          <button type="button" className="mc-section-jump" aria-label="Next section" title="Next section ( ] )" disabled={nextSectionCount === null} onClick={() => jumpSection(1)}>⏭</button>
          <div className="mc-stage-toggles">
            <button type="button" className="mc-stage-toggle" aria-label="Mirror video" title="Mirror video ( M )" aria-pressed={mirrored} onClick={toggleMirror}>Mirror</button>
            <button type="button" className="mc-stage-toggle" aria-label="Full stage view" title="Full stage view ( F )" aria-pressed={stageMode} onClick={toggleStageMode}>Stage</button>
          </div>
        </div>
        <div className="mc-transport-drill">
          {/* Each edge is one non-breaking cluster so the group wraps between the A
              and B clusters in a narrow rehearsal column instead of overflowing the
              performance panel — a real overflow the browser T-MCRO-037 run caught. */}
          <div className="mc-loop-marks" role="group" aria-label="Drill loop">
            <span className="mc-loop-edge">
              <button type="button" aria-label="Move loop start back one bar" disabled={loopNudgeDisabled('start', -1)} onClick={() => nudgeLoop('start', -1)}>‹</button>
              <button type="button" className="mc-loop-mark" aria-label="Set loop start to this bar" title="Set loop start to this bar ( A )" disabled={!canMarkLoopStart} onClick={markLoopStart}>Set A</button>
              <button type="button" aria-label="Move loop start forward one bar" disabled={loopNudgeDisabled('start', 1)} onClick={() => nudgeLoop('start', 1)}>›</button>
            </span>
            <span className="mc-loop-edge">
              <button type="button" aria-label="Move loop end back one bar" disabled={loopNudgeDisabled('end', -1)} onClick={() => nudgeLoop('end', -1)}>‹</button>
              <button type="button" className="mc-loop-mark" aria-label="Set loop end to this bar" title="Set loop end to this bar ( B )" disabled={!canMarkLoopEnd} onClick={markLoopEnd}>Set B</button>
              <button type="button" aria-label="Move loop end forward one bar" disabled={loopNudgeDisabled('end', 1)} onClick={() => nudgeLoop('end', 1)}>›</button>
            </span>
            {/* A polite live region rather than role="status": the range is announced
                when A/B marking or a nudge changes it, without claiming a second
                page-level status landmark alongside the existing drill notices. */}
            <span className="mc-loop-range" aria-live="polite">{`Loop ${loop.startCount}–${loop.endCount}`}</span>
          </div>
          <div className="mc-speed-presets" role="group" aria-label="Rehearsal speed presets">
            {REHEARSAL_SPEED_PRESETS.map((preset) => <button
              type="button"
              key={`preset-${preset}`}
              aria-label={`Rehearsal speed ${Math.round(preset * 100)}%`}
              aria-pressed={Math.abs(selectedRate - preset) < 0.001}
              onClick={() => changeSelectedRate(preset)}
            >{`${Math.round(preset * 100)}%`}</button>)}
          </div>
          <label className="mc-field mc-jump-to-bar"><span>Jump to bar</span><select aria-label="Jump to 8-beat bar" value="" onChange={(event) => jumpToBar(event.target.value)}><option value="" disabled>Choose a bar…</option>{loopBars.map((bar) => <option value={bar.startCount} key={`jump-${bar.startCount}`}>{bar.label}</option>)}</select></label>
          <button type="button" className="mc-quiet mc-loop-section" onClick={loopThisSection} disabled={!currentSectionBars.length}>Loop this section</button>
        </div>
        {effectiveVideoRateLabel && <p className="mc-effective-video-rate" role="status">{effectiveVideoRateLabel}</p>}
      </div>
      {stageMode && (session.focusMessage || notice) && <div className="mc-notice mc-stage-notice" role="status">{session.focusMessage || notice}</div>}
    </div>
    {!stageMode && <aside className="mc-panel mc-drill"><span className="mc-kicker">Phone drill</span><h2>Repeat with intention</h2><label className="mc-field"><span>Mode</span><select value={mode} disabled={active} onChange={(event) => { clearFocus('Drill mode changed. Selected rehearsal speed restored.'); setMode(event.target.value); }}><option value="music_led" disabled={!view.track?.audioAvailable}>Music-led</option><option value="count_led">Count-led</option><option value="walkthrough">Walkthrough</option></select></label><label className="mc-field"><span>Speed · {Math.round(selectedRate * 100)}%</span><input aria-label="Rehearsal speed" type="range" min="0.5" max="1.5" step="0.05" value={selectedRate} onChange={(event) => changeSelectedRate(event.target.value)} /></label>{explicitFocusId && <div className="mc-focus-session" role="status"><strong>Focus-on · {session.focusSequenceLabel}</strong><span>Selected speed {Math.round(selectedRate * 100)}% · effective speed {Math.round(effectiveRate * 100)}%{session.focusRateLocked ? ' · video 1x' : ''}</span><button type="button" className="mc-primary mc-return-from-focus" onClick={returnFromFocus}>Return to selected rehearsal speed</button></div>}<div className="mc-inline"><label className="mc-field"><span>Start 8-beat bar</span><div className="mc-inline"><select aria-label="Start 8-beat bar" value={selectedStartBar?.startCount || ''} onChange={(event) => chooseLoopStart(event.target.value)}>{loopBars.map((bar) => <option value={bar.startCount} title={bar.fullText} aria-label={`${bar.barIndex} · ${bar.fullText}`} key={`start-${bar.startCount}`}>{bar.label}</option>)}</select><button type="button" className="mc-section-jump" aria-label="Jump loop start to routine start" title="Jump loop start to routine start" disabled={loopStartAtRoutineStart} onClick={jumpLoopStartToRoutineStart}>⏮</button></div></label><label className="mc-field"><span>End 8-beat bar</span><div className="mc-inline"><select aria-label="End 8-beat bar" value={selectedEndBar?.startCount || ''} onChange={(event) => chooseLoopEnd(event.target.value)}>{endBars.map((bar) => <option value={bar.startCount} title={bar.fullText} aria-label={`${bar.barIndex} · ${bar.fullText}`} key={`end-${bar.startCount}`}>{bar.label}</option>)}</select><button type="button" className="mc-section-jump" aria-label="Jump loop end to routine end" title="Jump loop end to routine end" disabled={loopEndAtRoutineEnd} onClick={jumpLoopEndToRoutineEnd}>⏭</button></div></label></div>{active ? <button className="mc-publish" onClick={exitDrill}>Exit loop</button> : <button className="mc-primary" onClick={() => createDrill(null)}>Start bounded loop</button>}<p className="mc-muted">{isDraftView ? 'Private draft preview. Counts and walkthrough are available without publishing.' : view.track?.audioAvailable ? 'Pitch preservation is requested from this browser. Speed can change while the loop is running.' : 'No authorised audio. Count-led speed can change while the loop is running.'}</p>{session.focusMessage && <div className="mc-notice" role="status">{session.focusMessage}</div>}{notice && <div className="mc-notice" role="status">{notice}</div>}</aside>}
  </section>;
}

// MCRO001-F0030 / T-MCRO-030: claim -> chunked PATCH loop -> finalize -> poll
// lifecycle -> Core complete. Progress is phase text + 0..100% for the active
// upload; a mid-upload token expiry (VIDEO_INGEST_TOKEN_INVALID/AUTH_REQUIRED)
// or a validation-failed lifecycle both surface a clear, truthful error and
// require the user to restart (no silent retry-forever, per D007 §3).
const VIDEO_INGEST_RESTART_CODES = new Set(['VIDEO_INGEST_TOKEN_INVALID', 'VIDEO_INGEST_AUTH_REQUIRED', 'VIDEO_INGEST_EXPIRED']);
const videoIngestPhaseLabel = (lifecycle) => ({
  admitted: 'Starting upload…', uploading: 'Uploading', validating: 'Validating…', processing: 'Processing…', ready: 'Ready',
}[lifecycle] || 'Uploading');
// MCRO001-D007: a slow/timed-out finalize is not a failure — the ingest
// service keeps transcoding after the client's connection drops (see
// videoIngestApi.js's isFinalizeTransient citation of the service source),
// so the UI shows a truthful long-running "processing" state and keeps
// polling instead of surfacing the finalize transport error.

// MCRO001-F0051 / T-MCRO-051 — Draggable canvas/library resize divider with
// squeeze-collapse. Owner request 2026-08-06: the "On this canvas" pane had no
// right-side resize (the split was a fixed CSS flex row — CanvasMediaPanel
// flex: 0 0 18rem beside the library's flex: 1 1 auto, MusicChoreography.css
// ~L551/574 — so the only sizing control was F0034's binary collapse button).
// This adds a role="separator" divider the user drags to trade width between
// the two panes; dragging the library below ~one tile column snaps it closed
// (extending F0034's collapse contract). All widths are applied as inline
// flex-basis so the behaviour is assertable without a real layout engine
// (JSDOM computes none). This layer never mutates canvas/library membership
// (inherits the F0021/F0034 mutation-free boundary).
//
// Threshold derivation (Case B): the library grid is `.mc-grid`
// `grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr))` with
// `gap: var(--mc-lib-space-3)` (1.1rem) and the library flex owns
// `--mc-lib-space-4` (1.6rem) of internal rhythm. One tile column therefore
// needs ~12rem of tile plus ~2rem of library chrome ≈ 14rem. Below that the
// library cannot render even one full tile column, so it collapses. Values are
// in rem-derived px (LIBRARY_TILE_REM * REM_PX) rather than a bare magic number
// so a tile-min change stays honest.
const LIBRARY_RESIZE_REM_PX = 16;               // 1rem in px (app default root size)
const LIBRARY_TILE_MIN_REM = 12;                // .mc-grid minmax(12rem, 1fr) tile min
const LIBRARY_CHROME_REM = 2;                   // gap + library internal rhythm allowance
// One-tile-column collapse threshold in px. Dragging the library narrower than
// this snaps it closed (Case B).
const LIBRARY_COLLAPSE_THRESHOLD_PX = (LIBRARY_TILE_MIN_REM + LIBRARY_CHROME_REM) * LIBRARY_RESIZE_REM_PX; // 224px
// Default library width when first shown / reopened (Case C): two tile columns
// so the library is immediately useful, not a sliver.
const LIBRARY_DEFAULT_WIDTH_PX = 2 * LIBRARY_TILE_MIN_REM * LIBRARY_RESIZE_REM_PX + LIBRARY_CHROME_REM * LIBRARY_RESIZE_REM_PX; // 416px
// Constraints (Case D): the canvas never shrinks below this, and the library
// never grows past this, so neither pane can consume the whole row.
const CANVAS_MIN_WIDTH_PX = 18 * LIBRARY_RESIZE_REM_PX;   // 288px — the canvas pane's original flex-basis
const LIBRARY_MAX_WIDTH_PX = 44 * LIBRARY_RESIZE_REM_PX;  // 704px
const LIBRARY_KEYBOARD_STEP_PX = 24;
const LIBRARY_WIDTH_STORAGE_KEY = 'mc.libraryPaneWidth';

const readStoredLibraryWidth = () => {
  try {
    const raw = Number(localStorage.getItem(LIBRARY_WIDTH_STORAGE_KEY));
    if (Number.isFinite(raw) && raw >= LIBRARY_COLLAPSE_THRESHOLD_PX) {
      return Math.min(raw, LIBRARY_MAX_WIDTH_PX);
    }
  } catch { /* Storage is optional. */ }
  return LIBRARY_DEFAULT_WIDTH_PX;
};

// Clamp a proposed library width to the library max only; the caller decides
// whether a below-threshold value collapses (drag) or floors (keyboard at min).
const clampLibraryWidth = (px) => Math.max(0, Math.min(px, LIBRARY_MAX_WIDTH_PX));

// The draggable divider. `role="separator"`, keyboard-operable, focus-visible.
// `valueNow`/min/max report the LIBRARY width so assistive tech announces what
// the handle actually controls (Case G).
function LibraryPaneResizeDivider({ widthPx, onResizeBy, onResizeTo, onCollapse }) {
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const endDrag = useCallback(() => {
    draggingRef.current = false;
    try { document.body.style.removeProperty('user-select'); } catch { /* jsdom */ }
  }, []);

  const onPointerMove = useCallback((clientX) => {
    if (!draggingRef.current) return;
    // Divider sits between canvas (left) and library (right). Dragging LEFT
    // (clientX decreases) GROWS the library; RIGHT shrinks it (Case A).
    const delta = startXRef.current - clientX;
    onResizeBy(delta, startWidthRef.current);
  }, [onResizeBy]);

  useEffect(() => {
    const move = (event) => onPointerMove(event.clientX);
    const touchMove = (event) => { if (event.touches && event.touches[0]) onPointerMove(event.touches[0].clientX); };
    const up = () => endDrag();
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', touchMove, { passive: true });
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', touchMove);
      window.removeEventListener('touchend', up);
    };
  }, [onPointerMove, endDrag]);

  const beginDrag = (clientX) => {
    draggingRef.current = true;
    startXRef.current = clientX;
    startWidthRef.current = widthPx;
    try { document.body.style.userSelect = 'none'; } catch { /* jsdom */ }
  };

  const onKeyDown = (event) => {
    // Arrow LEFT grows the library, RIGHT shrinks it — same sense as the drag
    // (Case F). Home/End reach min(collapse)/max.
    if (event.key === 'ArrowLeft') { event.preventDefault(); onResizeTo(widthPx + LIBRARY_KEYBOARD_STEP_PX); }
    else if (event.key === 'ArrowRight') { event.preventDefault(); onResizeTo(widthPx - LIBRARY_KEYBOARD_STEP_PX); }
    else if (event.key === 'Home') { event.preventDefault(); onResizeTo(LIBRARY_MAX_WIDTH_PX); }
    else if (event.key === 'End') { event.preventDefault(); onCollapse(); }
  };

  return (
    <div
      className="mc-video-library-divider"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize video library"
      aria-valuemin={0}
      aria-valuemax={Math.round(LIBRARY_MAX_WIDTH_PX)}
      aria-valuenow={Math.round(widthPx)}
      tabIndex={0}
      onMouseDown={(event) => { event.preventDefault(); beginDrag(event.clientX); }}
      onTouchStart={(event) => { if (event.touches && event.touches[0]) beginDrag(event.touches[0].clientX); }}
      onKeyDown={onKeyDown}
    >
      <span className="mc-video-library-divider-grip" aria-hidden="true" />
    </div>
  );
}

export function MediaLibrary({ orgId, routineId, revisionContext, snapshot, canUpload, canEditTiming = false, getDemonstrationEligibility, onSaveTimingMap, trackColours = {}, demonstrationRecordsReady = false, existingDemonstrationMediaIds = new Set(), canManageFolders = false, pageCapabilities }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]); const [itemsStatus, setItemsStatus] = useState('idle'); const [itemsError, setItemsError] = useState(''); const [q] = useState(''); const [showUpload, setShowUpload] = useState(false); const [form, setForm] = useState({ title: '', storageRef: '', storageType: 'approved_external_reference', kind: 'example_video', startCount: 1, endCount: 8, teachingPoint: '', tags: '', file: null }); const [notice, setNotice] = useState('');
  const [ingestProgress, setIngestProgress] = useState(null); // { phase, percent } while a file upload is in flight

  // MCRO001-F0053 / T-MCRO-053 (MCRO001-D011 §5.3): the currently open folder.
  // `openFolders` is the FULL navigation path the user actually clicked
  // through, root EXCLUDED (root = []) — see MediaLibraryGrid's
  // FolderBreadcrumb comment for why this is built purely from client-side
  // navigation rather than a server ancestor walk (there is no GET-folder-by-
  // id endpoint in the merged backend contract to resolve an arbitrary
  // folder's ancestors from scratch). Resets to root on remount (tab switch),
  // which D011 §5.2 explicitly allows for v1.
  const [openFolders, setOpenFolders] = useState([]); // [{ folderId, name }, ...]
  const currentFolderId = openFolders.length > 0 ? openFolders[openFolders.length - 1].folderId : null;
  const breadcrumb = useMemo(() => [{ folderId: null, name: 'Root' }, ...openFolders], [openFolders]);
  // A single handler covers both a folder TILE click (a fresh child, never
  // already in the path -> appended) and a breadcrumb segment click (already
  // in the path -> truncates to it, root = index 0 = []).
  const openFolder = useCallback((folderId, name) => {
    if (!folderId) { setOpenFolders([]); return; }
    setOpenFolders((prev) => {
      const existingIndex = prev.findIndex((entry) => entry.folderId === folderId);
      return existingIndex !== -1 ? prev.slice(0, existingIndex + 1) : [...prev, { folderId, name }];
    });
  }, []);

  // MCRO001-F0159 (MCRO001-D021 §1.1): "Your Video Library" is PERSONAL. This
  // load consumes the personal scope — the choreo API returns only the viewer's
  // own uploads here, and no organisation role or capability widens it — so the
  // UI's contract is: render exactly what this one call returns, and NEVER
  // re-widen an empty result into an org-wide list (§1.3: an empty personal
  // library is the honest empty state, rendered by MediaLibraryGrid, never an
  // error and never an org-wide fallback). Sharing happens only through a
  // canvas: the distinct "On this canvas" pane (canvasItems below) is the
  // link-scoped collection; see CanvasMediaPanel.
  //
  // STRUCK (D021 supersedes): the owner correction 2026-07-24 recorded here
  // before F0159 — "Your Video Library" is EVERYTHING the user has access to,
  // org-wide. That correction addressed filtering by the open routine; it did
  // not decide cross-user visibility, which D021 does. The never-routine-
  // filtered half of it is unchanged and still holds: no routineId is ever
  // sent here.
  //
  // MCRO001-F0053 / T-MCRO-053 Case H (MCRO001-D011 §5.1's own "implementation
  // clarification"): `folderId` is ALWAYS sent, never omitted — '' for root, an
  // id for a specific folder — so this Library view is always folder-scoped,
  // never the legacy unfiltered flat list this route also still supports.
  const mediaRequest = useRef(0);
  // The same organisation/folder can be viewed by different authenticated
  // owners. Include the identity in the request scope so a late response from
  // the previous account cannot populate the next account's personal library.
  const mediaContext = JSON.stringify([orgId, user?.uuid || null, user?.id || null, currentFolderId || null, q]);
  const [itemsContext, setItemsContext] = useState('');
  const itemsContextCurrent = itemsContext === mediaContext;
  const load = useCallback(async () => {
    const request = ++mediaRequest.current;
    setItemsContext(mediaContext);
    setItems([]);
    setItemsStatus('loading');
    setItemsError('');
    try {
      const response = await choreographyApi.searchMedia(orgId, { q: q || undefined, folderId: currentFolderId || '' });
      if (mediaRequest.current !== request) return;
      setItems(listFrom(payload(response), ['media', 'items']));
      setItemsStatus('ready');
    } catch (error) {
      if (mediaRequest.current !== request) return;
      setItemsError(message(error));
      setItemsStatus('error');
    }
  }, [orgId, q, currentFolderId, mediaContext]);
  useEffect(() => {
    load();
    return () => { mediaRequest.current += 1; };
  }, [load]);

  // MCRO001-F0053 / T-MCRO-053: the live direct child folders of the currently
  // open location. `listFolders`'s OWN sentinel convention is different from
  // searchMedia's above (D011 §5.1): omitted `parentFolderId` means root here,
  // there is no '' sentinel for it — never pass '' to this one.
  const [folders, setFolders] = useState([]);
  const [foldersStatus, setFoldersStatus] = useState('idle');
  const [foldersError, setFoldersError] = useState('');
  const folderRequest = useRef(0);
  const folderContext = JSON.stringify([orgId, user?.uuid || null, user?.id || null, currentFolderId || null]);
  const [foldersContext, setFoldersContext] = useState('');
  const foldersContextCurrent = foldersContext === folderContext;
  const loadFolders = useCallback(async () => {
    const request = ++folderRequest.current;
    setFoldersContext(folderContext);
    if (!orgId) { setFolders([]); setFoldersStatus('ready'); return; }
    setFolders([]);
    setFoldersStatus('loading'); setFoldersError('');
    try {
      const response = await choreographyApi.listFolders(orgId, currentFolderId || undefined);
      if (folderRequest.current !== request) return;
      setFolders(listFrom(payload(response), ['folders']));
      setFoldersStatus('ready');
    } catch (error) {
      if (folderRequest.current !== request) return;
      setFolders([]);
      setFoldersError(message(error));
      setFoldersStatus('error');
    }
  }, [orgId, currentFolderId, folderContext]);
  useEffect(() => {
    loadFolders();
    return () => { folderRequest.current += 1; };
  }, [loadFolders]);

  // MCRO001-F0053 / T-MCRO-053 Case A: creates a child of whichever folder is
  // currently open (MCRO001-D011 §5.3 — "New folder... creates the folder as a
  // child of the currently open folder").
  const createFolder = useCallback(async (name) => {
    await choreographyApi.createFolder(orgId, { name, parentFolderId: currentFolderId || null });
    await loadFolders();
  }, [orgId, currentFolderId, loadFolders]);

  // T-MCRO-053 Case C: only `name` is sent — the server leaves parentFolderId,
  // contents and position untouched when the key is absent from the body.
  const renameFolder = useCallback(async (folder, name) => {
    await choreographyApi.updateFolder(orgId, folder.folderId, { name });
    // Keep an open breadcrumb segment's label in sync if the renamed folder is
    // part of the current navigation path, not just its parent's listing.
    setOpenFolders((prev) => prev.map((entry) => (entry.folderId === folder.folderId ? { ...entry, name } : entry)));
    await loadFolders();
  }, [orgId, loadFolders]);

  const moveFolder = useCallback(async (folder, destinationFolderId) => {
    await choreographyApi.updateFolder(orgId, folder.folderId, { parentFolderId: destinationFolderId });
    await loadFolders();
  }, [orgId, loadFolders]);

  // MCRO001-D011 §4.4: archives the folder (server promotes its direct
  // children to its own former parent in the same transaction). If the
  // archived folder was somehow part of the current navigation path — its
  // tile is only ever offered from its own PARENT's listing, so this should
  // not happen in the ordinary click path, but defends against a stale path —
  // snap the view back to that folder's former parent rather than continuing
  // to show a view "inside" a folder that no longer exists.
  const archiveFolder = useCallback(async (folder) => {
    await choreographyApi.archiveFolder(orgId, folder.folderId);
    setOpenFolders((prev) => {
      const index = prev.findIndex((entry) => entry.folderId === folder.folderId);
      return index === -1 ? prev : prev.slice(0, index);
    });
    await loadFolders();
  }, [orgId, loadFolders]);

  const restoreFolder = useCallback(async (folderId) => {
    await choreographyApi.restoreFolder(orgId, folderId);
    await loadFolders();
  }, [orgId, loadFolders]);

  // Independent browse state lives inside FolderDestinationPicker itself; this
  // is just the data source it calls, kept separate from the grid's own
  // folders/breadcrumb above so browsing the picker never disturbs the page
  // behind it.
  const listFoldersForPicker = useCallback(async (parentFolderId) => {
    const response = await choreographyApi.listFolders(orgId, parentFolderId || undefined);
    return listFrom(payload(response), ['folders']);
  }, [orgId]);

  // MCRO001-D011 §5.1: PATCH /media/:mediaId already accepts folderId — the
  // SAME authority as every other tile edit (deleteFromLibrary/onSaveMetadata
  // below), so no new capability is introduced by "Move to folder…".
  const moveMedia = useCallback(async (item, destinationFolderId) => {
    // MCRO001-F0058 hardening: see retryOnRateLimit's own comment. A 429 here
    // is retried in place before MediaLibraryGrid's moveMediaItems loop ever
    // sees a rejection, so a transient rate-limit blip never counts against
    // T-MCRO-055 Case E's honest succeeded/total arithmetic.
    await retryOnRateLimit(() => choreographyApi.updateMedia(orgId, item.mediaId, { folderId: destinationFolderId }));
    await load();
  }, [orgId, load]);

  const [canvasItems, setCanvasItems] = useState([]);
  const [canvasNotice, setCanvasNotice] = useState('');
  // MCRO001-F0058 (owner ruling 2026-08-25, MCRO001-D006 §2 amendment): media ids
  // successfully attached to the canvas this session, queued for a best-effort
  // auto-created default-span demonstration timing map so the MCRO001-F0028 rail
  // appears with zero further interaction. Populated by attachToCanvas below;
  // drained by the effect declared after getMediaLibraryDemonstrationEligibility
  // (it needs that eligibility function, the revision context and the
  // demonstrationBoundary save path, all defined later in this component).
  const [autoDemonstrationMediaIds, setAutoDemonstrationMediaIds] = useState(() => new Set());
  const autoDemonstrationInFlightRef = useRef(new Set());
  const loadCanvasMedia = useCallback(async () => {
    if (!orgId || !routineId) { setCanvasItems([]); return; }
    try { const response = await choreographyApi.getCanvasMedia(orgId, routineId); setCanvasItems(listFrom(payload(response), ['media', 'items'])); } catch (error) { setCanvasNotice(message(error)); }
  }, [orgId, routineId]);
  useEffect(() => { loadCanvasMedia(); }, [loadCanvasMedia]);

  // MCRO001-F0030 / T-MCRO-030 Case M (GitLab WebUI #20): the durable counterpart
  // to `items`/`load()` above -- everything the caller uploaded that is NOT (yet,
  // or will never be) a registered `items` row: admitted/uploading/validating/
  // processing, plus failed/cancelled/expired kept visible until the choreo
  // retention sweep actually removes them. This is the fix for "I uploaded two
  // videos and they completely disappeared": before this, a session stuck at
  // lifecycle=admitted (choreo #19) had NOTHING referencing it once the tab/
  // promise chain that started the upload was gone -- searchMedia only ever
  // returns rows that already exist in choreography_media, which an ingest never
  // reaches until it completes. MediaLibraryGrid merges this list with `items`
  // into one truthful gallery (see its `pendingUploads` prop below).
  const [pendingIngests, setPendingIngests] = useState([]);
  const loadPendingIngests = useCallback(async () => {
    if (!orgId || typeof choreographyApi.listPendingVideoIngests !== 'function') return;
    try {
      const response = await choreographyApi.listPendingVideoIngests(orgId);
      setPendingIngests(listFrom(payload(response), ['ingests', 'items']));
    } catch (error) {
      // Deliberately silent: a failed fetch of the PENDING half must never blank
      // or error out the REGISTERED half (`items`, already loaded by `load()`
      // above) -- the library still shows everything it successfully knows
      // about rather than nothing. The list simply stays at its last-known
      // value and is retried on the next scheduled/triggered refresh below.
    }
  }, [orgId]);
  useEffect(() => { loadPendingIngests(); }, [loadPendingIngests]);

  // Bounded, backed-off refresh -- WebUI #21 ("collab session protocol chatter")
  // measured this exact surface's sibling (the per-upload 5s status poll) firing
  // 63 times/hour with no progress data and named a 5s->15s->30s backoff as the
  // fix; this NEW poll starts there and must never become a second offender.
  // Scheduled ONLY while at least one ingest session is still non-terminal, and
  // stops completely (no timer at all) once every pending upload has resolved
  // (ready/failed/cancelled/expired) -- the direct fix for requirement (2) in
  // GitLab WebUI #20 ("the eternal poll must surface a bounded timeout instead
  // of polling silently forever"): once nothing is left pending there is no poll
  // to be eternal.
  const pendingIngestPollDelayRef = useRef(15000);
  useEffect(() => {
    const hasNonTerminal = pendingIngests.some((row) => ['admitted', 'uploading', 'validating', 'processing'].includes(row?.lifecycle));
    if (!hasNonTerminal) { pendingIngestPollDelayRef.current = 15000; return undefined; }
    const delay = pendingIngestPollDelayRef.current;
    pendingIngestPollDelayRef.current = Math.min(delay * 1.6, 30000);
    const timer = setTimeout(loadPendingIngests, delay);
    return () => clearTimeout(timer);
  }, [pendingIngests, loadPendingIngests]);

  // MCRO001-F0040 / T-MCRO-040 Case D: the configured Trash retention window, read
  // from the service rather than hardcoded, so the Library's delete confirmation
  // stays truthful if CHOREO_MEDIA_TRASH_RETENTION_DAYS is changed. Falls back to
  // the documented 30-day default only while capabilities is unresolved.
  // M004-F0666 (b) (WebUI #53): inside the Choreography page the window comes
  // from the page's single capabilities read (`pageCapabilities`); only a
  // standalone mount (no page) reads capabilities itself.
  const [fetchedTrashRetentionDays, setTrashRetentionDays] = useState(30);
  const pageRetentionDays = Number(pageCapabilities?.trashRetentionDays);
  const trashRetentionDays = pageCapabilities
    ? (Number.isInteger(pageRetentionDays) && pageRetentionDays > 0 ? pageRetentionDays : 30)
    : fetchedTrashRetentionDays;
  const ownsCapabilitiesRead = !pageCapabilities;
  useEffect(() => {
    let cancelled = false;
    if (!orgId || !ownsCapabilitiesRead) return undefined;
    (async () => {
      try {
        const capabilities = payload(await choreographyApi.getCapabilities(orgId));
        const days = Number(capabilities?.mediaRetention?.trashRetentionDays);
        if (!cancelled && Number.isInteger(days) && days > 0) setTrashRetentionDays(days);
      } catch { /* capabilities is progressive enhancement here; keep the default */ }
    })();
    return () => { cancelled = true; };
  }, [orgId, ownsCapabilitiesRead]);

  // MCRO001-F0039 / T-MCRO-039 Case G: the destructive delete. MediaLibraryGrid owns
  // the confirmation (it has the label and the retention number); this owns the call
  // and the refresh, because the library and canvas lists live here.
  const deleteFromLibrary = useCallback(async (item) => {
    await choreographyApi.archiveMedia(orgId, item.mediaId);
    await load();
    // An archived video is off every canvas too, so the canvas pane must re-read.
    await loadCanvasMedia();
  }, [orgId, load, loadCanvasMedia]);

  // MCRO001-F0040 / T-MCRO-040 Case C.
  const restoreFromTrash = useCallback(async (entry) => {
    await choreographyApi.restoreMedia(orgId, entry.mediaId);
    await load();
    // Restore returns the video to exactly the canvases whose link rows survived.
    await loadCanvasMedia();
  }, [orgId, load, loadCanvasMedia]);

  // Attach: optimistic add to the canvas pane, calling the link endpoint for every
  // dragged/selected media id; a failed attach rolls the optimistic row back and
  // reports which items failed rather than silently dropping them.
  const attachToCanvas = useCallback(async (mediaIdsOrItems) => {
    if (!orgId || !routineId) return;
    const requested = (Array.isArray(mediaIdsOrItems) ? mediaIdsOrItems : [mediaIdsOrItems]).map((entry) => (
      typeof entry === 'string' ? { mediaId: entry } : entry
    )).filter((entry) => entry?.mediaId);
    if (requested.length === 0) return;
    const optimisticSource = new Map(items.map((item) => [String(item.mediaId), item]));
    const optimisticRows = requested.map((entry) => ({
      ...(optimisticSource.get(String(entry.mediaId)) || entry),
      mediaId: entry.mediaId, origin: 'linked', __optimistic: true,
    }));
    setCanvasItems((prev) => {
      const present = new Set(prev.map((row) => String(row.mediaId)));
      return [...prev, ...optimisticRows.filter((row) => !present.has(String(row.mediaId)))];
    });
    const failures = [];
    await Promise.all(requested.map(async (entry) => {
      try { await choreographyApi.linkCanvasMedia(orgId, routineId, entry.mediaId); } catch (error) { failures.push({ entry, error }); }
    }));
    if (failures.length > 0) {
      const failedIds = new Set(failures.map((failure) => String(failure.entry.mediaId)));
      setCanvasItems((prev) => prev.filter((row) => !(row.__optimistic && failedIds.has(String(row.mediaId)))));
      setCanvasNotice(failures.length === 1
        ? (failures[0].error?.message || 'Could not add that video to this canvas.')
        : `Could not add ${failures.length} of ${requested.length} videos to this canvas.`);
    } else {
      setCanvasNotice(requested.length === 1 ? 'Added 1 video to this canvas.' : `Added ${requested.length} videos to this canvas.`);
    }
    // MCRO001-F0058 (owner ruling 2026-08-25): "when the video is added, 'on
    // canvas' panel and rail appears! I do not see any reason for any other
    // interaction". Queue every successfully linked media id for a best-effort
    // auto-created demonstration timing map — never blocks or changes the
    // attach result above, which has already committed either way.
    const failedMediaIds = new Set(failures.map((failure) => String(failure.entry.mediaId)));
    const attachedMediaIds = requested.map((entry) => String(entry.mediaId)).filter((id) => !failedMediaIds.has(id));
    if (attachedMediaIds.length > 0) {
      setAutoDemonstrationMediaIds((prev) => {
        const next = new Set(prev);
        attachedMediaIds.forEach((id) => next.add(id));
        return next;
      });
    }
    await loadCanvasMedia();
  }, [orgId, routineId, items, loadCanvasMedia]);

  // Detach: optimistic remove, rolled back on failure by simply reloading the
  // authoritative list (simpler and just as correct as re-inserting the row).
  const detachFromCanvas = useCallback(async (item) => {
    if (!orgId || !routineId || !item?.mediaId) return;
    const previous = canvasItems;
    setCanvasItems((prev) => prev.filter((row) => String(row.mediaId) !== String(item.mediaId)));
    try {
      await choreographyApi.unlinkCanvasMedia(orgId, routineId, item.mediaId);
    } catch (error) {
      setCanvasItems(previous);
      throw error;
    }
  }, [orgId, routineId, canvasItems]);

  // M004-F0396/F0397 (T-M004-0017/T-M004-0018): Google-Drive-style bulk upload
  // entry point. Unlike the metadata-gated single-file "Add practice video"
  // form below (which stays exactly as it is for callers who want to set
  // start/end count and a teaching point up front), this path starts the
  // upload immediately with a filename-derived title — the same
  // already-shipped inline-edit (F0394) lets the user refine metadata once it
  // lands in the grid. The video branch reuses the exact same createVideoIngest
  // -> uploadChunks -> finalizeThenAwaitReady -> completeVideoIngest sequence,
  // never a parallel implementation.
  //
  // MCRO001-F0035 / T-MCRO-035: MediaLibraryGrid's file-picker/drag-drop accept
  // JPEG/PNG/WebP alongside video (ACCEPTED_UPLOAD_TYPES), and both share this
  // one enqueueFiles -> beginBulkUpload entry point (UploadQueueManager is
  // media-type-agnostic by design). Without this branch every image file would
  // silently be sent to createVideoIngest, whose MIME allowlist only accepts
  // video/mp4|webm|quicktime — every real image would fail admission with
  // VIDEO_TYPE_UNSUPPORTED despite the picker/drop UI accepting it. Images use
  // the separate single-request admission call (choreographyApi.createImageUpload
  // -> choreo's POST .../image-uploads -> video-ingest's POST /internal/images,
  // studioaeye-video-ingest#2) — one call, no chunk/finalize/complete steps,
  // since §7.1 images are always small enough for a single request.
  const defaultTitleFromFilename = (name) => String(name || 'Untitled video').replace(/\.[^./\\]+$/, '');
  const IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
  // MCRO001-F0053 / T-MCRO-053 Case J (MCRO001-D011 §5.3: "Uploads land in the
  // open folder... it threads that folderId into POST .../media-upload").
  //
  // The design doc's own wording assumes the single-shot POST .../media-upload
  // route, which choreo MR !126 (0e5df3d1) DID extend with folderId. But the
  // grid's actual "+ Upload"/drag-drop button (below, via uploadQueue.
  // enqueueFiles -> beginBulkUpload) never calls that route — it uses the
  // resumable two-step POST .../video-ingests + .../complete pair for video,
  // and the separate single-request POST .../image-uploads for images, and
  // NEITHER of those was extended with a folderId field in the merged D011
  // §5.1 contract (only POST .../media[-upload] and PATCH /media/:mediaId
  // were). Re-plumbing the resumable transport itself to accept folderId is
  // out of scope for a WebUI-only ticket and would be a D011/backend change.
  //
  // Instead: once the upload lands (a real mediaId exists), follow up with the
  // ALREADY-supported PATCH /media/:mediaId { folderId } (same authority as
  // this whole upload action) to move it into the open folder before the grid
  // ever re-reads the library — this achieves the observable D011 §5.3
  // contract ("a video uploaded while 'Grade 3' is open appears in 'Grade
  // 3'") entirely within the existing, already-merged API surface, with no
  // invented endpoint. Flagged in the MR description as a possible D011
  // amendment (threading folderId through admission directly) rather than
  // silently worked around. A no-op at root (currentFolderId is falsy) — the
  // server's own default is already root.
  //
  // Deliberately best-effort: the upload itself already succeeded and the
  // video is fully visible (at root) even if this follow-up move fails, so a
  // failed move must never be reported as a failed upload.
  const moveIntoOpenFolderIfNeeded = useCallback(async (mediaId) => {
    if (!currentFolderId || !mediaId) return;
    try { await choreographyApi.updateMedia(orgId, mediaId, { folderId: currentFolderId }); } catch { /* best-effort, see comment above */ }
  }, [orgId, currentFolderId]);
  const beginBulkUpload = useCallback(async (file, { onProgress } = {}) => {
    const stableRevisionId = revisionContext?.ready ? revisionContext.revisionId : '';
    if (IMAGE_MIME_TYPES.has(String(file?.type || '').toLowerCase())) {
      onProgress?.({ phase: 'uploading', percent: 0 });
      const response = await choreographyApi.createImageUpload(orgId, routineId, file, {
        title: defaultTitleFromFilename(file.name),
      });
      const created = payload(response);
      await moveIntoOpenFolderIfNeeded(created?.mediaId);
      onProgress?.({ phase: 'ready', percent: 100 });
      return created;
    }
    try {
      onProgress?.({ phase: 'admitted', percent: 0 });
      const claim = payload(await choreographyApi.createVideoIngest(orgId, routineId, {
        originalFilename: file.name, declaredSize: file.size, mediaType: file.type,
        title: defaultTitleFromFilename(file.name), kind: 'example_video', revisionId: stableRevisionId || undefined,
      }));
      // MCRO001-F0030 / T-MCRO-030 Case M (GitLab WebUI #20 requirement 1):
      // "uploads appear in the library from the moment they're admitted" — a
      // single one-off refresh right after the admission that just happened,
      // not a poll, so the very upload this tab just started shows up in the
      // grid immediately instead of waiting for the next backed-off tick.
      loadPendingIngests();
      onProgress?.({ phase: 'uploading', percent: 0 });
      await uploadChunks(file, claim.uploadId, claim.uploadToken, claim.offset, (fraction) => {
        onProgress?.({ phase: 'uploading', percent: Math.round(fraction * 100) });
      });
      onProgress?.({ phase: 'processing', percent: 100 });
      const ready = await finalizeThenAwaitReady(claim.uploadId, claim.uploadToken, (status) => {
        onProgress?.({ phase: status.lifecycle, percent: 100 });
      });
      if (ready.lifecycle !== 'ready') throw new Error('The video upload did not reach Ready.');
      const completed = payload(await choreographyApi.completeVideoIngest(orgId, routineId, claim.uploadId));
      await moveIntoOpenFolderIfNeeded(completed?.mediaId);
      return completed;
    } catch (error) {
      // Same truthful-restart contract as the single-file path (D007 §3): a
      // mid-upload token expiry cannot resume, so this is surfaced as a clean,
      // specific "restart" message rather than the underlying auth error text.
      if (VIDEO_INGEST_RESTART_CODES.has(error.code)) {
        throw new Error('Your upload session expired. Choose the video again to restart the upload.');
      }
      throw error;
    }
  }, [orgId, routineId, revisionContext, loadPendingIngests, moveIntoOpenFolderIfNeeded]);
  const uploadQueue = useUploadQueueManager(beginBulkUpload);
  const completedCountRef = useRef(0);
  useEffect(() => {
    // Surface each finished upload into the grid as soon as it completes,
    // without a manual refresh (T-M004-0018 "grid-refresh-on-complete").
    // Also refreshes the pending-ingest list so the tile that was showing
    // "Uploading"/"Processing" for this exact upload is removed in the same
    // tick its real registered tile (from `load()`) appears, rather than
    // briefly showing both.
    if (uploadQueue.completedMedia.length > completedCountRef.current) {
      completedCountRef.current = uploadQueue.completedMedia.length;
      load();
      loadPendingIngests();
    }
  }, [uploadQueue.completedMedia, load, loadPendingIngests]);

  const addFileByResumableIngest = async (stableRevisionId) => {
    setIngestProgress({ phase: videoIngestPhaseLabel('admitted'), percent: 0 });
    const claim = payload(await choreographyApi.createVideoIngest(orgId, routineId, {
      originalFilename: form.file.name, declaredSize: form.file.size, mediaType: form.file.type,
      title: form.title, kind: form.kind, revisionId: stableRevisionId || undefined,
    }));
    setIngestProgress({ phase: videoIngestPhaseLabel('uploading'), percent: 0 });
    await uploadChunks(form.file, claim.uploadId, claim.uploadToken, claim.offset, (fraction) => {
      setIngestProgress({ phase: videoIngestPhaseLabel('uploading'), percent: Math.round(fraction * 100) });
    });
    setIngestProgress({ phase: videoIngestPhaseLabel('validating'), percent: 100 });
    const ready = await finalizeThenAwaitReady(claim.uploadId, claim.uploadToken, (status) => {
      setIngestProgress({ phase: videoIngestPhaseLabel(status.lifecycle), percent: 100 });
    });
    if (ready.lifecycle !== 'ready') throw new Error('The video upload did not reach Ready.');
    await choreographyApi.completeVideoIngest(orgId, routineId, claim.uploadId);
  };
  const add = async (event) => {
    event.preventDefault();
    try {
      const stableRevisionId = revisionContext?.ready ? revisionContext.revisionId : '';
      if (form.file) {
        try {
          await addFileByResumableIngest(stableRevisionId);
        } catch (error) {
          // A token expiring mid-upload (or any auth failure) cannot resume:
          // D007 issues no renewal path in version one. Surface a truthful,
          // specific message and require the user to pick the file again to
          // mint a fresh claim, instead of masking it as a generic failure.
          if (VIDEO_INGEST_RESTART_CODES.has(error.code)) {
            throw new Error('Your upload session expired. Choose the video again to restart the upload.');
          }
          throw new Error(error.message || 'The video upload did not complete.');
        } finally {
          setIngestProgress(null);
        }
      } else {
        await choreographyApi.addMedia(orgId, routineId, { ...form, file: undefined, revisionId: stableRevisionId || undefined, tags: form.tags.split(',').map((tag) => tag.trim()).filter(Boolean) });
      }
      setForm({ ...form, title: '', storageRef: '', teachingPoint: '', tags: '', file: null });
      setNotice('Demonstration added to the routine library.');
      await load();
    } catch (error) { setNotice(message(error)); }
  };
  // T-MCRO-006: edit a library card's descriptive metadata in place (choreo
  // PATCH /media/:mediaId) and reflect the saved row locally without a refetch.
  const saveMetadata = useCallback(async (item, patch) => {
    const mediaId = item?.mediaId || item?.id;
    const response = await choreographyApi.updateMedia(orgId, mediaId, patch);
    const saved = payload(response);
    setItems((current) => current.map((row) => (String(row.mediaId || row.id) === String(mediaId)
      ? { ...row, ...saved, tags: saved.tags || row.tags }
      : row)));
    setNotice('Saved.');
  }, [orgId]);

  // Owner redesign 2026-07-24: the Video library tab lands on the calm caption-free
  // gallery (MediaLibraryGrid). The demonstration-timing cockpit is preserved but is
  // now reached deliberately through "Use in routine" instead of being the default
  // surface. Selecting media there seeds the workspace with the chosen items.
  const [routineItems, setRoutineItems] = useState(null);
  // MCRO001-F0021 (bounded slice) / owner feedback 2026-07-24: "On this canvas"
  // now sits LEFT of "Your Video Library" (the working set the user is laying
  // out belongs beside the canvas, not the org-wide source list) and the Video
  // Library pane can fold away, mirroring the routines-rail collapse pattern
  // (see routinesCollapsed below) with its own independent, own-keyed state.
  //
  // STRUCK (D021 supersedes, 2026-09-03): "org-wide source list" above is the
  // pre-F0159 scope. MCRO001-D021 §1.1 makes "Your Video Library" PERSONAL, not
  // an org-wide source list. The LEFT/RIGHT placement and fold behaviour this
  // paragraph describes are unchanged; only the "org-wide" scope claim is
  // superseded.
  const [videoLibraryCollapsed, setVideoLibraryCollapsed] = useState(() => {
    try { return localStorage.getItem('mc.videoLibraryCollapsed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('mc.videoLibraryCollapsed', videoLibraryCollapsed ? '1' : '0'); } catch { /* Storage is optional. */ }
  }, [videoLibraryCollapsed]);
  // MCRO001-F0051 / T-MCRO-051: the draggable divider's library width, in px,
  // persisted independently of the F0034 collapse flag so a reopened library
  // (Case C) restores the user's chosen size, not a fixed default.
  const [libraryPaneWidth, setLibraryPaneWidth] = useState(readStoredLibraryWidth);
  useEffect(() => {
    try { localStorage.setItem(LIBRARY_WIDTH_STORAGE_KEY, String(Math.round(libraryPaneWidth))); } catch { /* Storage is optional. */ }
  }, [libraryPaneWidth]);
  // Drag handler (Case A/B/D): a below-threshold proposal snaps the library
  // CLOSED rather than clamping to a sliver; otherwise clamp to [threshold, max].
  const resizeLibraryBy = useCallback((deltaPx, startWidthPx) => {
    const proposed = clampLibraryWidth(startWidthPx + deltaPx);
    if (proposed < LIBRARY_COLLAPSE_THRESHOLD_PX) {
      setVideoLibraryCollapsed(true);
      return;
    }
    setVideoLibraryCollapsed(false);
    setLibraryPaneWidth(proposed);
  }, []);
  // Keyboard handler (Case F): same collapse-below-threshold rule as drag.
  const resizeLibraryTo = useCallback((targetPx) => {
    const proposed = clampLibraryWidth(targetPx);
    if (proposed < LIBRARY_COLLAPSE_THRESHOLD_PX) {
      setVideoLibraryCollapsed(true);
      return;
    }
    setVideoLibraryCollapsed(false);
    setLibraryPaneWidth(proposed);
  }, []);
  // Reopen (Case C): if the persisted width is still below threshold (i.e. the
  // library was collapsed by a squeeze), restore a sensible default.
  const reopenLibrary = useCallback(() => {
    setLibraryPaneWidth((current) => (current < LIBRARY_COLLAPSE_THRESHOLD_PX ? LIBRARY_DEFAULT_WIDTH_PX : current));
    setVideoLibraryCollapsed(false);
  }, []);
  const openInRoutine = useCallback((primary, selection) => {
    const chosen = (selection && selection.length ? selection : [primary]).filter(Boolean);
    setRoutineItems(chosen.length ? chosen : items);
  }, [items]);
  const authoredBars = rehearsalBarOptions(snapshot, revisionContext?.revisionId);

  // MCRO001-F0058 / T-MCRO-058 (owner ruling 2026-08-25, MCRO001-D006 §2
  // amendment): drains autoDemonstrationMediaIds (queued by attachToCanvas
  // above on every successful "Add to canvas"). For each pending media id this
  // reuses the SAME eligibility gate and save path the "Timing editor" /
  // DemonstrationWorkspace already use — no parallel endpoint, no relaxed
  // validation — so a video that could not be timed by hand cannot be timed
  // here either:
  //   - getDemonstrationEligibility (the same prop DemonstrationWorkspace's
  //     candidates use) already refuses when no complete authored 8-count bar
  //     exists for this revision, or the video's content/duration has not
  //     resolved yet — this IS the "skip silently, no complete bars yet" /
  //     "attach still succeeds for an ineligible video" behaviour the design
  //     doc now documents; no separate check is duplicated here.
  //   - existingDemonstrationMediaIds is the no-clobber check: it holds a
  //     mediaId ONLY once this revision's demonstrations list has actually
  //     been fetched AND that media already has a saved map in it — read
  //     straight off the SAME recordsByRevision state the rail/Library colour
  //     ultimately derive from (MusicChoreography's own definition above
  //     MediaLibrary's render call), never off trackColours: trackColours
  //     (mediaTrackColours()/approvedCandidate()) additionally requires
  //     rendition.availability === 'available', a LATER, SEPARATE async stage
  //     (the per-mediaId content fetch) that can still be pending after the
  //     list itself has settled — checking that would race a false "no
  //     existing map" open again. demonstrationRecordsReady gates this same
  //     way: both are false until the list fetch has actually settled, so
  //     this effect can never run against a stale/incomplete list.
  //   - createProportionalTimingMap + onSaveTimingMap are the exact functions
  //     DemonstrationWorkspace.chooseVideo()/save() call for a brand-new map,
  //     just with a full-span default: mediaStartMs 0 → the real probed
  //     durationMs (mediaEndMs), and startCount/endCount spanning the FIRST
  //     through LAST complete authored bar (not just the first bar
  //     chooseVideo seeds), so the default demonstration covers as much of
  //     the routine as is currently mappable.
  // A video still "resolving" (preview not cached yet) stays queued and is
  // retried automatically on the next render once its preview resolves
  // (getDemonstrationEligibility's own requestPreview side effect drives
  // that, exactly as DemonstrationWorkspace's own candidates already do). Any
  // other outcome — eligible-and-saved, ineligible, or a failed save — is
  // terminal for this media id and is removed from the queue; a failed
  // auto-create never surfaces a notice and never re-attempts, matching "the
  // attach itself must still succeed exactly as today" (the attach already
  // completed by the time this effect runs).
  useEffect(() => {
    if (autoDemonstrationMediaIds.size === 0) return;
    if (!revisionContext?.ready || !demonstrationRecordsReady
        || typeof getDemonstrationEligibility !== 'function' || typeof onSaveTimingMap !== 'function') return;
    const revisionId = revisionContext.revisionId;
    const drop = (mediaId) => setAutoDemonstrationMediaIds((prev) => {
      if (!prev.has(mediaId)) return prev;
      const next = new Set(prev);
      next.delete(mediaId);
      return next;
    });
    autoDemonstrationMediaIds.forEach((mediaId) => {
      const id = String(mediaId);
      if (autoDemonstrationInFlightRef.current.has(id)) return;
      if (existingDemonstrationMediaIds.has(id)) { drop(id); return; }
      const eligibility = getDemonstrationEligibility({ mediaId: id, revisionId, snapshotDigest: revisionContext.snapshotDigest });
      if (eligibility?.eligible !== true) {
        if (eligibility?.reason === 'Resolving this video for timing setup — try again in a moment.') return;
        drop(id);
        return;
      }
      const fullBars = completeDemonstrationBars(authoredBars, revisionId);
      if (fullBars.length === 0) { drop(id); return; }
      autoDemonstrationInFlightRef.current.add(id);
      (async () => {
        try {
          const timingMap = createProportionalTimingMap({
            revisionId,
            renditionId: eligibility.renditionId,
            durationMs: eligibility.durationMs,
            mediaStartMs: 0,
            mediaEndMs: eligibility.durationMs,
            startCount: fullBars[0].startCount,
            endCount: fullBars[fullBars.length - 1].endCount,
            sections: snapshot?.sections || [],
            authoredBars: fullBars,
          }, revisionId);
          await onSaveTimingMap({
            mediaId: id,
            revision: { revisionId, snapshotDigest: revisionContext.snapshotDigest },
            relation: { relationId: eligibility.relationId, expectedVersion: eligibility.expectedRelationVersion },
            rendition: { renditionId: eligibility.renditionId, expectedVersion: eligibility.expectedRenditionVersion },
            timingMap,
          });
        } catch {
          // Best-effort only — the canvas attach already committed; a save
          // failure here (e.g. a stale digest raced by a concurrent edit)
          // leaves the video attached with no rail, exactly as if the owner
          // had never opened the Timing editor for it.
        } finally {
          autoDemonstrationInFlightRef.current.delete(id);
          drop(id);
        }
      })();
    });
  }, [autoDemonstrationMediaIds, revisionContext, demonstrationRecordsReady, existingDemonstrationMediaIds, getDemonstrationEligibility, onSaveTimingMap, authoredBars, snapshot]);

  if (routineItems) {
    const breadcrumbTitle = routineItems.length === 1 ? (routineItems[0]?.title || 'Untitled video') : `${routineItems.length} videos`;
    return <div className="mc-media-workspace">
      <div className="mc-routine-return"><button type="button" className="mc-quiet" onClick={() => setRoutineItems(null)}>← Back to library</button><span className="mc-muted">Timing editor · {breadcrumbTitle}</span></div>
      <DemonstrationWorkspace
        items={routineItems}
        revisionContext={revisionContext}
        sections={snapshot?.sections || []}
        authoredBars={authoredBars}
        capabilities={{ canUpload, canShare: false, canEditTiming }}
        actions={{ onUpload: canUpload ? () => setShowUpload(true) : undefined, getDemonstrationEligibility, onSaveTimingMap }}
        autoOpen
      />
    </div>;
  }

  // MCRO001-F0021 (bounded slice): the Video library tab is a TWO-PANE layout —
  // "On this canvas" (routine-scoped, CanvasMediaPanel, LEFT — owner feedback
  // 2026-07-24: the working set belongs beside the canvas you're laying out)
  // beside "Your Video Library" (org-wide, MediaLibraryGrid, RIGHT, foldable).
  // Both share the same mc-library-* visual language; the split is a plain flex
  // row that stacks on narrow screens exactly like MediaLibraryGrid's own
  // library+drawer split does.
  //
  // STRUCK (D021 supersedes, 2026-09-03): "org-wide" above is the pre-F0159
  // scope. MCRO001-D021 §1.1 makes "Your Video Library" PERSONAL — the load
  // above returns only the viewer's own uploads, never an org-wide list. The
  // LEFT/RIGHT placement and foldability this paragraph describes are
  // unchanged; only the "org-wide" scope claim is superseded.
  return <div className="mc-media-workspace">
    <div className="mc-video-library-split">
      {routineId && (
        // MCRO001-F0039 / T-MCRO-039: CanvasMediaPanel still never takes
        // getContent — that existed only so it could reach
        // choreographyApi.archiveMedia to "remove" an owned tile, which deleted
        // the video from the whole organisation's library. It has no delete path
        // at all now; ✕ calls onDetach for every tile.
        //
        // Issue #22: orgId is passed again (same value MediaLibraryGrid below
        // already receives) so each canvas tile can mint its own real poster
        // through the same stream-url path the library uses — CanvasMediaPanel
        // never gained a delete capability from this, see its own header comment.
        <CanvasMediaPanel
          items={canvasItems}
          orgId={orgId}
          canEdit={canUpload}
          onAttach={attachToCanvas}
          onDetach={detachFromCanvas}
        />
      )}
      {/* MCRO001-F0051 / T-MCRO-051: the draggable divider only exists while the
          library is open — collapsed, the edge-expand button below is the
          affordance instead. */}
      {!videoLibraryCollapsed && (
        <LibraryPaneResizeDivider
          widthPx={libraryPaneWidth}
          onResizeBy={resizeLibraryBy}
          onResizeTo={resizeLibraryTo}
          onCollapse={() => setVideoLibraryCollapsed(true)}
        />
      )}
      {videoLibraryCollapsed && (
        <button
          type="button"
          className="mc-video-library-edge-expand"
          aria-expanded={false}
          aria-controls="mc-video-library-pane"
          aria-label="Expand video library"
          onClick={reopenLibrary}
        >«</button>
      )}
      <div
        id="mc-video-library-pane"
        className={`mc-video-library-pane ${videoLibraryCollapsed ? 'mc-video-library-pane-collapsed' : ''}`}
        // MCRO001-F0051 / T-MCRO-051: the chosen split width is applied as an
        // inline flex-basis so it survives reload and is assertable without a
        // layout engine. When collapsed, the width is dropped entirely so the
        // existing `mc-video-library-pane-collapsed` rule zeroes the footprint.
        style={videoLibraryCollapsed ? undefined : { flex: `0 0 ${Math.round(libraryPaneWidth)}px`, maxWidth: `${Math.round(LIBRARY_MAX_WIDTH_PX)}px` }}
      >
        <div className="mc-video-library-pane-head">
          <button
            type="button"
            className="mc-video-library-toggle"
            aria-expanded={!videoLibraryCollapsed}
            aria-controls="mc-video-library-pane"
            aria-label="Collapse video library"
            onClick={() => setVideoLibraryCollapsed(true)}
          >»</button>
        </div>
        <MediaLibraryGrid
          orgId={orgId}
          canUpload={canUpload}
          canEdit={canUpload}
          getContent={choreographyApi.getMediaContent}
          // WebUI issue #30 / M001-D023 §7 §12.1: the video library's Share
          // management surface — raw (unwrapped) responses, matching every
          // other sharingApi call ResourceAccessManager makes (see
          // ResourceSharePanel.js's own contract comment).
          getMediaSharingAccess={choreographyApi.getMediaSharingAccess}
          replaceMediaSharingAccess={choreographyApi.replaceMediaSharingAccess}
          searchMediaSharingPrincipals={choreographyApi.searchMediaSharingPrincipals}
          createMediaSharingLink={choreographyApi.createMediaSharingLink}
          deleteMediaSharingLink={choreographyApi.deleteMediaSharingLink}
          retryMediaSharingInvitation={choreographyApi.retryMediaSharingInvitation}
          getMediaSharingAccessExplain={choreographyApi.getMediaAccessExplain}
          onSaveMetadata={saveMetadata}
          onFilesSelected={canUpload ? uploadQueue.enqueueFiles : undefined}
          onUseInRoutine={openInRoutine}
          onAddToCanvas={routineId ? attachToCanvas : undefined}
          // MCRO001-F0039 / T-MCRO-039 Case G + MCRO001-F0040 / T-MCRO-040:
          // the destructive delete and the recycle bin, both Library-only.
          trashRetentionDays={trashRetentionDays}
          onDeleteMedia={canUpload ? deleteFromLibrary : undefined}
          listTrash={canUpload ? (() => choreographyApi.listTrashedMedia(orgId)) : undefined}
          onRestoreMedia={canUpload ? restoreFromTrash : undefined}
          // MCRO001-F0052: a video's Library tile borders in its Canvas rail colour.
          trackColours={trackColours}
          // MCRO001-F0030 / T-MCRO-030 Case M (GitLab WebUI #20): every one of the
          // caller's own not-yet-registered upload sessions, merged with `items`
          // above into one truthful gallery — this is the actual fix: an upload
          // stuck at admitted/uploading/processing, or terminally failed/expired,
          // renders with an honest status instead of the library showing nothing.
          pendingUploads={pendingIngests}
          // MCRO001-F0053 / T-MCRO-053 (MCRO001-D011 §5.3): the nested
          // media-library folder tree.
          folders={foldersContextCurrent ? folders : []}
          foldersStatus={foldersContextCurrent ? foldersStatus : 'loading'}
          foldersError={foldersContextCurrent ? foldersError : ''}
          items={itemsContextCurrent ? items : []}
          itemsStatus={itemsContextCurrent ? itemsStatus : 'loading'}
          itemsError={itemsContextCurrent ? itemsError : ''}
          breadcrumb={breadcrumb}
          canManageFolders={canManageFolders}
          onOpenFolder={openFolder}
          onCreateFolder={createFolder}
          onRenameFolder={renameFolder}
          onMoveFolder={moveFolder}
          onArchiveFolder={archiveFolder}
          onRestoreFolder={restoreFolder}
          listFoldersForPicker={listFoldersForPicker}
          onMoveMedia={moveMedia}
        />
        {/* M004-F0396/F0397 consolidation (owner 2026-07-24: "Two top-level
            upload buttons is confusing"): the grid's own header now has exactly
            ONE primary upload control (the bulk/resumable file picker). The
            HTTPS-reference capability (choreographyApi.addMedia) is preserved
            but demoted to a small, clearly-secondary control that opens the
            SAME dialog/logic (showUpload/form/add) reframed as "or add by
            link" — it never gates or precedes the primary upload button. */}
        {canUpload && (
          <button type="button" className="mc-text-button mc-library-add-by-link" onClick={() => setShowUpload(true)}>or add by link</button>
        )}
      </div>
    </div>
    {canvasNotice && <div className="mc-notice" role="status">{canvasNotice}</div>}
    {canUpload && <UploadQueueManager manager={uploadQueue} />}
    {/* Secondary "or add by link" dialog: file uploads now exclusively go
        through the primary bulk control above, so this form is link-only
        (Title + approved HTTPS reference + Start/End counts). Teaching point
        and tags are dropped from here — they are genuinely editable after
        creation via the grid tile's own inline-edit surface (ExpandedMedia's
        onSaveMetadata, T-MCRO-006/F0394), which applies to any item
        regardless of how it was created. Start/End counts have no such
        post-creation equivalent, so they stay. `add()`'s form.file branch is
        kept (defensively, dead in practice) since this dialog can no longer
        set form.file — DemonstrationWorkspace's own "Upload video" action
        (out of scope to edit) also opens this same showUpload dialog, which
        is now link-only too, matching the primary-bulk-only file path. */}
    {canUpload && showUpload ? <form className="mc-panel mc-media-form" onSubmit={add}><span className="mc-kicker">Add by link</span><h2>Add video by link</h2><RowField label="Title" value={form.title} onChange={(title) => setForm({ ...form, title })} /><RowField label="Approved HTTPS reference" value={form.storageRef} onChange={(storageRef) => setForm({ ...form, storageRef })} /><div className="mc-inline"><RowField label="Start" type="number" min="1" value={form.startCount} onChange={(startCount) => setForm({ ...form, startCount })} /><RowField label="End" type="number" min="1" value={form.endCount} onChange={(endCount) => setForm({ ...form, endCount })} /></div>{ingestProgress && <div className="mc-ingest-progress" role="status"><span>{ingestProgress.phase} — {ingestProgress.percent}%</span><div className="mc-progress-bar"><div className="mc-progress-fill" style={{ width: `${ingestProgress.percent}%` }} /></div></div>}<div className="mc-dialog-actions"><button type="button" className="mc-quiet" onClick={() => setShowUpload(false)} disabled={Boolean(ingestProgress)}>Cancel</button><button className="mc-primary" disabled={!form.title || !form.storageRef || Boolean(ingestProgress)}>Add to library</button></div>{notice && <div className="mc-notice" role="status">{notice}</div>}</form> : null}
  </div>;
}

// Owner decision 2026-07-23: the separate drafting page is removed. Creating a
// routine asks only for a title (and optional track title), then lands the
// author directly in the collaborative canvas. This panel is inline and
// non-modal; the canvas remains the only writing surface.
export function CreateRoutineDialog({ onClose, onCreate, saving }) {
  const [title, setTitle] = useState('');
  const [trackTitle, setTrackTitle] = useState('');
  const submit = async (event) => {
    event.preventDefault();
    if (!title.trim() || saving) return;
    await onCreate({
      title: title.trim(),
      trackTitle: trackTitle.trim() || title.trim(),
      artist: '',
      durationMs: 180000,
      bpm: undefined,
      firstCountOffsetMs: undefined,
      tapTimesMs: [],
      audioFile: null,
      // MCRO001-F0164 / T-MCRO-165 (WebUI issue #58): scope-free create body
      // — choreo's private default (MCRO001-F0161) applies instead of the
      // removed hard-coded 'organisation'. Owner ruling, verbatim: "I must
      // be able to see all access by any path and I must be able to control
      // what rights each access path gives."
      snapshot: emptySnapshot(),
    });
  };
  return <section className="mc-panel mc-create-routine" aria-labelledby="mc-create-routine-title">
    <span className="mc-kicker">New routine</span>
    <h2 id="mc-create-routine-title">Name the routine</h2>
    <p className="mc-muted">You land straight in the collaborative canvas — write, paste and mark the structure up there.</p>
    <form onSubmit={submit} aria-label="Create routine">
      <label className="mc-field"><span>Routine title</span><input aria-label="Routine title" value={title} onChange={(event) => setTitle(event.target.value)} autoFocus required /></label>
      <label className="mc-field"><span>Track title (optional)</span><input aria-label="Track title" value={trackTitle} onChange={(event) => setTrackTitle(event.target.value)} placeholder="Defaults to the routine title" /></label>
      <div className="mc-dialog-actions"><button type="button" className="mc-quiet" onClick={onClose}>Cancel</button><button type="submit" className="mc-primary" disabled={saving || !title.trim()}>{saving ? 'Creating…' : 'Create and open canvas'}</button></div>
    </form>
  </section>;
}

export default function MusicChoreography() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]); const [orgId, setOrgId] = useState(''); const [capabilities, setCapabilities] = useState({}); const [routines, setRoutines] = useState([]); const [routineId, setRoutineId] = useState(''); const [storedRoutine, setRoutine] = useState(null); const [storedSnapshot, setSnapshot] = useState(emptySnapshot()); const [storedBaselineSnapshot, setBaselineSnapshot] = useState(emptySnapshot()); const [reanchorPreview, setReanchorPreview] = useState(null); const [reviewDisplay, setReviewDisplay] = useState('inline'); const [tab, setTab] = useState('map');   const [creating, setCreating] = useState(false); const [loading, setLoading] = useState(true); const [saving, setSaving] = useState(false); const [notice, setNotice] = useState(''); const [error, setError] = useState(''); const [publishing, setPublishing] = useState(false);
  const [collaborationStatus, setCollaborationStatus] = useState(null);
  const actorId = user?.id ?? user?.userId ?? null;
  const [capabilitiesContext, setCapabilitiesContext] = useState(null);
  const capabilitiesContextKey = JSON.stringify([orgId, actorId]);
  const readPermissionKey = `${capabilities.choreography_view === true}:${capabilities.choreography_edit_all === true}:${capabilities.choreography_edit_own === true}`;
  const readContextKey = JSON.stringify([orgId, routineId, actorId, readPermissionKey]);
  const readContextRef = useRef(readContextKey);
  readContextRef.current = readContextKey;
  const [loadedReadContext, setLoadedReadContext] = useState(null);
  const blankReadSnapshot = useMemo(() => emptySnapshot(), []);
  // Bind saved data to its actor/organisation/routine/permission before paint.
  // An effect cleanup alone would leave the old scope visible for one frame.
  const routine = loadedReadContext === readContextKey ? storedRoutine : null;
  const snapshot = loadedReadContext === readContextKey ? storedSnapshot : blankReadSnapshot;
  const baselineSnapshot = loadedReadContext === readContextKey ? storedBaselineSnapshot : blankReadSnapshot;

  // MCRO001-F0065 (T-MCRO-065): the workspace-wide "viewing as" choice —
  // '' = full team, otherwise a cast number (string, as a <select> carries).
  // Lives HERE so the Canvas mirror and the Rehearsal readout keep the same
  // dancer across tab switches, and resets with the routine.
  const [viewingAs, setViewingAs] = useState('');
  const [routinesCollapsed, setRoutinesCollapsed] = useState(() => {
    try { return localStorage.getItem('mc.routinesRailCollapsed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('mc.routinesRailCollapsed', routinesCollapsed ? '1' : '0'); } catch { /* Storage is optional. */ }
  }, [routinesCollapsed]);
  const [demonstrationRevisionContext, setDemonstrationRevisionContext] = useState({ ready: false, revisionId: '', snapshotDigest: '' });
  const [demonstrationPlaybackSession, setDemonstrationPlaybackSession] = useState(emptyDemonstrationPlaybackSession);
  const canvasWorkspaceCaptureRef = useRef(null);
  const handleCollaborationStatus = useCallback((next) => setCollaborationStatus(next), []);
  const registerCanvasWorkspaceCapture = useCallback((capture) => { canvasWorkspaceCaptureRef.current = capture; }, []);
  const org = orgs.find((item) => String(item.id || item.organisation_id) === String(orgId));
  const loadRoutinesGenerationRef = useRef(0);
  // T-MCRO-028 unmount hygiene (lead review of WebUI !298, 2026-09-05): the
  // generation guard only handles SUPERSESSION — a load still settling after
  // the component is GONE must not touch state either. aliveRef is checked
  // before every setState in loadRoutines and in the getMine effect below;
  // the capabilities effect needs no extra guard (its per-run `cancelled`
  // flag is set by the same cleanup that runs on unmount). Set true in the
  // effect body, not just at declaration, so StrictMode's double mount
  // re-arms it after the simulated unmount.
  const aliveRef = useRef(true);
  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; }; }, []);
  const loadRoutines = useCallback(async () => {
    if (!aliveRef.current) return;
    if (!orgId || capabilities.choreography_view !== true) { setRoutines([]); setLoading(false); return; }
    const generation = ++loadRoutinesGenerationRef.current;
    setLoading(true);
    try {
      const response = await retryTransientAuthorization('GET choreography routines', () => choreographyApi.listRoutines(orgId));
      // A superseded org's late settlement must never publish its routines over
      // the current org's (same T-MCRO-028 stale-stomp class as the
      // capabilities effect above; WebUI main 8036).
      if (!aliveRef.current || generation !== loadRoutinesGenerationRef.current) return;
      const next = listFrom(payload(response), ['routines', 'items']);
      setRoutines(next);
      setRoutineId((current) => current && !next.some((item) => String(item.routineId) === String(current)) ? '' : current);
    } catch (cause) {
      if (!aliveRef.current || generation !== loadRoutinesGenerationRef.current) return;
      setError(message(cause));
    } finally {
      if (aliveRef.current && generation === loadRoutinesGenerationRef.current) setLoading(false);
    }
  }, [orgId, capabilities.choreography_view]);
  useEffect(() => { organisationApi.getMine().then((response) => { if (!aliveRef.current) return; const next = listFrom(payload(response), ['organisations', 'items']); setOrgs(next); const primary = next.find((item) => item.is_primary) || next[0]; setOrgId(String(primary?.id || primary?.organisation_id || '')); }).catch((cause) => { if (!aliveRef.current) return; setError(message(cause)); setLoading(false); }); }, []);
  useEffect(() => {
    if (!orgId) return undefined;
    setCapabilities({});
    setCapabilitiesContext(null);
    // A superseded orgId's settlement must never write: orgId changes twice per
    // load (auto primary org, then the selected org) and !297's bounded retry
    // stretches a failing fetch past the successor's success — without this
    // guard the stale chain's catch wipes good capabilities (T-MCRO-028
    // Rehearsal disabled, WebUI main 8036). Same pattern as the
    // trashRetentionDays effect below.
    let cancelled = false;
    retryTransientAuthorization('GET choreography capabilities', () => choreographyApi.getCapabilities(orgId)).then((response) => {
      if (cancelled) return undefined;
      const data = payload(response);
      // MCRO001-F0029 focus-on rate threshold: served at
      // capabilities.demonstrationPriority.focusOnRateThreshold (choreo backend
      // choreography.service.ts:371-373, MR !46 "feat(choreo): env-configurable
      // focus-on rate threshold [MCRO001-F0029]"). Also watch a couple of
      // plausible flatter names defensively in case that shape moves again;
      // fall back to the existing hardcoded 1.3 (demonstrationPriority.js)
      // when every candidate is absent or non-numeric — never block or invent
      // a name that doesn't exist.
      const watchedThreshold = [
        data.demonstrationPriority?.focusOnRateThreshold,
        data.focusOnRateThreshold,
        data.demonstrationFocusOnRateThreshold,
      ].map(Number).find((value) => Number.isFinite(value) && value > 0);
      setCapabilitiesContext(capabilitiesContextKey);
      setCapabilities({
        ...(data.permissions || {}),
        collaboration: data.collaboration,
        focusOnRateThreshold: watchedThreshold ?? 1.3,
        // M004-F0666 (b): the Video library reads its Trash window from here.
        trashRetentionDays: data.mediaRetention?.trashRetentionDays,
      });
      return undefined;
    }).catch((cause) => {
      if (cancelled) return undefined;
      setCapabilities({});
      setError(message(cause));
      return undefined;
    });
    return () => { cancelled = true; };
  }, [orgId, actorId, capabilitiesContextKey]);
  useEffect(() => { loadRoutines(); }, [loadRoutines]);
  // Unmount-only cleanup (empty deps: this effect's own cleanup never runs on
  // a mere loadRoutines identity change, only on teardown): invalidate the
  // in-flight generation so a load still resolving after the component is
  // gone finds `generation !== loadRoutinesGenerationRef.current` and never
  // calls setRoutines/setLoading/setError on an unmounted tree.
  useEffect(() => () => { loadRoutinesGenerationRef.current += 1; }, []);
  useEffect(() => { setReviewDisplay(readCommunityReviewDisplay(user?.id || user?.userId, orgId, routineId)); }, [orgId, routineId, user?.id, user?.userId]);
  // MCRO001-F0192: every routine read in this component is the explicit-current read
  // (`?revisions=current`). The scope token retires any in-flight read/refresh when the
  // actor, organisation, routine or read permission changes; there is no automatic retry
  // and no fallback to legacy full history. An explicit Retry bumps readNonce once.
  const readScopeRef = useRef(0);
  const readControllerRef = useRef(null);
  const [routineReadFailed, setRoutineReadFailed] = useState(false);
  const [readNonce, setReadNonce] = useState(0);
  const isCurrentReadScope = (scope) => scope === readScopeRef.current && readContextKey === readContextRef.current;
  const readCurrentRoutine = (scope) => {
    if (!isCurrentReadScope(scope)) return Promise.resolve(null);
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    readControllerRef.current = controller;
    return choreographyApi.getRoutineCurrent(orgId, routineId, controller ? { signal: controller.signal } : undefined)
      .then((response) => (isCurrentReadScope(scope) ? payload(response) : null))
      .catch((cause) => {
        if (!isCurrentReadScope(scope)) return null;
        setRoutineReadFailed(true);
        setRoutine(null); setSnapshot(emptySnapshot()); setBaselineSnapshot(emptySnapshot());
        setError(routineReadMessage(cause));
        throw cause;
      });
  };
  useEffect(() => {
    const scope = readScopeRef.current + 1;
    readScopeRef.current = scope;
    readControllerRef.current?.abort?.();
    setCollaborationStatus(null);
    setRoutineReadFailed(false);
    setSaving(false); setPublishing(false);
    setLoadedReadContext(null);
    // The previous scope's routine/snapshot must never stay visible or editable.
    setRoutine(null); setSnapshot(emptySnapshot()); setBaselineSnapshot(emptySnapshot());
    if (!orgId || !routineId || capabilitiesContext !== capabilitiesContextKey || capabilities.choreography_view !== true) return undefined;
    readCurrentRoutine(scope).then((next) => {
      if (!next) return;
      const nextSnapshot = currentRoutineSnapshot(next);
      setLoadedReadContext(readContextKey);
      setRoutine(next);
      setSnapshot(nextSnapshot);
      setBaselineSnapshot(JSON.parse(JSON.stringify(nextSnapshot)));
    }).catch(() => { /* the current-read boundary reports the scoped failure */ });
    return () => { readScopeRef.current += 1; readControllerRef.current?.abort?.(); };
  }, [orgId, routineId, actorId, readPermissionKey, readNonce, capabilitiesContext, capabilitiesContextKey]);
  const latestRevision = useMemo(() => currentRoutineRevision(routine) || undefined, [routine]);
  const canCreate = capabilities.choreography_create === true;
  const canEdit = capabilities.choreography_edit_all === true || (capabilities.choreography_edit_own === true && String(routine?.createdBy) === String(user?.id));
  // The original RoutineCanvasEditor is the ONLY Sheet editor and it is multi-user: tab === 'map'
  // always renders <CollaborativeCanvas/>, the thin engine mount that backs the restored canvas UI
  // with the collaborative line-source document (bootstrap + websocket queue). There is no feature
  // switch and no single-user fallback: reads come from the live projection of the durable document
  // and every canvas gesture is translated into MCRO-COLLAB/1 operations. Read-only for non-editors
  // is enforced by backend live-editing admission (the editor stays disabled while the engine phase
  // is not editable), not by a client switch. This derived flag only gates the Rehearsal tab /
  // draft-revision context for users who can edit (or have explicit collaboration admission); it
  // does not control which editor renders.
  const canAttemptCollaboration = canEdit || routine?.collaborationAdmission?.allowed === true;
  const draftDirty = useMemo(() => JSON.stringify(snapshot) !== JSON.stringify(baselineSnapshot), [snapshot, baselineSnapshot]);
  const canUpload = capabilities.choreography_video_manage_all === true || capabilities.choreography_video_upload_own === true;
  // MCRO001-F0053 / T-MCRO-053 Case K (MCRO001-D011 §5.1): folder create/
  // rename/move/archive/restore all require choreography_video_manage_all
  // specifically — narrower than canUpload above (manage_all OR upload_own),
  // an upload-own-only actor can edit/delete their own media but must never
  // see a folder-management control that would only 403.
  const canManageFolders = capabilities.choreography_video_manage_all === true;
  // demonstrationCandidateRevisionIds covers both revisions a demonstration
  // can be viewed against from this page: the Canvas always shows
  // latestRevision (the draft, or the sole published revision when there is
  // no draft) while Rehearsal can resolve to either the draft or the
  // published revision depending on source. readApprovedSavedDemonstrations
  // looks the requested context.revisionId up in this fetched set and falls
  // back to [] (fail closed) for any revision not in it.
  const demonstrationCandidateRevisionIds = [latestRevision?.revisionId, routine?.publishedRevisionId];
  const demonstrationAuthoredBars = rehearsalBarOptions(baselineSnapshot, latestRevision?.revisionId);
  const injectedDemonstrationBoundary = controlledMappedDemonstrationEvidenceBoundary();
  const { boundary: realDemonstrationBoundary, conflictNotice: demonstrationConflictNotice, clearConflictNotice: clearDemonstrationConflictNotice, recordsByRevision: demonstrationRecordsByRevision } = useMappedDemonstrationBoundary({
    orgId,
    routineId,
    revisionIds: demonstrationCandidateRevisionIds,
    sections: baselineSnapshot?.sections || [],
    authoredBars: demonstrationAuthoredBars,
    canEditTiming: canEdit,
    // Never issue live network calls when the E2E-injected boundary is the
    // one actually in effect (see the hook's own comment above) — the
    // Playwright specs' strict route allowlists throw on any unexpected
    // request, and this hook cannot be conditionally skipped as a hook.
    enabled: !injectedDemonstrationBoundary,
  });
  // The E2E-injected test double (window.__STUDIOAEYE_MAPPED_DEMONSTRATION_BOUNDARY__
  // under REACT_APP_CHOREO_MAPPED_DEMONSTRATION_E2E=1) takes priority when present so
  // the existing choreography-mapped-demonstration / demonstration-timing-rail /
  // demonstration-priority Playwright specs keep exercising their controlled fixture
  // unmodified; otherwise this is the real API-backed boundary.
  const demonstrationBoundary = injectedDemonstrationBoundary || realDemonstrationBoundary;
  // MediaLibrary's own "Timing editor" adapter, threaded down as props
  // rather than a second useMappedDemonstrationBoundary instance (see this
  // hook's own enabled-guard comment: a second live instance would risk
  // conflicting with the E2E-injected-boundary allowlist AND leave two
  // out-of-sync copies of demonstration list state). onSaveTimingMap is read
  // off demonstrationBoundary (the ALREADY injected-or-real merge just above,
  // the exact same object CollaborativeCanvas/Rehearsal receive wholesale as
  // demonstrationBoundary={demonstrationBoundary}), not realDemonstrationBoundary
  // directly — this is required so the Playwright E2E test double
  // (window.__STUDIOAEYE_MAPPED_DEMONSTRATION_BOUNDARY__) is honoured for a
  // save triggered from the Video library tab exactly like it already is for
  // the Canvas rail, never bypassed into a live network call. When the
  // injected double IS active, its own onSaveTimingMap still refetches
  // whatever state it models; when it is NOT active, this resolves to
  // realDemonstrationBoundary.onSaveTimingMap, which refetches the SAME
  // recordsByRevision state the Canvas rail reads, so switching tab back to
  // Canvas shows the new map without a manual reload. authoredBars is
  // computed against demonstrationRevisionContext (not latestRevision as the
  // rail above uses) because that is the exact revision MediaLibrary itself
  // resolves authoredBars against
  // (rehearsalBarOptions(snapshot, revisionContext?.revisionId) at its own
  // callsite) — using the rail's revision here would check eligibility
  // against the wrong revision whenever they diverge (e.g. a dirty draft).
  const mediaLibraryAuthoredBars = useMemo(
    () => rehearsalBarOptions(baselineSnapshot, demonstrationRevisionContext?.revisionId),
    [baselineSnapshot, demonstrationRevisionContext?.revisionId],
  );
  const { previewByMediaId: mediaLibraryPreviewByMediaId, requestPreview: requestMediaLibraryPreview } = useDemonstrationMediaPreviews(orgId);
  const getMediaLibraryDemonstrationEligibility = useMemo(() => buildDemonstrationEligibility({
    previewByMediaId: mediaLibraryPreviewByMediaId,
    requestPreview: requestMediaLibraryPreview,
    authoredBars: mediaLibraryAuthoredBars,
  }), [mediaLibraryPreviewByMediaId, requestMediaLibraryPreview, mediaLibraryAuthoredBars]);
  // MCRO001-F0052: { [mediaId]: colour } for every video that currently has a Canvas
  // demonstration timing rail on the Video-library tab's revision, read from the SAME
  // demonstrationBoundary the Canvas rail reads (so the two never diverge) and coloured
  // by the SAME shared source the rail colours its lines with (mediaTrackColour.js).
  // A video's Library tile then borders in its rail colour. Read on the revision
  // MediaLibrary itself resolves against (demonstrationRevisionContext), not the rail's
  // latestRevision, for the same divergence reason mediaLibraryAuthoredBars documents.
  const mediaLibraryTrackColours = useMemo(
    () => mediaTrackColours(readApprovedMappedDemonstrations(demonstrationBoundary, {
      organisationId: orgId,
      routineId,
      revisionId: demonstrationRevisionContext?.revisionId,
    })),
    [demonstrationBoundary, orgId, routineId, demonstrationRevisionContext?.revisionId],
  );
  // MCRO001-F0058: true only once the demonstrations list for MediaLibrary's
  // OWN revision (demonstrationRevisionContext, not the rail's latestRevision
  // — same divergence reason as above) has actually settled at least once —
  // see useMappedDemonstrationBoundary's recordsByRevision comment. Gates the
  // auto-create effect below MediaLibrary's own no-clobber check
  // (mediaLibraryTrackColours only has an entry once this is true), so a
  // slow/still-in-flight fetch can never race a false "no existing map".
  // Always false while the E2E-injected boundary is active (the real hook's
  // own fetch effect is disabled then, per its `enabled` guard), which is
  // safe: the auto-create queue simply never drains under the injected
  // double, matching every existing Playwright spec's unmodified behaviour.
  // The E2E-injected boundary (window.__STUDIOAEYE_MAPPED_DEMONSTRATION_BOUNDARY__)
  // supplies its own synchronous readApprovedSavedDemonstrations with no async
  // fetch/settle concept at all — it is ready the instant it exists, unlike the
  // real boundary's recordsByRevision, which only gains a key once its own
  // listDemonstrations fetch has actually resolved. Without this branch, the
  // auto-create/reconcile hooks below could never reach a save under the
  // injected double (recordsByRevision stays permanently empty while
  // useMappedDemonstrationBoundary's own `enabled: !injectedDemonstrationBoundary`
  // guard keeps it from fetching), which would make T-MCRO-058's rail-appears
  // case impossible to prove under the same controlled harness every other
  // demonstration Playwright spec already uses.
  const mediaLibraryDemonstrationRecordsReady = injectedDemonstrationBoundary
    ? true
    : demonstrationRecordsByRevision.has(String(demonstrationRevisionContext?.revisionId || ''));
  // MCRO001-F0058: the no-clobber check reads THIS, never trackColours —
  // trackColours (mediaTrackColours()/approvedCandidate()) additionally
  // requires rendition.availability === 'available', which only becomes true
  // once a SECOND, later async stage (useMappedDemonstrationBoundary's own
  // per-mediaId getMediaContent fetch) has also settled. Gating "does a saved
  // map already exist" on that unrelated media-playability signal reopened
  // the exact race mediaLibraryDemonstrationRecordsReady was added to close:
  // recordsReady can be true (the list fetch settled) while trackColours is
  // still empty (the content fetch has not), so a genuine existing map was
  // invisible to the no-clobber check for one extra render. This reads the
  // records list directly instead, which is exactly what recordsReady itself
  // is keyed on.
  const mediaLibraryExistingDemonstrationMediaIds = useMemo(() => {
    // Under the injected double, read the SAME merged demonstrationBoundary
    // every other consumer (CollaborativeCanvas, Rehearsal, MediaLibrary's own
    // onSaveTimingMap) already reads, so this hook's no-clobber check honours
    // the test double exactly like the save call it gates does.
    if (injectedDemonstrationBoundary) {
      return new Set(
        demonstrationBoundary
          .readApprovedSavedDemonstrations({ organisationId: orgId, routineId, revisionId: demonstrationRevisionContext?.revisionId })
          .map((candidate) => String(candidate.mediaId)),
      );
    }
    return new Set(
      (demonstrationRecordsByRevision.get(String(demonstrationRevisionContext?.revisionId || '')) || [])
        .map((record) => String(record.mediaId)),
    );
  }, [injectedDemonstrationBoundary, demonstrationBoundary, orgId, routineId, demonstrationRecordsByRevision, demonstrationRevisionContext?.revisionId]);
  // MCRO001-F0058 extension (owner/coordinator follow-up, 2026-08-25): the
  // attach-time hook inside MediaLibrary above only helps a video attached
  // AFTER this feature shipped. The owner's actual complaint was a routine
  // with FOUR videos already attached and no rails at all — those need the
  // same default map reconciled on load, not just on the next attach. This
  // fetch is deliberately separate from MediaLibrary's own `canvasItems`:
  // that state can hold unconfirmed `__optimistic` rows mid-attach, and
  // reconciling against an unconfirmed attach could create a demonstration
  // for media that never actually landed on the canvas. It is also
  // deliberately NOT gated behind any tab — MediaLibrary only mounts on the
  // Video-library tab, and the owner's four already-attached videos need
  // their rails on the CANVAS tab, which he may open without ever visiting
  // Video library.
  const [reconcileCanvasMediaIds, setReconcileCanvasMediaIds] = useState([]);
  const reconcileFetchedForRef = useRef(null);
  useEffect(() => {
    let active = true;
    if (!orgId || !routineId) { setReconcileCanvasMediaIds([]); return undefined; }
    // Owner/coordinator hardening (2026-08-25, pipeline 7008 job 146183
    // investigation): while the Video-library tab is open, MediaLibrary
    // already fetches this exact routine's canvas-media list for its own
    // "On this canvas" panel (loadCanvasMedia/canvasItems) — issuing a
    // SECOND, independent GET to the identical live endpoint at the same
    // moment is pure redundant load against a real backend, never needed for
    // correctness (this effect's own dependency array still re-fires the
    // instant the tab changes, so switching back to Canvas re-fetches
    // fresh). Skipping it here cannot change reconciliation's own outcome —
    // it only removes a duplicate network round-trip while MediaLibrary's
    // own attach-time hook already covers every video attached from that
    // tab — and reduces concurrent request pressure on a shared TestDeploy
    // host at exactly the moment a real user interaction (e.g. a drag-drop)
    // may be in flight. This is a plain product/perf decision, not a
    // test-only branch: identical in production and under test.
    if (tab === 'media') return undefined;
    // M004-F0666 (a) (WebUI #53): read once per (org, routine) per page mount.
    // A Canvas/Rehearsal/Access round-trip is not new information — the
    // Video-library attach hook keeps reconciliation current — so a tab switch
    // no longer re-issues this GET.
    const reconcileKey = `${orgId}:${routineId}`;
    if (reconcileFetchedForRef.current === reconcileKey) return undefined;
    reconcileFetchedForRef.current = reconcileKey;
    let settled = false;
    Promise.resolve()
      .then(() => choreographyApi.getCanvasMedia(orgId, routineId))
      .then((response) => {
        if (!active) return;
        setReconcileCanvasMediaIds(
          listFrom(payload(response), ['media', 'items']).map((item) => String(item.mediaId)).filter(Boolean),
        );
      })
      .catch(() => { if (!active) return; reconcileFetchedForRef.current = null; setReconcileCanvasMediaIds([]); })
      .finally(() => { settled = true; });
    // A run torn down before its answer arrives (tab switch, StrictMode
    // double effect) un-marks the key so the next run reads again.
    return () => { active = false; if (!settled && reconcileFetchedForRef.current === reconcileKey) reconcileFetchedForRef.current = null; };
  }, [orgId, routineId, tab]);
  // MCRO001-F0058 hardening (owner/coordinator, pipeline 7008/7013/146349
  // investigation): reconciliation is background work and must behave like
  // background work — it never competes with a user's own initial actions
  // for a shared backend's rate budget. Job 146349's captured evidence
  // showed this Feature's own extra per-load requests consuming enough rate
  // budget that a user-initiated PATCH burst (T-MCRO-055's folder move,
  // performed within moments of page load) tipped over the live limiter.
  // Deferring reconcile's first save attempt until a few seconds after the
  // routine loads gives any real user-initiated burst — which starts the
  // instant tiles are interactive, well before this delay elapses — first
  // claim on the rate budget, while reconcile (whose whole point is "the
  // owner never has to do anything," not "instantly") simply starts a few
  // seconds later. retryOnRateLimit above remains the correctness backstop
  // for whatever contention this politeness delay does not fully avoid.
  const RECONCILE_POLITE_DELAY_MS = 2000;
  const [reconcilePolite, setReconcilePolite] = useState(false);
  useEffect(() => {
    setReconcilePolite(false);
    if (!orgId || !routineId || capabilitiesContext !== capabilitiesContextKey || capabilities.choreography_view !== true) return undefined;
    const timer = setTimeout(() => setReconcilePolite(true), RECONCILE_POLITE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [orgId, routineId]);
  // reconciledMediaIds is the failure-damping ceiling: a mediaId is added to
  // it on EVERY terminal outcome for this session (saved, ineligible, or a
  // failed save attempt) and is never re-attempted afterwards, so a broken
  // video (e.g. one with no thumbnail/undecodable content) is tried at most
  // once per session rather than on every render. Only a mediaId still
  // "resolving" (eligibility's own pending-preview reason) is left OUT of
  // this set, so it is retried automatically once its preview resolves —
  // still bounded, since the preview cache itself always settles to either a
  // real duration or 'failed'.
  //
  // Serialisation: this effect looks up at most ONE candidate per run
  // (`.find`, not `.forEach`) and only enqueues its async save after marking
  // it in-flight; finishing that one candidate (success or failure) adds it
  // to reconciledMediaIds, which changes this effect's own dependency and
  // re-runs it for the NEXT candidate. There is never more than one
  // reconcile save in flight at a time, and no burst of concurrent writes.
  const [reconciledMediaIds, setReconciledMediaIds] = useState(() => new Set());
  const reconcileInFlightRef = useRef(new Set());
  useEffect(() => {
    if (!canEdit) return; // read-only viewers never write.
    if (!reconcilePolite) return; // background work waits its turn — see the delay's own comment above.
    // Never on the initial empty state: mediaLibraryDemonstrationRecordsReady
    // is true only once this revision's demonstrations list has actually
    // settled (see its own definition above) — proceeding earlier would treat
    // "not fetched yet" as "confirmed no map" and could double-create.
    if (!mediaLibraryDemonstrationRecordsReady) return;
    const revisionId = demonstrationRevisionContext?.revisionId;
    if (!revisionId) return;
    const candidateId = reconcileCanvasMediaIds.find((id) => (
      !mediaLibraryExistingDemonstrationMediaIds.has(id)
      && !reconciledMediaIds.has(id)
      && !reconcileInFlightRef.current.has(id)
    ));
    if (!candidateId) return;
    const eligibility = getMediaLibraryDemonstrationEligibility({ mediaId: candidateId, revisionId });
    if (eligibility?.eligible !== true) {
      if (eligibility?.reason === 'Resolving this video for timing setup — try again in a moment.') return;
      setReconciledMediaIds((prev) => new Set(prev).add(candidateId));
      return;
    }
    const fullBars = completeDemonstrationBars(mediaLibraryAuthoredBars, revisionId);
    if (fullBars.length === 0) { setReconciledMediaIds((prev) => new Set(prev).add(candidateId)); return; }
    reconcileInFlightRef.current.add(candidateId);
    (async () => {
      try {
        const timingMap = createProportionalTimingMap({
          revisionId,
          renditionId: eligibility.renditionId,
          durationMs: eligibility.durationMs,
          mediaStartMs: 0,
          mediaEndMs: eligibility.durationMs,
          startCount: fullBars[0].startCount,
          endCount: fullBars[fullBars.length - 1].endCount,
          sections: baselineSnapshot?.sections || [],
          authoredBars: fullBars,
        }, revisionId);
        await demonstrationBoundary.onSaveTimingMap({
          mediaId: candidateId,
          revision: { revisionId, snapshotDigest: demonstrationRevisionContext.snapshotDigest },
          relation: { relationId: eligibility.relationId, expectedVersion: eligibility.expectedRelationVersion },
          rendition: { renditionId: eligibility.renditionId, expectedVersion: eligibility.expectedRenditionVersion },
          timingMap,
        });
      } catch {
        // Damped — see reconciledMediaIds comment above. The canvas/rail
        // render is entirely unaffected either way.
      } finally {
        reconcileInFlightRef.current.delete(candidateId);
        setReconciledMediaIds((prev) => new Set(prev).add(candidateId));
      }
    })();
  }, [canEdit, reconcilePolite, mediaLibraryDemonstrationRecordsReady, demonstrationRevisionContext, reconcileCanvasMediaIds,
    mediaLibraryExistingDemonstrationMediaIds, reconciledMediaIds, getMediaLibraryDemonstrationEligibility,
    mediaLibraryAuthoredBars, baselineSnapshot, demonstrationBoundary]);
  useEffect(() => {
    setDemonstrationPlaybackSession(emptyDemonstrationPlaybackSession());
  }, [routineId]);
  // MCRO001-F0026 (T-MCRO-026 Case B/C): readiness here means "this exact
  // revision's authored structure is STABLE, so a timing map may be bound to
  // it" — nothing more.
  //
  // It previously also required !canAttemptCollaboration, which made the whole
  // Video-library Timing editor unreachable: canAttemptCollaboration is
  // `canEdit || routine.collaborationAdmission.allowed`, and MediaLibrary's
  // canEditTiming is `canEdit`, so `ready` and `canEditTiming` were mutually
  // exclusive by construction. DemonstrationWorkspace needs BOTH (revision.valid
  // AND capabilities.canEditTiming) before any card is launchable, so every
  // actor with authority to save saw "Demonstration setup unavailable" and
  // "Timing editor unavailable.", and nothing could ever create a timing map —
  // which is why production held zero
  // choreography_demonstration_timing_maps rows. The rail (F0028) can only
  // RETIME an already-saved map, so this was the sole create path.
  //
  // The real hazard the old condition was reaching for is an authored structure
  // being changed underneath the map, which is exactly UNCOMMITTED work:
  // `draftDirty` (local unsaved edits) or collaborative operations not yet
  // durably accepted. Those are checked instead; merely BEING ABLE to
  // collaborate is not a reason to refuse.
  //
  // Deliberately NOT keyed on collaborationStatus.phase: the Canvas tab unmounts
  // when the author switches to Video library, so the last status seen would
  // freeze at whatever phase it held and could strand the editor permanently.
  // A disconnected canvas does not alter the stored revision snapshot that
  // baselineSnapshot/the digest are computed from.
  const demonstrationStructurePending = Boolean(draftDirty)
    || Number(collaborationStatus?.pendingCount) > 0;
  useEffect(() => {
    let active = true;
    const revisionId = String(routine?.draftRevisionId || routine?.publishedRevisionId || '');
    const ready = Boolean(revisionId && !demonstrationStructurePending);
    setDemonstrationRevisionContext({ ready: false, revisionId, snapshotDigest: '' });
    if (ready) {
      demonstrationSnapshotDigest(baselineSnapshot).then((snapshotDigest) => {
        if (active) setDemonstrationRevisionContext({ ready: true, revisionId, snapshotDigest });
      }).catch(() => {
        if (active) setDemonstrationRevisionContext({ ready: false, revisionId, snapshotDigest: '' });
      });
    }
    return () => { active = false; };
  }, [baselineSnapshot, demonstrationStructurePending, routine?.draftRevisionId, routine?.publishedRevisionId]);
  const selectRoutine = (nextRoutineId) => {
    const nextId = String(nextRoutineId || '');
    if (nextId !== String(routineId)) {
      setRoutine(null);
      setSnapshot(emptySnapshot());
      setBaselineSnapshot(emptySnapshot());
      setReanchorPreview(null);
      setCollaborationStatus(null);
      setViewingAs('');
    }
    setRoutineId(nextId);
    setTab('map');
  };
  const canManageAccess = canManageChoreographyAccess(user, org);
  const create = async (form) => { setSaving(true); try { const track = payload(await choreographyApi.createTrack(orgId, { title: form.trackTitle, artist: form.artist || undefined, durationMs: form.durationMs, bpm: form.bpm, tapTimesMs: form.tapTimesMs.length > 1 ? form.tapTimesMs : undefined, firstCountOffsetMs: form.firstCountOffsetMs, audioSource: 'none' })); if (form.audioFile) { const body = new FormData(); body.append('file', form.audioFile); body.append('rights_confirmed', 'true'); await choreographyApi.uploadAudio(orgId, track.trackId, body); } const created = payload(await choreographyApi.createRoutine(orgId, { trackId: track.trackId, title: form.title, shareScope: form.shareScope, snapshot: form.snapshot || emptySnapshot() })); setCreating(false); await loadRoutines(); setRoutineId(created.routineId); setTab('map'); setNotice('Choreo Canvas created. Keep writing directly into the music structure.'); return true; } catch (cause) { setError(message(cause)); return false; } finally { setSaving(false); } };
  const revisionReadScope = readScopeRef.current;
  const refreshSavedRevision = async () => {
    // This callback is captured before the editor awaits its checkpoint.
    const scope = revisionReadScope;
    const next = await readCurrentRoutine(scope);
    if (!next) return;
    const saved = currentRoutineSnapshot(next);
    setRoutine(next); setSnapshot(saved); setBaselineSnapshot(JSON.parse(JSON.stringify(saved)));
  };
  const persistSnapshot = async (candidate) => {
    const scope = readScopeRef.current;
    setSaving(true); setError('');
    try {
      const revision = payload(await choreographyApi.saveMap(orgId, routineId, { baseRevisionId: routine?.draftRevisionId, snapshot: candidate, changeNote: 'Updated from Choreo Canvas' }));
      if (!isCurrentReadScope(scope)) return;
      const next = await readCurrentRoutine(scope);
      if (!next) return;
      const saved = currentRoutineSnapshot(next);
      setRoutine(next); setSnapshot(saved); setBaselineSnapshot(JSON.parse(JSON.stringify(saved)));
      setReanchorPreview(null); setNotice(`Draft revision ${revision.revisionNumber} saved.`);
    } catch (cause) { if (isCurrentReadScope(scope)) setError(routineReadMessage(cause)); }
    finally { if (isCurrentReadScope(scope)) setSaving(false); }
  };
  const save = async () => { const boundary = findBoundaryReanchor(baselineSnapshot, snapshot); if (!boundary) { await persistSnapshot(snapshot); return; } try { const preview = payload(await choreographyApi.previewReanchor(orgId, routineId, { revisionId: routine?.draftRevisionId || latestRevision?.revisionId, fromCount: boundary.fromCount, deltaCounts: boundary.deltaCounts })); setReanchorPreview(preview); } catch (cause) { setError(message(cause)); } };
  // M001-D023 §7.4.1 / M001-F0297 (2026-08-22): the standalone "Publish
  // version…" control (and this function's previous window.prompt-for-reason
  // / setTab('rehearsal') side effects) is retired along with the
  // mc-sheet-publish section — publishing is reachable only through Quick
  // setup's Publish button now, one click, no second screen. The `reason`
  // field is still real and required server-side (min 3 chars,
  // choreographyCollaboration.service.ts `publish()`); per this panel's own
  // standing rule against justification prompts (ResourceSharePanel.js:
  // "audit-reason machinery either derived automatically or removed from the
  // user's path"), it is auto-derived here rather than asked via
  // window.prompt, which would turn "one click" into a blocking dialog.
  // Throws on failure/guard so `ResourceAccessManager`'s compound-Publish
  // orchestration (§7.4.1 point 4) can distinguish "publish failed" from
  // "the follow-on share failed" — this function must not itself touch
  // sharing/access state, only the routine's own draft/publish lifecycle.
  const publishDraftForQuickSetup = async () => {
    const scope = readScopeRef.current;
    if (collaborationStatus?.draftId && (collaborationStatus.pendingCount > 0 || collaborationStatus.phase !== 'live')) {
      throw new Error('Wait for live authority and every collaborative change to show Saved before publishing.');
    }
    if (!collaborationStatus?.draftId && (draftDirty || !routine?.draftRevisionId)) {
      throw new Error('Save the current draft before publishing a version for viewers.');
    }
    const reason = 'Published from quick setup.';
    await choreographyApi.publish(orgId, routineId, collaborationStatus?.draftId
      ? { draftId: collaborationStatus.draftId, expectedServerSequence: collaborationStatus.serverSequence, pendingOperationCount: 0, reason }
      : { revisionId: routine.draftRevisionId, reason });
    if (!isCurrentReadScope(scope)) return;
    await loadRoutines();
    if (!isCurrentReadScope(scope)) return;
    const next = await readCurrentRoutine(scope);
    if (next) {
      const saved = currentRoutineSnapshot(next);
      setRoutine(next); setSnapshot(saved); setBaselineSnapshot(JSON.parse(JSON.stringify(saved)));
    }
  };
  // MCRO001-F0151 (D004 §12.4, choreo issue #41): the Canvas Publish control
  // reuses the exact guarded quick-setup path above — one click, no prompt,
  // auto-derived reason, same preconditions. The only difference is where the
  // outcome is reported: the Canvas toast/error surfaces.
  const publishFromCanvas = async () => {
    if (publishing) return;
    const scope = readScopeRef.current;
    setPublishing(true);
    try { await publishDraftForQuickSetup(); if (isCurrentReadScope(scope)) setNotice('Routine published. Viewers now see this version.'); }
    catch (cause) { if (isCurrentReadScope(scope)) setError(routineReadMessage(cause)); }
    finally { if (isCurrentReadScope(scope)) setPublishing(false); }
  };
  const changeReviewDisplay = (value) => { const next = normalizeCommunityReviewDisplay(value); setReviewDisplay(next); writeCommunityReviewDisplay(user?.id || user?.userId, orgId, routineId, next); };
  // MCRO001-F0164: the PATCH /routines/:id/sharing response is authoritative
  // ({ routineId, shareScope }); merge it instead of refetching.
  const handleCanvasScopeChanged = (nextScope) => setRoutine((current) => (current ? { ...current, shareScope: nextScope } : current));
  return <Layout><div className="mc-page"><header className="mc-toolbar"><div className="mc-toolbar-title"><span className="mc-kicker">Music choreography</span><h1>Choreography</h1></div><div className="mc-toolbar-actions"><label><span>Studio</span><select aria-label="Studio" value={orgId} onChange={(event) => { setOrgId(event.target.value); setRoutineId(''); }}>{orgs.map((item) => <option key={item.id || item.organisation_id} value={item.id || item.organisation_id}>{item.name || item.organisation_name}</option>)}</select></label>{canManageAccess && <button type="button" className="mc-toolbar-access" onClick={() => setTab('access')}>Manage access</button>}<button className="mc-primary" onClick={() => setCreating(true)} disabled={!orgId || !canCreate}>+ Create routine</button></div></header>
    {notice && <div className="mc-notice" role="status">{notice}</div>}{error && <div className="mc-alert" role="alert">{error}{routineReadFailed && <button type="button" onClick={() => { setError(''); setReadNonce((n) => n + 1); }}>Retry</button>}<button onClick={() => setError('')}>Dismiss</button></div>}{demonstrationConflictNotice && <div className="mc-alert" role="alert">{demonstrationConflictNotice}<button onClick={clearDemonstrationConflictNotice}>Dismiss</button></div>}
<div className={`mc-workspace ${routinesCollapsed ? 'mc-workspace-collapsed' : ''}`}>{routinesCollapsed && <button type="button" className="mc-routines-edge-expand" aria-expanded={false} aria-controls="mc-routines-rail" aria-label="Expand routines list" onClick={() => setRoutinesCollapsed(false)}>»</button>}<aside id="mc-routines-rail" className={`mc-routines ${routinesCollapsed ? 'mc-routines-collapsed' : ''}`}><div className="mc-routines-head"><div><span className="mc-kicker">{org?.name || 'Your studio'}</span><h2>Routines</h2></div><button type="button" className="mc-routines-toggle" aria-expanded={!routinesCollapsed} aria-controls="mc-routines-rail" aria-label="Collapse routines list" onClick={() => setRoutinesCollapsed(true)}>«</button></div><button type="button" className="mc-routines-new" onClick={() => setCreating(true)} disabled={!orgId || !canCreate}>+ New routine</button>{capabilities.choreography_view !== true ? <div className="mc-empty"><strong>Choreography is unavailable here.</strong><span>Your organisation permission does not allow published routine access.</span></div> : loading ? <p>Loading routines…</p> : routines.length === 0 ? <div className="mc-empty"><strong>Your first canvas starts here.</strong><span>No audio or formation required.</span></div> : routines.map((item) => <button key={item.routineId} data-routine-id={item.routineId} className={String(item.routineId) === String(routineId) ? 'active' : ''} onClick={() => selectRoutine(item.routineId)}><span className={`mc-status ${item.status}`}>{item.status}</span><strong>{item.title}</strong><small>{item.trackTitle}{item.artist ? ` · ${item.artist}` : ''}</small></button>)}</aside>
      <div className="mc-main">{tab === 'access' && canManageAccess ? <ChoreographyAccessPanel orgId={orgId} /> : creating ? <CreateRoutineDialog onClose={() => setCreating(false)} onCreate={create} saving={saving} /> :!routineId ? <section className="mc-welcome"><div className="mc-orbit">1—8</div><span className="mc-kicker">Canvas first</span><h2>Select a routine or start writing.</h2><div className="mc-welcome-promise"><h3>Hear the structure.<br />Write the movement.</h3><p>Your text becomes the musical structure without a separate timing editor.</p></div><p>Sections, eight-count rows and movement stay together on one calm writing surface. Rehearsal, video and music remain optional.</p><button className="mc-primary" onClick={() => setCreating(true)} disabled={!orgId || !canCreate}>Create the first canvas</button></section> : <><header className="mc-routine-head mc-routine-head-compact"><h2 className="mc-routine-title">{routine?.title || 'Routine'}</h2><div className="mc-routine-actions"></div></header><nav className="mc-tabs" aria-label="Choreography workspace"><button className={tab === 'map' ? 'active' : ''} onClick={() => setTab('map')}>Canvas</button><button className={tab === 'rehearsal' ? 'active' : ''} onPointerDown={() => canvasWorkspaceCaptureRef.current?.()} onKeyDown={() => canvasWorkspaceCaptureRef.current?.()} onClick={() => setTab('rehearsal')} disabled={!canOpenRehearsal(routine, canEdit || canAttemptCollaboration)}>Rehearsal</button><button className={tab === 'media' ? 'active' : ''} onClick={() => setTab('media')}>Video library</button><button className={tab === 'sheet_access' ? 'active' : ''} onClick={() => setTab('sheet_access')}>Access</button></nav>{tab === 'map' && routine && !routineReadFailed && <CollaborativeCanvas key={readContextKey} organisationId={orgId} routineId={routineId} userId={user?.id} onStatus={handleCollaborationStatus} snapshot={snapshot} canEdit={canEdit} capabilities={capabilities} reviewDisplay={reviewDisplay} onChangeReviewDisplay={changeReviewDisplay} routineTitle={routine?.title} publishedRevisionId={routine?.publishedRevisionId} displayedRevisionId={latestRevision?.revisionId} registerWorkspaceCapture={registerCanvasWorkspaceCapture} demonstrationBoundary={demonstrationBoundary} demonstrationPlaybackContext={demonstrationPlaybackSession} viewingAs={viewingAs} onChangeViewingAs={setViewingAs} onRevisionSaved={refreshSavedRevision} onPublish={capabilities.choreography_publish === true ? publishFromCanvas : undefined} publishBusy={publishing || Boolean(collaborationStatus?.draftId && (collaborationStatus.pendingCount > 0 || collaborationStatus.phase !== 'live'))} />}{tab === 'rehearsal' && <Rehearsal orgId={orgId} routineId={routineId} routine={routine} canEdit={canEdit || canAttemptCollaboration} draftSnapshot={shouldUseDraftPreview(routine, canEdit || canAttemptCollaboration, collaborationStatus?.pendingCount > 0 || draftDirty) ? (collaborationStatus?.snapshot || snapshot) : null} dirty={collaborationStatus?.pendingCount > 0 || draftDirty} onReturnCanvas={() => setTab('map')} demonstrationBoundary={demonstrationBoundary} playbackSession={demonstrationPlaybackSession} setPlaybackSession={setDemonstrationPlaybackSession} focusOnRateThreshold={capabilities.focusOnRateThreshold} viewingAs={viewingAs} />}{tab === 'media' && <MediaLibrary orgId={orgId} routineId={routineId} revisionContext={demonstrationRevisionContext} snapshot={baselineSnapshot} canUpload={canUpload} canEditTiming={canEdit} getDemonstrationEligibility={getMediaLibraryDemonstrationEligibility} onSaveTimingMap={demonstrationBoundary.onSaveTimingMap} trackColours={mediaLibraryTrackColours} demonstrationRecordsReady={mediaLibraryDemonstrationRecordsReady} existingDemonstrationMediaIds={mediaLibraryExistingDemonstrationMediaIds} canManageFolders={canManageFolders} pageCapabilities={capabilities} />}{tab === 'sheet_access' && <><CanvasAccessPanel routine={routine} orgId={orgId} orgName={org?.name} canEdit={canEdit} currentUser={user} onScopeChanged={handleCanvasScopeChanged} /><SheetPermissionsPanel routine={routine} orgId={orgId} routineId={routineId} publishControl={{ canPublish: capabilities.choreography_publish === true, hasDraft: Boolean(collaborationStatus?.draftId || routine?.draftRevisionId || draftDirty), busy: Boolean(collaborationStatus?.draftId && (collaborationStatus.pendingCount > 0 || collaborationStatus.phase !== 'live')), draftRevisionNumber: latestRevision?.revisionNumber || null, onPublishDraft: publishDraftForQuickSetup }} /></>}</>}</div>
    </div>{reanchorPreview && <div className="mc-overlay"><section className="mc-dialog" role="dialog" aria-modal="true" aria-labelledby="mc-reanchor-title"><span className="mc-kicker">Boundary changed</span><h2 id="mc-reanchor-title">Choose how dependent cues move</h2><p>Nothing moves silently. Review every affected item, then keep absolute counts or shift the phrase and its choreography together.</p><div className="mc-reanchor-list">{reanchorPreview.affected.map((item) => <div key={`${item.type}-${item.id}`}><strong>{item.type} · {item.id}</strong><span>{item.fromCount} → {item.toCount}</span></div>)}</div><div className="mc-dialog-actions"><button className="mc-quiet" onClick={() => setReanchorPreview(null)}>Cancel</button><button className="mc-quiet" onClick={() => persistSnapshot(snapshot)}>Keep cue counts</button><button className="mc-primary" onClick={() => persistSnapshot(reanchorPreview.proposedSnapshot)}>Shift map and cues</button></div></section></div>}</div></Layout>;
}
